<template>
  <div class="patrimonial-form-container form-apat-shell" :class="{ 'form-apat--readonly': isReportGenerated }">
    <header class="form-apat-hero">
      <h1 class="form-apat-hero__title">Votre diagnostic patrimonial</h1>
    </header>

    <!-- Barre de progression -->
    <div class="form-progress" role="progressbar" :aria-valuenow="formProgressPercent" aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="`Progression du diagnostic : ${formProgressPercent} pour cent, ${formProgressPhaseLabel}`">
      <div class="form-progress__header">
        <span class="form-progress__phase">Page {{ wizardStepNumber }}/{{ wizardStepTotal }} — {{ formProgressPhaseLabel
        }}</span>
        <span class="form-progress__pct">{{ formProgressPercent }}&nbsp;%</span>
      </div>
      <div class="form-progress__track">
        <div class="form-progress__bar" :style="{ width: formProgressPercent + '%' }"></div>
      </div>
    </div>

    <!-- Message si infos personnelles non validées -->
    <div v-if="!isPersonalInfoLocked && isWizardStep('page1_profile')" class="form-header form-header--hint">
      <span>Renseignez vos informations d'identité, puis cliquez sur «&nbsp;Suivant&nbsp;» pour continuer.</span>
    </div>

    <!-- Notifications (annoncées aux lecteurs d'écran) -->
    <div v-if="successMessage" class="notification success" role="status" aria-live="polite" aria-atomic="true">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
      </svg>
      {{ successMessage }}
    </div>

    <div v-if="errorMessage" class="notification error" role="alert" aria-live="assertive" aria-atomic="true">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
      </svg>
      {{ errorMessage }}
    </div>

    <!-- Raccourci une fois le champ manquant corrigé : évite de refaire le parcours
         page par page pour retourner à l'étape d'envoi déjà atteinte une fois. -->
    <div v-if="canJumpToSubmit" class="notification info" role="status" aria-live="polite">
      <span>Vos informations sont à jour, vous pouvez reprendre l'envoi.</span>
      <button type="button" class="notification__action" @click="goToWizardStep('page6_submit')">
        Retourner à l'envoi →
      </button>
    </div>

    <!-- Rappel de l'offre : le choix se confirme au paiement, il se fige une fois réglé -->
    <div v-if="isPersonalInfoLocked && isWizardStep('page1_profile')" class="form-section offre-locked">
      <h3 class="section-title">Votre offre</h3>
      <p class="offre-locked__value">
        {{ selectedOfferTitle }}
        <span v-if="clientApte" class="lock-badge lock-badge--inline">Réglée</span>
      </p>
      <p class="offre-locked__hint">
        <template v-if="clientApte">
          Pour changer d’offre, contactez votre conseiller depuis « Contacter mon conseiller ».
        </template>
        <template v-else>
          Offre retenue pour votre accompagnement.
        </template>
      </p>
    </div>

    <!-- Duo : onglets conjoint visibles sur tout le wizard (après soumission C1, bascule vers C2). -->
    <div v-if="isDuoOffer" class="duo-selector duo-selector--personal">
      <div class="duo-tabs">
        <button type="button" :class="['duo-tab', { active: ordreDuo === 1 }]" @click="switchOrdreDuo(1)">
          Conjoint 1
          <span v-if="duoWorkflowByOrdre[1]?.isReportGenerated" class="duo-tab-badge" title="Analyse envoyée">✓</span>
        </button>
        <button type="button" :class="['duo-tab', { active: ordreDuo === 2 }]" @click="switchOrdreDuo(2)">
          Conjoint 2
          <span v-if="duoWorkflowByOrdre[2]?.isReportGenerated" class="duo-tab-badge" title="Analyse envoyée">✓</span>
        </button>
      </div>
      <p v-if="isWizardStep('page1_profile')" class="duo-hint text-muted">Renseignez les informations personnelles pour
        chaque conjoint ; les données sont enregistrées séparément. <br>
        <i class="fas fa-asterisk"></i> Les biens en commun sont renseignées par un seul conjoint.
      </p>
      <p v-else-if="isReportGenerated" class="duo-hint text-muted">
        Analyse du conjoint {{ ordreDuo }} envoyée. Basculez vers l'autre conjoint pour compléter le second dossier.
      </p>
    </div>
    <!-- novalidate : la validation HTML5 bloque l'événement submit sans feedback clair (champs requis vides, etc.) ; la logique métier est dans handleSubmit + validationErrorItems. -->
    <form ref="patrimonialForm" novalidate @submit.prevent="handleSubmit" class="patrimonial-form">
      <!-- Reste visible une fois verrouillée : sinon l'étape 1 n'affiche plus rien
           du tout au client dont l'identité est déjà validée. -->
      <div v-show="isWizardStep('page1_profile')" id="section-personal" class="form-section"
        :class="{ 'section-locked': isPersonalInfoLocked }">
        <h3 class="section-title">
          <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
          Informations Personnelles
          <span v-if="isPersonalInfoLocked" class="lock-badge">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor"
                d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
            </svg>
            Validé
          </span>
        </h3>

        <div class="form-grid form-section-personal">
          <div class="form-group">
            <label for="last_name">Nom <i class="fas fa-asterisk"></i></label>
            <input type="text" id="last_name" :value="displayLastName" @input="last_name = $event.target.value"
              :readonly="isPersonalInfoLocked" required />
          </div>
          <div class="form-group">
            <label for="first_name">Prénom(s)<i class="fas fa-asterisk"></i></label>
            <input type="text" id="first_name" :value="displayFirstName" @input="first_name = $event.target.value"
              :readonly="isPersonalInfoLocked" required />
          </div>
          <!-- Champ Login généré -->
          <div class="form-group">
            <label for="login">Nom d'utilisateur <i class="fas fa-asterisk"></i></label>
            <input type="text" id="login" :value="displayLogin" readonly class="login-field" />
            <small class="login-hint">Ceci est désormais votre nom d'utilisateur pour vous connecter</small>
          </div>
          <!-- Champs modifiables (Client) -->
          <div class="form-group">
            <label for="born">Date de naissance <i class="fas fa-asterisk"></i></label>
            <input v-if="isPersonalInfoLocked" type="text" :value="'***'" readonly />
            <input v-else type="date" id="born" :value="displayBorn" @input="born = $event.target.value" required
              aria-required="true" />
          </div>
          <div class="form-group">
            <label for="comborn">Commune de naissance <i class="fas fa-asterisk"></i></label>
            <input type="text" id="comborn" :value="displayComborn" @input="comborn = $event.target.value"
              :readonly="isPersonalInfoLocked" />
          </div>
          <div class="form-group">
            <label for="depborn">Département de naissance <i class="fas fa-asterisk"></i></label>
            <input type="text" id="depborn" :value="displayDepborn" @input="depborn = $event.target.value"
              :readonly="isPersonalInfoLocked" required aria-required="true" />
          </div>
          <!-- fin Champs modifiables (Client) -->
          <div class="form-group">
            <label for="fonction">Profession <i class="fas fa-asterisk"></i></label>
            <input type="text" id="fonction" :value="displayFonction" @input="fonction = $event.target.value"
              :readonly="isPersonalInfoLocked" required />
          </div>
          <div class="form-group">
            <label for="salaire">Salaire / Revenu Mensuel <i class="fas fa-asterisk"></i></label>
            <input v-if="isPersonalInfoLocked" type="text" :value="'***'" readonly />
            <input v-else type="number" id="salaire" :value="displaySalaire" @input="salaire = $event.target.value"
              :min="minRevenuMensuelNet" required :aria-invalid="salaireBelowEligibility ? 'true' : 'false'"
              :class="{ 'input-eligibility-error': salaireBelowEligibility }" />
            <p v-if="salaireBelowEligibility"
              class="photo-selfie-notice photo-selfie-notice--error salaire-eligibility-notice" role="alert">
              Vous ne remplissez pas les conditions. Le revenu mensuel net minimum requis est de
              {{ minRevenuMensuelLabel }}.
            </p>
          </div>
          <div class="form-group">
            <label for="nb_annees_restantes">Nombre d'années restantes avant la retraite</label>
            <input v-if="isPersonalInfoLocked" type="text" :value="'***'" readonly />
            <input v-else type="number" id="nb_annees_restantes" :value="displayNbAnneesRestantes"
              @input="nb_annees_restantes = $event.target.value" required />
          </div>
          <div class="form-group form-group--selfie">
            <label for="photo_selfie_input">Selfie / photo d'identité (obligatoire)</label>
            <p class="photo-selfie-hint">
              Photo de <strong>votre visage seul</strong>, nette, bien éclairée et de face (sans lunettes de soleil ni
              autre
              personne).
            </p>
            <div v-if="isPersonalInfoLocked" class="photo-selfie-locked" role="status">
              ✓ Photo et pièce d'identité enregistrées pour cette étape.
            </div>
            <template v-else>
              <div v-if="faceDetectionLoading && !photoCaptured && !webcamActive" class="photo-selfie-loading"
                role="status">
                <p>Préparation de la vérification faciale… (quelques secondes)</p>
              </div>

              <p v-if="selfieNotice" class="photo-selfie-notice" :class="`photo-selfie-notice--${selfieNotice.type}`"
                role="alert">
                {{ selfieNotice.text }}
              </p>

              <div v-if="!webcamActive && !photoCaptured" class="photo-selfie-options">
                <!-- Ouvrir la caméra n'attend pas le modèle : il finit de charger pendant
                   que l'utilisateur se cadre, et la vérification l'attend au besoin. -->
                <button type="button" class="btn btn-primary photo-selfie-options__btn" @click="startSelfieCapture">
                  Prendre un selfie
                </button>
                <label for="photo_selfie_input"
                  class="btn btn-outline photo-selfie-options__btn photo-selfie-options__upload">
                  Importer une photo
                </label>
                <input id="photo_selfie_input" ref="photoFallbackFileInput" type="file" accept="image/*"
                  :capture="isMobileDevice ? 'user' : undefined" class="photo-selfie-input-hidden"
                  @change="handlePhotoUpload" />
              </div>

              <div v-if="webcamActive && !photoCaptured" class="webcam-stage">
                <div ref="webcamContainer" class="webcam-container"></div>
                <div class="webcam-frame-guide" :class="{ 'webcam-frame-guide--aligned': autoCaptureStable }"
                  aria-hidden="true"></div>
                <p class="webcam-frame-hint">
                  <template v-if="autoCaptureStable">
                    Restez immobile… capture automatique dans {{ Math.ceil(autoCaptureRemainingMs / 1000) }}s
                  </template>
                  <template v-else>
                    Centrez votre visage dans le cadre, regardez la caméra.
                  </template>
                </p>
                <div class="webcam-section webcam-section--controls">
                  <div class="webcam-controls">
                    <button type="button" class="btn btn-success" :disabled="selfieChecking" :aria-busy="selfieChecking"
                      @click="capturePhoto">
                      {{ selfieChecking ? 'Vérification…' : '✓ Capturer' }}
                    </button>
                    <button type="button" class="btn btn-secondary" :disabled="selfieChecking" @click="stopWebcam">
                      ✕ Annuler
                    </button>
                  </div>
                  <p v-if="webcamError" class="webcam-error-msg">{{ webcamError }}</p>
                </div>
              </div>

              <canvas ref="photoCanvas" class="photo-canvas-hidden"></canvas>

              <div v-if="photoCaptured" class="webcam-section webcam-section--preview">
                <img :src="photoPreview" alt="Aperçu de votre selfie" class="photo-preview photo-preview--large" />
                <button type="button" class="btn btn-warning" @click="retakePhoto">
                  Reprendre la photo
                </button>
                <p v-if="!selfieNotice || selfieNotice.type !== 'error'" class="photo-selfie-accepted" role="status">
                  ✓ Photo acceptée
                </p>
              </div>
            </template>
          </div>
          <div class="form-group">
            <label for="type_justificatif_identite">Type d'identité</label>
            <input v-if="isPersonalInfoLocked" type="text" :value="'***'" readonly class="login-field" />
            <select v-else id="type_justificatif_identite" v-model="type_justificatif_identite" required>
              <option value="">-- Sélectionnez un type --</option>
              <option value="cni">Carte Nationale d'Identité (CNI)</option>
              <option value="passeport">Passeport</option>
              <option value="titre_sejour">Titre de séjour</option>
              <option value="permis_conduire">Permis de conduire</option>
            </select>
          </div>
          <div class="form-group" v-if="!isPersonalInfoLocked">
            <label for="justificatif_identite">Justificatif d'identité (PDF ou image)</label>
            <input type="file" id="justificatif_identite" accept=".pdf,image/*"
              @change="handleFileChange($event, 'justificatif_identite')" />
            <small v-if="justificatif_identite_filename" class="text-muted" style="display:block;margin-top:6px;">
              Fichier sélectionné : <strong>{{ justificatif_identite_filename }}</strong>
            </small>
          </div>
        </div>
      </div>

      <div v-show="showRestOfForm && isWizardStep('page2_coordonnees')" class="form-section">
        <h3 class="section-title">
          <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          Coordonnées
        </h3>
        <div class="form-grid form-grid--2cols">
          <div class="form-group">
            <label for="email">Email</label>
            <input type="email" id="email" v-model="email" readonly />
          </div>
          <!-- Champs modifiables (UserProfile) -->
          <div class="form-group">
            <label for="telMobile">Téléphone Mobile</label>
            <input type="tel" id="telMobile" v-model="telMobile" />
          </div>
          <div class="form-group">
            <label for="adresse">Adresse</label>
            <input type="text" id="adresse" v-model="adresse" />
          </div>
          <!-- Champs modifiables (UserProfile) -->
          <div class="form-group">
            <label for="ville">Ville</label>
            <input type="text" id="ville" v-model="ville" />
          </div>
          <div v-if='isProOffer' class="form-group">
            <label for="secteur">Secteur d'activité</label>
            <input type="text" id="secteur" v-model="secteur" />
          </div>
        </div>
      </div>

      <div v-show="showRestOfForm && isWizardStep('page2b_situation')" class="form-section">
        <h3 class="section-title">
          <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z" />
          </svg>
          Situation Familiale
          <span class="section-status-badge"
            :class="isSectionComplete('situation') ? 'section-status-badge--done' : 'section-status-badge--pending'">
            {{ isSectionComplete('situation') ? '✓ Complété' : 'À compléter' }}
          </span>
        </h3>
        <div class="form-grid form-grid--2cols">
          <div class="form-group">
            <label for="situation">Situation Familiale</label>
            <select id="situation" v-model="situation" required>
              <option value="">Sélectionnez...</option>
              <option value="Celibataire">Célibataire</option>
              <option value="Marié(e)">Marié(e)</option>
              <option value="En concubinage">En concubinage</option>
              <option value="Divorcé(e)">Divorcé(e)</option>
              <option value="Veuf">Veuf/Veuve</option>
            </select>
          </div>
          <div class="form-group">
            <label for="regim">Régime matrimonial</label>
            <select id="regim" v-model="regim" required>
              <option value="">Sélectionnez...</option>
              <option value="Aucun">Aucun</option>
              <option value="Communauté de biens">Communauté de biens</option>
              <option value="Séparation de biens">Séparation de biens</option>
            </select>
          </div>
          <div class="form-group">
            <label for="nb_enfants">Nombre d'enfants</label>
            <input type="number" id="nb_enfants" v-model="nb_enfants" min="0" required />
          </div>
          <div class="form-group">
            <label for="nb_enfants_charges">Enfants à charge</label>
            <input type="number" id="nb_enfants_charges" v-model="nb_enfants_charges" min="0" required />
          </div>
          <div class="form-group">
            <label for="nbenfants_non_charges">Enfants non à charge</label>
            <input type="number" id="nbenfants_non_charges" v-model="nbenfants_non_charges" min="0" />
          </div>
        </div>
      </div>

      <div v-show="showRestOfForm && isWizardStep('page2c_patrimoine')" class="form-section">
        <h3 class="section-title">
          <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z" />
          </svg>
          Patrimoine Immobilier
        </h3>
        <div class="form-grid">
          <div class="form-group">
            <label for="bien_usage">Nombre de biens d'usage (biens dont vous êtes propriétaire qui ne génèrent pas de
              revenus locatifs)</label>
            <input type="number" id="bien_usage" v-model="bien_usage" min="0" />
          </div>
          <div class="form-grid form-grid--2cols form-grid--inline-pair">
            <div class="form-group">
              <label for="valeurs_bien_usage">Valeur approximative des biens d'usage : </label>
              <input type="number" id="valeurs_bien_usage" v-model="valeurs_bien_usage" min="0" />
            </div>
            <div class="form-group">
              <label for="biens_generateurs">Nombre de biens générant des revenus :</label>
              <input type="number" id="biens_generateurs" v-model="biens_generateurs" min="0" />
            </div>
          </div>
          <div class="form-grid form-grid--2cols form-grid--inline-pair">
            <div class="form-group">
              <label for="valeurs_biens_generateurs">Valeur approximative des biens générant des revenus :</label>
              <input type="number" id="valeurs_biens_generateurs" v-model="valeurs_biens_generateurs" min="0" />
            </div>
            <div class="form-group">
              <label for="revenus_locatifs">Montant mensuel estimé des revenus locatifs :</label>
              <input type="number" id="revenus_locatifs" v-model="revenus_locatifs" min="0" />
            </div>
          </div>
          <div class="form-grid form-grid--2cols form-grid--inline-pair">
            <div class="form-group">
              <label for="terrains">Détenez-vous des terrains non bâtis ?</label>
              <select id="terrains" v-model="terrains">
                <option value="">Sélectionnez...</option>
                <option value="Oui">Oui</option>
                <option value="Non">Non</option>
              </select>
            </div>
            <div class="form-group">
              <label for="valeurterrains">Valeur monétaire de tous les terrains non bâtis :</label>
              <input type="number" id="valeurterrains" v-model="valeurterrains" min="0" />
            </div>
          </div>
          <div class="form-group">
            <label for="documents_propriete">Décrivez les documents de propriété que vous detenez(ACD, attestation
              ...):</label>
            <textarea id="documents_propriete" v-model="documents_propriete" rows="3"
              placeholder="Décrivez en détail...">
        </textarea>
          </div>
        </div>
      </div>

      <div id="section-activites" v-show="showRestOfForm && isWizardStep('page2d_activites')" class="form-section">
        <h3 class="section-title">
          <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
          </svg>
          Activités Professionnelles
          <span class="section-status-badge"
            :class="isSectionComplete('activites') ? 'section-status-badge--done' : 'section-status-badge--pending'">
            {{ isSectionComplete('activites') ? '✓ Complété' : 'À compléter' }}
          </span>
        </h3>
        <div class="form-grid">
          <div class="form-group">
            <label for="possede_entreprise">Possédez-vous une ou des entreprises ? </label>
            <select id="possede_entreprise" v-model="possede_entreprise" @change="syncProfessionalActivitiesFromFlags">
              <option value="">Sélectionnez...</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          <div class="form-group">
            <label for="possede_commerce">Possédez-vous un commerce ? </label>
            <select id="possede_commerce" v-model="possede_commerce" @change="syncProfessionalActivitiesFromFlags">
              <option value="">Sélectionnez...</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
          <div v-if="hasProfessionalActivities" class="form-group form-group--full professional-activities">
            <div class="professional-activities__header">
              <p class="professional-activities__title">Activités détenues</p>
              <p class="professional-activities__hint">Ajoutez autant d'activités professionnelles que nécessaire.</p>
            </div>

            <div v-if="activites_professionnelles.length === 0" class="professional-activities__empty">
              Aucune activité ajoutée pour le moment.
            </div>

            <div v-for="(activite, index) in activites_professionnelles" :key="activite.id || index"
              class="professional-activity-card">
              <div class="professional-activity-card__top">
                <strong>Activité {{ index + 1 }}</strong>
                <button type="button" class="professional-activity-card__remove"
                  @click="removeProfessionalActivity(index)">
                  Supprimer
                </button>
              </div>

              <div class="form-grid">
                <div class="form-group">
                  <label :for="`activite_type_${index}`">Type d'activité</label>
                  <select :id="`activite_type_${index}`" v-model="activite.type_activite"
                    @change="syncProfessionalActivityTotals">
                    <option v-if="possede_entreprise === 'Oui'" value="entreprise_formelle">Entreprise formelle</option>
                    <option v-if="possede_commerce === 'Oui'" value="commerce">Commerce</option>
                  </select>
                </div>

                <div class="form-group">
                  <label :for="`activite_description_${index}`">Nom ou description de l'activité</label>
                  <input :id="`activite_description_${index}`" v-model="activite.description" type="text"
                    placeholder="Ex: Boutique, société de services, restaurant..." @input="persistDraft" />
                </div>

                <div class="form-group">
                  <label :for="`activite_revenu_${index}`">Revenu mensuel généré par l'activité ou dividendes
                    mensualisés</label>
                  <input :id="`activite_revenu_${index}`" v-model="activite.revenu_mensuel" type="number" min="0"
                    @input="syncProfessionalActivityTotals" />
                </div>

                <div class="form-group">
                  <label :for="`activite_valeur_${index}`">Valorisation approximative</label>
                  <input :id="`activite_valeur_${index}`" v-model="activite.valorisation" type="number" min="0"
                    @input="syncProfessionalActivityTotals" />
                </div>
              </div>
            </div>

            <div class="professional-activities__actions">
              <button type="button" class="btn btn-outline-primary" @click="addProfessionalActivity()">
                Ajouter une nouvelle activité
              </button>
            </div>

            <div class="professional-activities__summary">
              <div><strong>Revenus mensuels totaux :</strong> {{ totalProfessionalRevenue }}</div>
              <div><strong>Valorisation totale :</strong> {{ totalProfessionalValue }}</div>
            </div>
          </div>
        </div>
      </div>
      <div v-show="showRestOfForm && isWizardStep('page3_finances')" class="page3-finances-step">
        <div id="section-avoirs" class="form-section">
          <h3 class="section-title">
            Avoirs Financiers
            <span class="section-status-badge"
              :class="isSectionComplete('avoirs') ? 'section-status-badge--done' : 'section-status-badge--pending'">
              {{ isSectionComplete('avoirs') ? '✓ Complété' : 'À compléter' }}
            </span>
          </h3>
          <div class="form-grid">
            <div class="form-group col-lg-12">
              <label for="type_avoir">Quels types de comptes bancaires et financiers détenez-vous actuellement? :
                <br>Cochez
                les comptes que vous détenez</label>
              <div class="accounts-list">
                <div class="account-item-horizontal">
                  <div class="account-item-header">
                    <div class="checkbox-wrapper">
                      <input type="checkbox" id="compte_valeur_check" v-model="has_compte_valeur" />
                      <label for="compte_valeur_check">Compte valeur mobilière (compte titre)</label>
                    </div>
                  </div>
                  <div v-if="has_compte_valeur" class="account-item-fields">
                    <div class="field-row">
                      <label>Montant du compte (FCFA)</label>
                      <input id="input-compte-valeur" type="number" v-model="compte_valeur" placeholder="Montant (FCFA)"
                        min="0"
                        :class="{ 'error': has_compte_valeur && (compte_valeur == null || compte_valeur === '' || compte_valeur < 0) }" />
                    </div>
                    <div class="field-row">
                      <label>Montant du revenu généré par ce compte, ramené au mois (FCFA)</label>
                      <input id="input-montant-compte-valeur" type="number" v-model="montant_compte_valeur"
                        placeholder="Montant (FCFA)" min="0" step="1"
                        :class="{ 'error': has_compte_valeur && (montant_compte_valeur != null && montant_compte_valeur < 0) }" />
                    </div>
                  </div>
                </div>
                <div class="account-item-horizontal">
                  <div class="account-item-header">
                    <div class="checkbox-wrapper">
                      <input type="checkbox" id="compte_cheque_check" v-model="has_compte_cheque" />
                      <label for="compte_cheque_check">Compte Chèque</label>
                    </div>
                  </div>
                  <div v-if="has_compte_cheque" class="account-item-fields">
                    <div class="field-row">
                      <label>Montant du compte (FCFA)</label>
                      <input id="input-compte-cheque" type="number" v-model="compte_cheque" placeholder="Montant (FCFA)"
                        min="0" step="1"
                        :class="{ 'error': has_compte_cheque && (!compte_cheque || compte_cheque < 0) }" />
                    </div>
                  </div>
                </div>
                <div class="account-item-horizontal">
                  <div class="account-item-header">
                    <div class="checkbox-wrapper">
                      <input type="checkbox" id="compte_epargne_check" v-model="has_compte_epargne" />
                      <label for="compte_epargne_check">Compte Épargne</label>
                    </div>
                  </div>
                  <div v-if="has_compte_epargne" class="account-item-fields">
                    <div class="field-row">
                      <label>Montant du compte (FCFA)</label>
                      <input id="input-compte-epargne" type="number" v-model="compte_epargne"
                        placeholder="Montant (FCFA)" min="0" step="1"
                        :class="{ 'error': has_compte_epargne && (!compte_epargne || compte_epargne < 0) }" />
                    </div>
                    <div class="field-row">
                      <label>Montant du revenu généré par ce compte, ramené au mois (FCFA)</label>
                      <input type="number" v-model="montant_compte_epargne" placeholder="Montant (FCFA)" min="0"
                        step="1"
                        :class="{ 'error': has_compte_epargne && (montant_compte_epargne != null && montant_compte_epargne < 0) }" />
                    </div>
                  </div>
                </div>
                <div class="account-item-horizontal">
                  <div class="account-item-header">
                    <div class="checkbox-wrapper">
                      <input type="checkbox" id="compte_dat_check" v-model="has_compte_dat" />
                      <label for="compte_dat_check">DAT (dépôt à terme bancaire)</label>
                    </div>
                  </div>
                  <div v-if="has_compte_dat" class="account-item-fields">
                    <div class="field-row">
                      <label>Montant du compte (FCFA)</label>
                      <input id="input-compte-dat" type="number" v-model="compte_dat" placeholder="Montant (FCFA)"
                        min="0" step="1" />
                    </div>
                    <div class="field-row">
                      <label>Montant du revenu généré par ce compte, ramené au mois (FCFA)</label>
                      <input id="input-montant-compte-dat" type="number" v-model="montant_compte_dat"
                        placeholder="Montant (FCFA)" min="0" step="1"
                        :class="{ 'error': has_compte_dat && (montant_compte_dat == null || montant_compte_dat === '' || Number(montant_compte_dat) < 0) }" />
                    </div>
                  </div>
                </div>
                <div class="account-item-horizontal">
                  <div class="account-item-header">
                    <div class="checkbox-wrapper">
                      <input type="checkbox" id="compte_disponible_check" v-model="has_compte_disponible" />
                      <label for="compte_disponible_check">Autres comptes d'épargne ou placements</label>
                    </div>
                  </div>
                  <div v-if="has_compte_disponible" class="account-item-fields">
                    <div class="field-row">
                      <label>Montant du compte (FCFA)</label>
                      <input id="input-compte-disponible" type="number" v-model="compte_disponible"
                        placeholder="Montant (FCFA)" min="0" step="1" />
                    </div>
                    <div class="field-row">
                      <label>Montant du revenu généré par ce compte, ramené au mois (FCFA)</label>
                      <input id="input-montant-compte-disponible" type="number" v-model="montant_compte_disponible"
                        placeholder="Montant (FCFA)" min="0" step="1"
                        :class="{ 'error': has_compte_disponible && (montant_compte_disponible == null || montant_compte_disponible === '' || Number(montant_compte_disponible) < 0) }" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label for="placements_financiers">Avez-vous des placements financiers autres que vos comptes (crypto,
                etc)
                ?</label>
              <textarea id="placements_financiers" v-model="placements_financiers" rows="3"
                placeholder="Décrivez"></textarea>
            </div>
            <div class="form-group">
              <label for="detention_conjointe">Quels sont les comptes détenus conjointement avec une tierce personne ?
                Précisez</label>
              <textarea id="detention_conjointe" v-model="detention_conjointe" rows="3"
                placeholder="Décrivez"></textarea>
            </div>
          </div>
          <div id="anchor-assurances" class="form-grid">
            <div class="form-group">
              <label for="assurance">Quel type d'assurance detenez-vous ? Donnez le montant capitalisé</label>
              <div class="accounts-list">
                <div>
                  <div class="checkbox-wrapper">
                    <input type="checkbox" id="assurance_check" v-model="has_assurance_vie" />
                    <label for="assurance_check">Assurance Vie</label>
                  </div>
                  <input id="input-assurance-vie" v-if="has_assurance_vie" type="number" v-model="assurance_vie"
                    placeholder="Montant (FCFA)" min="0" />
                </div>

                <div>
                  <div class="checkbox-wrapper">
                    <input type="checkbox" id="assurance_produit_check" v-model="has_assurance_produit" />
                    <label for="assurance_produit_check">Assurance Produit</label>
                  </div>
                  <input id="input-assurance-produit" v-if="has_assurance_produit" type="number"
                    v-model="assurance_produit" placeholder="Montant (FCFA)" min="0" />
                </div>

                <div>
                  <div class="checkbox-wrapper">
                    <input type="checkbox" id="assurance_decès_check" v-model="has_assurance_deces" />
                    <label for="assurance_decès_check">Assurance Décès</label>
                  </div>
                  <input id="input-assurance-deces" v-if="has_assurance_deces" type="number" v-model="assurance_deces"
                    placeholder="Montant (FCFA)" min="0" />
                </div>
              </div>
            </div>
            <div class="form-group">
              <label for="placements_financiers">Montant du revenu généré par ces contrats d’assurances, ramené au
                mois</label>
              <input type="number" v-model="montant_assurance_check" placeholder="Montant (FCFA)" min="0" />
            </div>
          </div>
        </div>
      </div>

      <!-- Charges : 4 étapes normales du wizard (comme toutes les autres), sans
           pagination interne dédiée. Chaque page regroupe 2-3 des 10 catégories,
           affichées à plat sans accordéon ; on avance avec la barre Précédent/Suivant
           commune à tout le formulaire. -->
      <div v-show="showRestOfForm && isWizardStep('page3c_charges_1')" class="page3c-finances-step">
        <div class="form-section">
          <h3 class="section-title">
            <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            Vos charges — Logement, transport, assurance
          </h3>
          <div class="charges-fields">
            <h5 class="p" style="color: red;">Veuillez noter que les montants saisis doivent être mensuels</h5>

            <h6 class="charge-category-title">Logement</h6>
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="loyer">Emprunt ou loyer</label>
                <input type="number" id="loyer" v-model="loyer" />
              </div>
              <div class="form-group">
                <label for="telephone">Charge téléphonique </label>
                <input type="number" id="telephone" v-model="telephone" />
              </div>
              <div class="form-group">
                <label for="electricite">Electricité</label>
                <input type="number" id="electricite" v-model="electricite" />
              </div>
              <div class="form-group">
                <label for="gaz">Gaz</label>
                <input type="number" id="gaz" v-model="gaz" />
              </div>
              <div class="form-group">
                <label for="eau">Eau</label>
                <input type="number" id="eau" v-model="eau" />
              </div>
              <div class="form-group">
                <label for="abonnement">Abonnement câble (TV, internet, Satellite, etc.)</label>
                <input type="number" id="abonnement" v-model="abonnement" />
              </div>
              <div class="form-group">
                <label for="ordures">Enlèvement des ordures ménàgères</label>
                <input type="number" id="ordures" v-model="ordures" />
              </div>
              <div class="form-group">
                <label for="reparation">Entretiens ou réparations</label>
                <input type="number" id="reparation" v-model="reparation" />
              </div>
              <div class="form-group">
                <label for="fournitures">Produits ménagers</label>
                <input type="number" id="fournitures" v-model="fournitures" />
              </div>
              <div class="form-group">
                <label for="salaire_personnel_maison">Salaire total du personnel de maison</label>
                <input type="number" id="salaire_personnel_maison" v-model="salaire_personnel_maison" />
              </div>
              <div class="form-group">
                <label for="popote">Popote</label>
                <input type="number" id="popote" v-model="popote" />
              </div>
              <div class="form-group">
                <label for="loyer_autre">Autres</label>
                <input type="number" id="loyer_autre" v-model="loyer_autre" />
              </div>
            </div>
            <h6 class="charge-category-title">Transport <span class="mtn-sai">(les montants saisis doivent être
                mensuels)</span></h6>
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="pret_voiture">Mensualité prêt véhicule</label>
                <input type="number" id="pret_voiture" v-model="pret_voiture" />
              </div>
              <div class="form-group">
                <label for="transport_public">Transport public/Commun</label>
                <input type="number" id="transport_public" v-model="transport_public" />
              </div>
              <div class="form-group">
                <label for="assurance_voiture">Assurance</label>
                <input type="number" id="assurance_voiture" v-model="assurance_voiture" />
              </div>
              <div class="form-group">
                <label for="carburant">Carburant</label>
                <input type="number" id="carburant" v-model="carburant" />
              </div>
              <div class="form-group">
                <label for="entretien_transport">Entretien/Vidange</label>
                <input type="number" id="entretien_transport" v-model="entretien_transport" />
              </div>
              <div class="form-group">
                <label for="autres_transport">Autres</label>
                <input type="number" id="autres_transport" v-model="autres_transport" />
              </div>
            </div>
            <h6 class="charge-category-title">Assurance <span class="mtn-sai">(les montants saisis doivent être
                mensuels)</span></h6>
            <input type="hidden" id="vie" v-model="vie" value="0" />
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="habitat">Habitat</label>
                <input type="number" id="habitat" v-model="habitat" />
              </div>
              <div class="form-group">
                <label for="sante">Santé</label>
                <input type="number" id="sante" v-model="sante" />
              </div>
              <div class="form-group">
                <label for="autresAssurance">Autres</label>
                <input type="number" id="autresAssurance" v-model="autresAssurance" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-show="showRestOfForm && isWizardStep('page3c_charges_2')" class="page3c-finances-step">
        <div class="form-section">
          <h3 class="section-title">
            <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            Vos charges — Animaux, soins, loisirs
          </h3>
          <div class="charges-fields">
            <h5 class="p" style="color: red;">Veuillez noter que les montants saisis doivent être mensuels</h5>

            <h6 class="charge-category-title">Animaux <span class="mtn-sai">(les montants saisis doivent être
                mensuels)</span></h6>
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="nourriture_animaux">Nourriture</label>
                <input type="number" id="nourriture_animaux" v-model="nourriture_animaux" />
              </div>
              <div class="form-group">
                <label for="frais_medicaux">Frais Médicaux</label>
                <input type="number" id="frais_medicaux" v-model="frais_medicaux" />
              </div>
              <div class="form-group">
                <label for="toilettage">Toilettage</label>
                <input type="number" id="toilettage" v-model="toilettage" />
              </div>
              <div class="form-group">
                <label for="jouets">Jouets</label>
                <input type="number" id="jouets" v-model="jouets" />
              </div>
              <div class="form-group">
                <label for="autres_animaux">Autres</label>
                <input type="number" id="autres_animaux" v-model="autres_animaux" />
              </div>
            </div>
            <h6 class="charge-category-title">Soins personnels <span class="mtn-sai">(les montants saisis doivent être
                mensuels)</span></h6>
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="medical">Médical</label>
                <input type="number" id="medical" v-model="medical" />
              </div>
              <div class="form-group">
                <label for="coiffeur">Coiffeur/Manucure</label>
                <input type="number" id="coiffeur" v-model="coiffeur" />
              </div>
              <div class="form-group">
                <label for="habit">Habillement</label>
                <input type="number" id="habit" v-model="habit" />
              </div>
              <div class="form-group">
                <label for="sport_soins">Club de sport</label>
                <input type="number" id="sport_soins" v-model="sport_soins" />
              </div>
              <div class="form-group">
                <label for="cotisations">Cotisations ou frais d'inscription</label>
                <input type="number" id="cotisations" v-model="cotisations" />
              </div>
              <div class="form-group">
                <label for="nettoyage">Nettoyage à sec</label>
                <input type="number" id="nettoyage" v-model="nettoyage" />
              </div>
              <div class="form-group">
                <label for="autre_soins">Autres</label>
                <input type="number" id="autre_soins" v-model="autre_soins" />
              </div>
            </div>
            <h6 class="charge-category-title">Loisirs <span class="mtn-sai">(les montants saisis doivent être
                mensuels)</span></h6>
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="concert">Concert</label>
                <input type="number" id="concert" v-model="concert" />
              </div>
              <div class="form-group">
                <label for="cinema">Cinéma/CD/Vidéo/DVD</label>
                <input type="number" id="cinema" v-model="cinema" />
              </div>
              <div class="form-group">
                <label for="theatre">Théâtre</label>
                <input type="number" id="theatre" v-model="theatre" />
              </div>
              <div class="form-group">
                <label for="sport">Sport/Evènements sportifs</label>
                <input type="number" id="sport" v-model="sport" />
              </div>
              <div class="form-group">
                <label for="restaurant">Restaurant</label>
                <input type="number" id="restaurant" v-model="restaurant" />
              </div>
              <div class="form-group">
                <label for="autres_loisirs">Autres</label>
                <input type="number" id="autres_loisirs" v-model="autres_loisirs" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-show="showRestOfForm && isWizardStep('page3c_charges_3')" class="page3c-finances-step">
        <div class="form-section">
          <h3 class="section-title">
            <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            Vos charges — Épargne, impôts
          </h3>
          <div class="charges-fields">
            <h5 class="p" style="color: red;">Veuillez noter que les montants saisis doivent être mensuels</h5>

            <h6 class="charge-category-title">Emprunts/Epargne <span class="mtn-sai">(les montants saisis doivent être
                mensuels)</span></h6>
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="pret_personnel">Prêt personnel (différent des prêts immobiliers et liés à véhicule
                  éventuellement renseignés plus haut)</label>
                <input type="number" id="pret_personnel" v-model="pret_personnel" />
              </div>
              <div class="form-group">
                <label for="pret_etudiant">Prêt d'etude</label>
                <input type="number" id="pret_etudiant" v-model="pret_etudiant" />
              </div>
              <div class="form-group">
                <label for="epargne">Epargne enveloppe</label>
                <input type="number" id="epargne" v-model="epargne" />
              </div>
            </div>
            <h6 class="charge-category-title">Impôts <span class="mtn-sai">(les montants saisis doivent être
                mensuels)</span></h6>
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="foncier">Foncier (Terrains non bâtis)</label>
                <input type="number" id="foncier" v-model="foncier" />
              </div>
              <div class="form-group">
                <label for="locaux">Locaux (Immobiliers bâtis)</label>
                <input type="number" id="locaux" v-model="locaux" />
              </div>
              <div class="form-group">
                <label for="autres_impots">Autres</label>
                <input type="number" id="autres_impots" v-model="autres_impots" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-show="showRestOfForm && isWizardStep('page3c_charges_4')" class="page3c-finances-step">
        <div class="form-section">
          <h3 class="section-title">
            <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            Vos charges — Études, dons
          </h3>
          <div class="charges-fields">
            <h5 class="p" style="color: red;">Veuillez noter que les montants saisis doivent être mensuels</h5>

            <h6 class="charge-category-title">Etudes & Scolarité <span class="mtn-sai">(les montants saisis doivent être
                mensuels)</span></h6>
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="soi_meme">Soi-même(mensualisé)</label>
                <input type="number" id="soi_meme" v-model="soi_meme" />
              </div>
              <div class="form-group">
                <label for="enfant">Enfants(mensualisé)</label>
                <input type="number" id="enfant" v-model="enfant" />
              </div>
              <div class="form-group">
                <label for="autres_etudes">Autres</label>
                <input type="number" id="autres_etudes" v-model="autres_etudes" />
              </div>
            </div>
            <h6 class="charge-category-title">Dons & Autres <span class="mtn-sai">(les montants saisis doivent être
                mensuels)</span></h6>
            <div class="form-grid form-grid--2cols">
              <div class="form-group">
                <label for="autres">Autres</label>
                <input type="number" id="autres" v-model="autres" />
              </div>
              <div class="form-group">
                <label for="caritatif">Caritatif/Connaissance</label>
                <input type="number" id="caritatif" v-model="caritatif" />
              </div>
              <div class="form-group">
                <label for="dimes">Offrandes/Dimes</label>
                <input type="number" id="dimes" v-model="dimes" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Passifs & revenus : étape à part, séparée d'Avoirs/Charges (page3_finances). -->
      <div v-show="showRestOfForm && isWizardStep('page3b_passifs_revenus')" class="page3b-finances-step">
        <div class="form-section">
          <h3 class="section-title">
            <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            Vos passifs
            <span class="section-status-badge"
              :class="isSectionComplete('passifs') ? 'section-status-badge--done' : 'section-status-badge--pending'">
              {{ isSectionComplete('passifs') ? '✓ Complété' : 'À compléter' }}
            </span>
          </h3>
          <div class="form-grid">
            <div class="form-group">
              <label for="pret_en_cours">Avez-vous actuellement un ou plusieurs prêts en cours ?</label>
              <select id="pret_en_cours" v-model="pret_en_cours" required>
                <option value="">Sélectionnez...</option>
                <option value="Oui">Oui</option>
                <option value="Non">Non</option>
              </select>
            </div>
            <template v-if="pret_en_cours === 'Oui'">
              <div class="form-group">
                <label for="montant_pret">Quel est le montant des encours totaux des prêts ? (Somme de toutes vos dettes
                  restantes dues)</label>
                <input type="number" id="montant_pret" v-model="montant_pret" min="0" required />
              </div>
              <div class="form-group">
                <label for="duree_pret">Quelle est la durée maximum restante de ce/ces prêts ? (en mois)</label>
                <input type="number" id="duree_pret" v-model="duree_pret" min="0" required />
              </div>
              <div class="form-group">
                <label for="montant_mensualite">Montant remboursé par mois de tous les prêts</label>
                <input type="number" id="montant_mensualite" v-model="montant_mensualite" min="0" required />
              </div>
            </template>
          </div>
        </div>
        <div class="form-section">
          <h3 class="section-title">
            <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            Vos revenus
          </h3>
          <div class="form-grid">
            <div class="form-group">
              <label for="revenu_mensuel_reel">Revenu mensuel réel</label>
              <input type="number" id="revenu_mensuel_reel" :value="revenuMensuelReelCalcule" readonly />
            </div>
            <div v-if="isProOffer">
              <div class="form-grid form-grid--2cols">
                <div class="form-group">
                  <label :for="`chiffre_affaires_0`">Les trois (03) derniers chiffres d'affaires</label>
                  <input v-for="(ca, index) in chiffres_affaires" :key="`ca_${index}`" type="number"
                    :id="`chiffre_affaires_${index}`" v-model.number="chiffres_affaires[index]" min="0" />
                </div>
                <div class="form-group">
                  <label :for="`resultat_net_0`">Les trois (03) derniers résultats nets</label>
                  <input v-for="(rn, index) in resultats_nets" :key="`rn_${index}`" type="number"
                    :id="`resultat_net_${index}`" v-model.number="resultats_nets[index]" />
                </div>
                <div class="form-group">
                  <label for="ebe_ebitda">EBE / EBITDA</label>
                  <input type="number" id="ebe_ebitda" v-model.number="ebe_ebitda" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-show="showRestOfForm && isWizardStep('page4_objectifs')" class="form-section">
        <h3 class="section-title">
          <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          Vos objectifs et souhaits
        </h3>
        <div class="form-grid">
          <div class="form-group">
            <label for="objectif">Quels sont vos objectifs et souhaits ?</label>
            <span class="mtn-sai">(vos trois (03) objectifs les plus importants)</span>
            <textarea id="objectif" v-model="objectif" rows="5" placeholder="Décrivez">
        </textarea>
          </div>
          <div class="form-group">
            <label for="montantObjectif">Quel montant detenez-vous en apport initial pour vos projets ?</label>
            <input type="number" id="montantObjectif" v-model="montantObjectif" />
          </div>
        </div>
      </div>

      <!-- Justificatifs financiers (après paiement) -->
      <div v-show="showRestOfForm && isWizardStep('page5_documents_payment')" id="section-documents-payment"
        class="form-section page4-documents">
        <h3 class="section-title">
          <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
          </svg>
          Justificatifs complémentaires
        </h3>
        <p class="page4-documents__intro">
          Téléversez les documents ci-dessous pour finaliser votre dossier avant validation.
        </p>
        <div class="form-grid">
          <div id="anchor-justificatif-residence" class="form-group">
            <label for="justificatif_residence">Justificatif de résidence</label>
            <input type="file" id="justificatif_residence" accept=".pdf,image/*"
              @change="handleFileChange($event, 'justificatif_residence')" />
            <small v-if="justificatif_residence_filename" class="text-muted" style="display:block;margin-top:6px;">
              Fichier sélectionné : <strong>{{ justificatif_residence_filename }}</strong>
            </small>
          </div>
          <div id="anchor-justificatif-revenu" class="form-group">
            <label for="justificatif_revenu">Bulletin de salaire / justificatif de revenu</label>
            <input type="file" id="justificatif_revenu" accept=".pdf,image/*"
              @change="handleFileChange($event, 'justificatif_revenu')" />
            <small v-if="justificatif_revenu_filename" class="text-muted" style="display:block;margin-top:6px;">
              Fichier sélectionné : <strong>{{ justificatif_revenu_filename }}</strong>
            </small>
          </div>
          <div id="anchor-releve" class="form-group">
            <label for="releve">Relevé bancaire (6 derniers mois)</label>
            <input type="file" id="releve" accept=".pdf,image/*" @change="handleFileChange($event, 'releve')" />
            <small v-if="releve_filename" class="text-muted" style="display:block;margin-top:6px;">
              Fichier sélectionné : <strong>{{ releve_filename }}</strong>
            </small>
          </div>
        </div>
      </div>

      <div v-show="showRestOfForm && isWizardStep('page6_submit')"
        class="buttons-container form-section wizard-submit-panel">
        <div v-if="isReportGenerated" class="notification success submit-done-notice" role="status" aria-live="polite">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
          <div class="submit-done-notice__text">
            <strong>Analyse envoyée avec succès</strong>
            <span>
              Votre analyse a été enregistrée avec succès. Votre demande est désormais en cours de traitement. Vous serez notifié(e) par e-mail dans un délai maximum de 72 heures dès que vos résultats seront disponibles. Vous pourrez ensuite les consulter dans l’onglet « Résultats d’analyse ».
            </span>
            <router-link class="submit-done-notice__link" to="/analyse-patrimoniale/analyses-sups">
              Voir mes résultats
            </router-link>
            <router-link class="submit-done-notice__link" to="/analyse-patrimoniale/contact">
              Contacter un conseiller
            </router-link>
            <router-link class="submit-done-notice__link" to="/analyse-patrimoniale/dashboards">
              Retour au tableau de bord
            </router-link>
            <button v-if="typeOffre === 'duo' && !duoWorkflowByOrdre[ordreDuo === 1 ? 2 : 1]?.isReportGenerated"
              type="button" class="submit-done-notice__link submit-done-notice__link--btn"
              @click="switchOrdreDuo(ordreDuo === 1 ? 2 : 1)">
              Continuer le dossier de l’autre conjoint
            </button>
          </div>
        </div>
        <p v-else class="wizard-submit-intro">
          Vous avez terminé le questionnaire. Lancez votre analyse patrimoniale — vous serez notifié par email sous 72
          h.
        </p>
      </div>
    </form>

    <!-- En flux normal (pas de position fixed/sticky) : la barre n'apparaît
         qu'une fois arrivé en bas de la page, comme dans Diagnostic.vue
         (.diagnostic-actions), donc ne peut jamais recouvrir un champ. -->
    <nav v-if="showWizardNav" class="wizard-nav" aria-label="Navigation du formulaire">
      <button type="button" class="wizard-nav__btn wizard-nav__btn--ghost" :disabled="!canWizardPrev"
        @click="wizardPrev">
        ← Précédent
      </button>
      <span class="wizard-nav__status">{{ currentWizardStepLabel }}</span>
      <button v-if="showWizardNextButton" type="button" class="wizard-nav__btn wizard-nav__btn--primary"
        :disabled="wizardNextDisabled" :aria-busy="personalInfoSaving || financialDocumentsSaving" @click="wizardNext">
        Suivant →
      </button>
      <button v-else-if="showWizardSubmitButton" type="button" class="wizard-nav__btn wizard-nav__btn--primary"
        :disabled="loading" :aria-busy="loading" @click="handleSubmit">
        {{ submitButtonLabel }}
      </button>
    </nav>

    <div v-if="(loading && !isReportGenerated) || personalInfoSaving || financialDocumentsSaving" class="submit-overlay"
      role="status" aria-live="polite" aria-busy="true" aria-labelledby="submit-overlay-title"
      aria-describedby="submit-overlay-desc">
      <div class="submit-overlay__panel">
        <span class="spinner-border text-primary submit-overlay__spinner" role="status" aria-hidden="true"></span>
        <p id="submit-overlay-title" class="submit-overlay__title">
          {{ personalInfoSaving
            ? 'Validation de vos informations en cours…'
            : financialDocumentsSaving
              ? 'Enregistrement de vos justificatifs en cours…'
              : 'Envoi de votre dossier en cours…' }}
        </p>
        <p id="submit-overlay-desc" class="submit-overlay__hint">
          {{ personalInfoSaving
            ? 'Enregistrement sécurisé de votre photo et de votre pièce d\'identité. Ne fermez pas cette page.'
            : financialDocumentsSaving
              ? 'Enregistrement sécurisé de vos justificatifs. Ne fermez pas cette page.'
              : 'Le traitement peut prendre jusqu\'à une minute (photos et justificatifs). Ne fermez pas cette page.' }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import '../../../assets/css/analysepat.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getErrorMessage, formatValidationErrors } from '@/utils/errorMessages';
import { escapeHtml } from '@/utils/markdownHtml';
import { getUserRole } from '@/utils/auth';
import { clearAuthenticatedSession, getProfileUserId, storeUserProfileFromApi, isAuthenticatedSession } from '@/utils/sessionAuth';
import { createSubmitGuard } from '@/utils/submitGuard';
import {
  trackMonAnalyseFormStart,
  trackMonAnalysePersonalInfoSubmit,
  trackMonAnalyseKycApproved,
  trackMonAnalyseFormSubmit,
  trackMonAnalyseComplete,
  trackMonAnalyseFormAbandon,
  resolveMonAnalyseLastSection,
} from '@/utils/analytics';
import { ensureFaceApiReady, SELFIE_FACE_MESSAGES } from '@/utils/faceDetectionLoader';
import { offerLabel, normalizePayableOffer } from '@/utils/paymentPricing';
import { WIZARD_STEPS, LEGACY_WIZARD_STEP_MAP } from '@/utils/wizardSteps';
import {
  MIN_REVENU_MENSUEL_NET_FCFA,
  meetsMinRevenuMensuel,
  formatMinRevenuMensuelLabel,
} from '@/utils/eligibility';
import {
  applyFormApatPrefill,
  consumeFormApatPrefill,
  isFormApatFieldEmpty,
  markServerDiagnosticPrefillChecked,
  normalizeDateToIso,
  resolveFormApatPrefillForUser,
  wasServerDiagnosticPrefillChecked,
} from '@/utils/diagnosticPrefill';
import { flushPendingSignatures } from '@/utils/pendingSignatures';

const formSubmitGuard = createSubmitGuard(4000);
const personalInfoSubmitGuard = createSubmitGuard(4000);

const TOKEN_KEYS = {
  ACCESS: 'auth_session',
  REFRESH: null /* httpOnly cookie */,
  ROLE: 'user_role',
  ID: 'user_id',
};

/** Flux caméra persistant noir (paramètres de confidentialité Windows, caméra IR
 *  sélectionnée par défaut, caméra déjà utilisée par une autre appli) : le stream
 *  démarre sans erreur mais ne renvoie aucune image exploitable. */
const CAMERA_BLACK_FEED_MESSAGE =
  "Votre caméra ne renvoie aucune image (écran noir). Fermez les autres applications qui l'utilisent, "
  + "vérifiez que l'accès caméra est autorisé pour les applications de bureau dans les paramètres de "
  + "confidentialité de Windows, ou importez une photo depuis votre galerie.";

const POST_PAYMENT_WIZARD_STEPS = new Set([
  'page3_finances',
  'page3b_passifs_revenus',
  'page3c_charges_1',
  'page3c_charges_2',
  'page3c_charges_3',
  'page3c_charges_4',
  'page5_documents_payment',
  'page4_documents_payment',
  'page5_contract',
]);

export default {
  data() {
    return {
      successMessage: '',
      // Informations Personnelles
      last_name: '',
      first_name: '',
      login: '',
      /**
       * Offre Persona: 1 seul formulaire -> 1 verrou.
       * Offre Duo: 2 formulaires distincts (ordre_duo 1 et 2) -> verrou par conjoint.
       *
       * On garde un stockage explicite pour éviter l'effet actuel:
       * "Conjoint 1 valide => section grisée aussi pour Conjoint 2".
       */
      personalInfoLocked: {
        persona: false,
        duo_1: false,
        duo_2: false,
      },
      clientApte: false,
      ga4KycApprovedTracked: false,
      ga4AbandonTracked: false,
      wizardStepId: 'page1_profile',
      // Une fois vrai, permet d'afficher le raccourci « Retourner à l'envoi » plutôt que
      // de forcer un nouveau passage page par page après correction d'un champ manquant.
      hasVisitedSubmitStep: false,
      born: '',
      comborn: '',
      depborn: '',
      fonction: '',

      // Coordonnées
      email: '',
      telMobile: '',
      adresse: '',
      ville: '',
      secteur: '',

      // Situation Familiale
      situation: '',
      regim: '',
      salaire: '',
      nb_enfants: null,
      nb_enfants_charges: null,
      nbenfants_non_charges: null,
      possede_entreprise: '',
      possede_commerce: '',
      revenus_entreprise: null,
      valeur_entreprise: null,
      activites_professionnelles: [],
      nb_annees_restantes: null,

      // Patrimoine Immobilier
      bien_usage: null,
      valeurs_bien_usage: null,
      choix_biens_usage: 'Non',
      biens_generateurs: null,
      valeurs_biens_generateurs: null,
      revenus_locatifs: null,
      terrains: '',
      valeurterrains: null,
      documents_propriete: '',

      // Avoirs Financiers
      // Comptes bancaires
      has_compte_valeur: false,
      compte_valeur: null,
      montant_compte_valeur: null,
      has_compte_cheque: false,
      compte_cheque: null,
      has_compte_epargne: false,
      compte_epargne: null,
      montant_compte_epargne: null,
      has_compte_disponible: false,
      compte_disponible: null,
      montant_compte_disponible: null,
      compte_dat: null,
      has_compte_dat: false,
      montant_compte_dat: null,


      // Assurances
      has_assurance_vie: false,
      assurance_vie: null,
      has_assurance_produit: false,
      assurance_produit: null,
      has_assurance_deces: false,
      assurance_deces: null,
      montant_assurance_check: null,

      // Autres informations financières
      placements_financiers: '',
      detention_conjointe: '',
      // Nouveaux justificatifs
      type_justificatif_identite: '',
      justificatif_identite: '',
      justificatif_identite_filename: '',
      justificatif_residence: '',
      justificatif_residence_filename: '',
      photo: '',
      photo_filename: '',
      justificatif_revenu: '',
      justificatif_revenu_filename: '',
      releve: '',
      releve_filename: '',
      /** Fichiers bruts pour envoi multipart (évite base64 JSON volumineux). */
      uploadFileRefs: {
        photo: null,
        justificatif_identite: null,
        justificatif_residence: null,
        justificatif_revenu: null,
        releve: null,
      },

      // Passifs
      pret_en_cours: '',
      montant_pret: null,
      duree_pret: null,
      montant_mensualite: null,

      // Revenus
      revenu_mensuel_reel: null,
      // Offre pro : historique déclaratif (3 derniers exercices)
      chiffres_affaires: [null, null, null],
      resultats_nets: [null, null, null],
      ebe_ebitda: null,

      // Charges
      // Logement
      loyer: null,
      telephone: null,
      electricite: null,
      gaz: null,
      eau: null,
      abonnement: null,
      ordures: null,
      reparation: null,
      fournitures: null,
      salaire_personnel_maison: null,
      popote: null,
      loyer_autre: null,

      // Transport
      pret_voiture: null,
      transport_public: null,
      assurance_voiture: null,
      carburant: null,
      entretien_transport: null,
      autres_transport: null,

      // Assurance
      habitat: null,
      sante: null,
      vie: null,
      autresAssurance: null,

      // Animaux
      nourriture_animaux: null,
      frais_medicaux: null,
      toilettage: null,
      jouets: null,
      autres_animaux: null,

      // Soins personnels
      medical: null,
      coiffeur: null,
      habit: null,
      sport_soins: null,
      cotisations: null,
      autre_soins: null,
      nettoyage: null,

      // Loisirs
      concert: null,
      cinema: null,
      theatre: null,
      sport: null,
      restaurant: null,
      autres_loisirs: null,

      // Emprunts/Epargne
      pret_personnel: null,
      pret_etudiant: null,
      epargne: null,

      // Impôts
      foncier: null,
      locaux: null,
      autres_impots: null,

      // Etudes & Scolarité
      soi_meme: null,
      enfant: null,
      autres_etudes: null,

      // Dons & Autres
      autres: null,
      caritatif: null,
      dimes: null,

      // Objectifs
      objectif: '',
      montantObjectif: null,

      // Etat du formulaire
      errorMessage: '',
      loading: false,
      personalInfoSaving: false,
      financialDocumentsSaving: false,
      financialDocumentsSaved: false,
      analyseId: null,
      downloadingPDF: false,
      isReportGenerated: false,
      photoPreview: '',

      // Webcam capture
      webcamActive: false,
      webcamStream: null,
      webcamVideoElement: null, // Élément vidéo créé dynamiquement
      photoCaptured: false,
      webcamError: '',

      // Face detection
      faceDetectionReady: false,
      faceDetectionLoading: false,
      /** Analyse d'une photo en cours (capture ou import). */
      selfieChecking: false,
      /** Retour affiché sous le bloc selfie : { type: 'error'|'info', text }. */
      selfieNotice: null,

      // Capture automatique : détection continue pendant que la webcam tourne
      autoCaptureTimer: null,
      autoCaptureStableSince: null,
      autoCaptureStable: false,
      autoCaptureRemainingMs: 0,
      autoCaptureTriggering: false,
      // Offre Duo : type_offre depuis le profil client, ordreDuo = 1 ou 2 (conjoint)
      typeOffre: 'persona',
      ordreDuo: 1,
      // Duo : copie session des champs « infos perso » par conjoint (filet si localStorage incomplet / quota).
      duoPersonalStashByOrdre: { 1: null, 2: null },
      /** Offre Duo : état wizard / soumission mémorisé par conjoint (évite la fuite C1 → C2). */
      duoWorkflowByOrdre: { 1: null, 2: null },
      clientAptePolling: false,
      clientAptePollAttempts: 0,
      // Sections repliables des étapes 2 & 3 (true = ouvert). Seule la première
      // sous-section de chaque étape est ouverte par défaut, pour ne pas noyer
      // l'utilisateur sous tous les champs d'un coup.
      mobileSections: {
        coordonnees: true,
        situation: false,
        immobilier: false,
        activites: false,
        avoirs: true,
        // Passifs (1re section de page3b) et Charges (seule section de page3c) sont
        // désormais chacune en tête de leur propre étape : ouvertes par défaut,
        // comme "avoirs"/"coordonnees" le sont déjà pour les leurs.
        passifs: true,
        revenus: false,
        charges: true,
        objectifs: true,
        documents: true,
      },
      // Indique que les infos personnelles (dont justificatif_residence) ont été restaurées depuis le profil/brouillons.
      // Sert à éviter l'affichage fugitif du bandeau "Document manquant" à la reconnexion.
      initialPersonalInfoLoaded: false,
    };
  },
  async created() {
    // 1 Charger le profil (pour isPersonalInfoLocked, login, etc.)
    await this.loadUserProfile();
    // 1b Offre Duo : récupérer type_offre depuis le profil client
    try {
      await this.loadClientProfileForOffre();
    } catch (_) { }
    // 2 Brouillon serveur : retrouver les données (par ordre_duo si Duo)
    try {
      await this.loadDraftFromServer();
    } catch (_) { }
    // 3 Brouillon local (peut compléter avec fichiers non envoyés au serveur)
    try {
      const draft = localStorage.getItem(this.getDraftKey());
      if (draft) {
        const parsed = JSON.parse(draft);
        // Compat anciens brouillons: isPersonalInfoLocked était un bool global.
        // On le convertit en verrou du conjoint courant / persona.
        if (parsed.personalInfoLocked == null && parsed.isPersonalInfoLocked != null) {
          const legacy = parsed.isPersonalInfoLocked === true || parsed.isPersonalInfoLocked === 'true';
          const t = String(this.typeOffre || 'persona').toLowerCase();
          if (t === 'duo') {
            const key = this.ordreDuo === 2 ? 'duo_2' : 'duo_1';
            parsed.personalInfoLocked = { persona: false, duo_1: false, duo_2: false, [key]: legacy };
          } else {
            parsed.personalInfoLocked = { persona: legacy, duo_1: false, duo_2: false };
          }
        }
        Object.assign(this, parsed);
        this.restoreProfessionalActivities(parsed.activites_professionnelles, parsed);
      }
      try { this.mergeDraftDocumentsFromSessionBackup(); } catch (_) { }
    } catch (_) { }
    // À ce stade, les données initiales sont restaurées (profil + brouillons éventuels)
    this.normalizeStateAfterDraftMerge();
    this.migrateWizardStepId();
    try { await this.applyRouteWizardStep(); } catch (_) { }
    try {
      // Lien de rappel « justificatifs manquants » depuis le tableau de bord.
      if (this.$route?.query?.goto === 'documents' && this.isPersonalInfoLocked) {
        this.goToWizardStep('page5_documents_payment');
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => { });
      }
    } catch (_) { }
    try { this.syncPersonalPhotoUiAfterDraftChange(); } catch (_) { }
    try { this.hydrateDuoPersonalStashFromLocalStorage(); } catch (_) { }
    this.ensureWizardStepValid();
    this.initialPersonalInfoLoaded = true;
    try { this.applyDiagnosticPrefillIfNeeded(); } catch (_) { }
    try {
      await flushPendingSignatures({
        clientApte: this.clientApte,
        email: this.email || '',
      });
    } catch (_) { }
  },
  mounted() {
    trackMonAnalyseFormStart({ offer: this.typeOffre });
    this._onFormAbandon = () => this.trackFormAbandonIfNeeded();
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this._onFormAbandon);
    }
    this.startAutoSaveInterval();
    // Différer les appels plus lourds après le premier rendu pour réduire la latence perçue
    this.$nextTick(() => {
      const runDeferred = async () => {
        try { await this.prefillFromLatestAnalysis(); } catch (_) { }
        try { await this.loadClientProfile(); } catch (_) { }
        // Après latest analysis / profil : réinjecter le diagnostic sur les champs encore vides
        // (évite le wipe `|| 0` et date_born null).
        try { this.applyDiagnosticPrefillIfNeeded(true); } catch (_) { }
        this.initFaceDetection();
      };
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(runDeferred, { timeout: 1500 });
      } else {
        setTimeout(runDeferred, 200);
      }
    });
  },
  beforeUnmount() {
    this.trackFormAbandonIfNeeded();
    if (typeof window !== 'undefined' && this._onFormAbandon) {
      window.removeEventListener('beforeunload', this._onFormAbandon);
    }
    this.stopWebcam();
    if (this._autoSaveInterval) clearInterval(this._autoSaveInterval);
    if (this._saveDraftToServerTimer) clearTimeout(this._saveDraftToServerTimer);
    this.stopClientAptePolling();
  },

  watch: {
    clientApte(newVal, oldVal) {
      if (newVal) this.stopClientAptePolling();
      if (newVal && !oldVal) {
        flushPendingSignatures({
          clientApte: true,
          email: this.email || '',
        }).catch(() => { });
      }
      if (newVal && !oldVal && this.isPersonalInfoLocked && !this.ga4KycApprovedTracked) {
        this.ga4KycApprovedTracked = true;
        trackMonAnalyseKycApproved({ offer: this.typeOffre });
      }
      if (newVal && !oldVal && (this.isWizardStep('page3_finances') || this.isWizardStep('page3b_passifs_revenus'))) {
        this.scrollWizardIntoView();
      }
      if (newVal && !oldVal && this.isWizardStep('page5_documents_payment')) {
        this.scrollWizardIntoView();
      }
      // Si le paiement n’est plus valide, ramener hors des sections post-paiement.
      if (!newVal && oldVal) {
        this.ensureWizardStepValid();
      }
    },
    /**
     * Le reste du formulaire utilise surtout des v-model "simples" sans appeler persistDraft()
     * explicitement à chaque saisie. En offre Duo, sans cette surveillance, l'utilisateur peut
     * croire que Conjoint 1 et 2 partagent les mêmes données car rien n'est sauvegardé/chargé
     * au changement d'onglet (hors actions explicites).
     *
     * On déclenche donc un persistDraft() dès que les champs "serveur" du brouillon changent,
     * plus des empreintes légères sur photo / justificatifs (getDraftKeysForServer les exclut
     * pour ne pas deep-watch des data URLs volumineuses).
     */
    draftWatchTrigger: {
      handler() {
        try { this.persistDraft(); } catch (_) { }
      },
      deep: true,
    },
    availableWizardSteps: {
      handler() {
        this.ensureWizardStepValid();
      },
      immediate: true,
    },
  },

  computed: {
    userRole() {
      return getUserRole() || '';
    },
    user_id() {
      return getProfileUserId();
    },
    submitButtonLabel() {
      if (this.isReportGenerated) return 'Analyse déjà envoyée';
      if (this.loading) return 'Envoi en cours…';
      return 'Lancer mon analyse patrimoniale';
    },
    submitButtonAriaLabel() {
      if (this.isReportGenerated) {
        return 'Analyse déjà envoyée. Consultez vos résultats dans l’onglet Résultats d’analyses.';
      }
      if (this.loading) {
        return 'Envoi en cours, veuillez patienter et ne pas fermer la page';
      }
      return 'Lancer mon analyse patrimoniale';
    },
    availableWizardSteps() {
      return WIZARD_STEPS;
    },
    currentWizardStepIndex() {
      return this.availableWizardSteps.findIndex((step) => step.id === this.wizardStepId);
    },
    currentWizardStepMeta() {
      const idx = this.currentWizardStepIndex;
      return idx >= 0 ? this.availableWizardSteps[idx] : null;
    },
    currentWizardStepLabel() {
      return this.currentWizardStepMeta?.label || 'Diagnostic patrimonial';
    },
    wizardStepNumber() {
      const idx = this.currentWizardStepIndex;
      return idx >= 0 ? idx + 1 : 1;
    },
    wizardStepTotal() {
      return Math.max(1, this.availableWizardSteps.length);
    },
    canWizardPrev() {
      return this.currentWizardStepIndex > 0;
    },
    canWizardNext() {
      const idx = this.currentWizardStepIndex;
      return idx >= 0 && idx < this.availableWizardSteps.length - 1;
    },
    showWizardNextButton() {
      return this.canWizardNext;
    },
    wizardNextDisabled() {
      if (this.isReportGenerated) return false;
      if (this.personalInfoSaving || this.financialDocumentsSaving) return true;
      if (this.isWizardStep('page1_profile') && !this.isPersonalInfoLocked && !this.isPersonalInfoComplete) {
        return true;
      }
      return false;
    },
    showWizardSubmitButton() {
      return this.showRestOfForm && this.isWizardStep('page6_submit') && !this.isReportGenerated;
    },
    showWizardNav() {
      return this.availableWizardSteps.length > 0;
    },
    /** Raccourci affiché après correction d'un champ manquant : évite de refaire tout
     * le parcours page par page pour retourner à l'étape d'envoi déjà atteinte une fois. */
    canJumpToSubmit() {
      return (
        this.hasVisitedSubmitStep
        && !this.isWizardStep('page6_submit')
        && !this.isReportGenerated
        && this.validationErrorItems.length === 0
      );
    },
    questionnaireProgress() {
      let filled = 0;
      const total = 8;
      if (this.telMobile && this.ville) filled += 1;
      if (this.situation && this.regim) filled += 1;
      if ((this.bien_usage != null && this.bien_usage !== '') || (this.biens_generateurs != null && this.biens_generateurs !== '')) filled += 1;
      if (this.possede_entreprise || this.possede_commerce) filled += 1;
      if (this.has_compte_valeur || this.has_compte_cheque || this.has_compte_epargne || this.has_compte_dat) filled += 1;
      if (this.pret_en_cours) filled += 1;
      if (Number(this.revenuMensuelReelCalcule) > 0) filled += 1;
      if (this.objectif && String(this.objectif).trim()) filled += 1;
      return { filled, total };
    },
    formProgressStepIndex() {
      return this.wizardStepNumber;
    },
    formProgressPhaseLabel() {
      if (this.isReportGenerated) return 'Analyse envoyée';
      return this.currentWizardStepLabel;
    },
    /**
     * Pourcentage d'étapes *terminées* : à l'arrivée sur la page 1 rien n'est encore
     * rempli, afficher 17 % laisserait croire à une progression acquise. Sur la page
     * de validation finale (dernière étape), tout est déjà rempli : on affiche 100 %
     * dès l'arrivée sur cette page plutôt que d'attendre l'envoi effectif.
     */
    formProgressPercent() {
      if (this.isReportGenerated) return 100;
      if (this.isWizardStep('page6_submit')) return 100;
      const total = this.wizardStepTotal;
      const current = this.wizardStepNumber;
      if (total <= 1) return 0;
      return Math.min(99, Math.max(0, Math.round(((current - 1) / total) * 100)));
    },
    selectedOfferTitle() {
      return offerLabel(this.typeOffre);
    },
    isDuoOffer() {
      return normalizePayableOffer(this.typeOffre) === 'duo';
    },
    isProOffer() {
      return normalizePayableOffer(this.typeOffre) === 'pro';
    },
    /**
     * API interne unifiée: le template continue d'utiliser isPersonalInfoLocked,
     * mais en Duo on renvoie/écrit le verrou du conjoint courant (ordre_duo).
     * Setter utilisé par lockPersonalInfo() et restauration brouillons.
     */
    isPersonalInfoLocked: {
      get() {
        const t = String(this.typeOffre || 'persona').toLowerCase();
        if (t === 'duo') {
          const key = this.ordreDuo === 2 ? 'duo_2' : 'duo_1';
          return !!(this.personalInfoLocked && this.personalInfoLocked[key]);
        }
        return !!(this.personalInfoLocked && this.personalInfoLocked.persona);
      },
      set(v) {
        const val = v === true || v === 'true';
        const t = String(this.typeOffre || 'persona').toLowerCase();
        if (!this.personalInfoLocked || typeof this.personalInfoLocked !== 'object' || Array.isArray(this.personalInfoLocked)) {
          this.personalInfoLocked = { persona: false, duo_1: false, duo_2: false };
        }
        if (t === 'duo') {
          const key = this.ordreDuo === 2 ? 'duo_2' : 'duo_1';
          this.personalInfoLocked[key] = val;
        } else {
          this.personalInfoLocked.persona = val;
        }
      },
    },
    isMobileDevice() {
      return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    },
    // Loi du tout ou rien : champs obligatoires (alignés avec le backend save-personal-info)
    isPersonalInfoComplete() {
      return (
        this.last_name &&
        this.first_name &&
        this.born &&
        this.depborn &&
        this.fonction &&
        this.salaire != null && this.salaire !== '' &&
        this.meetsRevenueEligibility &&
        this.nb_annees_restantes != null && this.nb_annees_restantes !== '' &&
        this.photo &&
        this.type_justificatif_identite &&
        this.justificatif_identite
      );
    },
    minRevenuMensuelNet() {
      return MIN_REVENU_MENSUEL_NET_FCFA;
    },
    minRevenuMensuelLabel() {
      return formatMinRevenuMensuelLabel();
    },
    meetsRevenueEligibility() {
      return meetsMinRevenuMensuel(this.salaire);
    },
    /** Salaire saisi mais sous le seuil commercial (500 000 FCFA). */
    salaireBelowEligibility() {
      if (this.isPersonalInfoLocked) return false;
      if (this.salaire == null || this.salaire === '') return false;
      return !this.meetsRevenueEligibility;
    },
    isFinancialDocumentsComplete() {
      return !!(
        this.justificatif_residence &&
        this.justificatif_revenu &&
        this.releve
      );
    },
    // Computed properties : quand la section est verrouillée, masquer les données sensibles par ***
    displayLastName() {
      return this.isPersonalInfoLocked ? '***' : (this.last_name || '');
    },
    displayFirstName() {
      return this.isPersonalInfoLocked ? '***' : (this.first_name || '');
    },
    displayBorn() {
      return this.isPersonalInfoLocked ? '***' : (this.born || '');
    },
    displayComborn() {
      return this.isPersonalInfoLocked ? '***' : (this.comborn || '');
    },
    displayDepborn() {
      return this.isPersonalInfoLocked ? '***' : (this.depborn || '');
    },
    displayFonction() {
      return this.isPersonalInfoLocked ? '***' : (this.fonction || '');
    },
    displaySalaire() {
      return this.isPersonalInfoLocked ? '***' : (this.salaire ?? '');
    },
    displayNbAnneesRestantes() {
      return this.isPersonalInfoLocked ? '***' : (this.nb_annees_restantes ?? '');
    },
    displayLogin() {
      return this.isPersonalInfoLocked ? '***' : (this.login || '');
    },
    /** Afficher le questionnaire (pages 2–6) dès que l'identité est validée — sans attendre le paiement. */
    showRestOfForm() {
      return this.isPersonalInfoLocked;
    },
    /** Détails des erreurs avec ancre DOM + sections mobiles à ouvrir (guidage utilisateur). */
    validationErrorItems() {
      const items = [];
      const add = (message, anchorId, mobileKeys = []) => {
        items.push({
          message,
          anchorId,
          mobileKeys: Array.isArray(mobileKeys) ? mobileKeys : [mobileKeys].filter(Boolean),
        });
      };
      const avoirs = ['avoirs'];
      const passifs = ['passifs'];

      if (!this.situation || !this.regim) {
        add('Veuillez compléter votre situation familiale.', 'situation', ['situation']);
      }

      if (this.hasProfessionalActivities && (!Array.isArray(this.activites_professionnelles) || this.activites_professionnelles.length === 0)) {
        add('Veuillez ajouter au moins une activité professionnelle.', 'section-activites', ['activites']);
      }

      if (this.has_compte_valeur && (!this.compte_valeur || this.compte_valeur <= 0)) {
        add('Le montant du compte valeur mobilière est requis', 'input-compte-valeur', avoirs);
      }
      if (this.has_compte_cheque && (!this.compte_cheque || this.compte_cheque <= 0)) {
        add('Le montant du compte chèque est requis', 'input-compte-cheque', avoirs);
      }
      if (this.has_compte_epargne && (!this.compte_epargne || this.compte_epargne <= 0)) {
        add('Le montant du compte épargne est requis', 'input-compte-epargne', avoirs);
      }
      if (this.has_compte_disponible && (!this.compte_disponible || this.compte_disponible <= 0)) {
        add('Le montant des autres comptes d\'épargne est requis', 'input-compte-disponible', avoirs);
      }
      if (this.has_compte_dat && (!this.compte_dat || this.compte_dat <= 0)) {
        add('Le montant du DAT est requis', 'input-compte-dat', avoirs);
      }
      if (this.has_compte_valeur && (this.montant_compte_valeur == null || this.montant_compte_valeur === '' || Number(this.montant_compte_valeur) < 0)) {
        add('Le montant du revenu généré par ce compte, ramené au mois du compte valeur est requis', 'input-montant-compte-valeur', avoirs);
      }
      if (this.has_compte_dat && (this.montant_compte_dat == null || this.montant_compte_dat === '' || Number(this.montant_compte_dat) < 0)) {
        add('Le montant du revenu généré par ce compte, ramené au mois du DAT est requis', 'input-montant-compte-dat', avoirs);
      }
      if (this.has_compte_disponible && (this.montant_compte_disponible == null || this.montant_compte_disponible === '' || Number(this.montant_compte_disponible) < 0)) {
        add('Le montant du revenu généré par ce compte, ramené au mois des autres comptes d\'épargne est requis', 'input-montant-compte-disponible', avoirs);
      }

      if (this.has_assurance_vie && (!this.assurance_vie || this.assurance_vie <= 0)) {
        add('Le montant de l\'assurance vie est requis', 'input-assurance-vie', avoirs);
      }
      if (this.has_assurance_produit && (!this.assurance_produit || this.assurance_produit <= 0)) {
        add('Le montant de l\'assurance produit est requis', 'input-assurance-produit', avoirs);
      }
      if (this.has_assurance_deces && (!this.assurance_deces || this.assurance_deces <= 0)) {
        add('Le montant de l\'assurance décès est requis', 'input-assurance-deces', avoirs);
      }

      if (!this.pret_en_cours) {
        add('Veuillez indiquer si vous avez des prêts en cours.', 'pret_en_cours', passifs);
      } else if (this.pret_en_cours === 'Oui') {
        if (this.montant_pret == null || this.montant_pret === '' || Number(this.montant_pret) < 0) {
          add('Le montant total des encours de prêts est requis.', 'montant_pret', passifs);
        }
        if (this.duree_pret == null || this.duree_pret === '' || Number(this.duree_pret) < 0) {
          add('La durée restante des prêts (en mois) est requise.', 'duree_pret', passifs);
        }
        if (this.montant_mensualite == null || this.montant_mensualite === '' || Number(this.montant_mensualite) < 0) {
          add('Le montant mensuel remboursé est requis.', 'montant_mensualite', passifs);
        }
      }

      if (!String(this.objectif || '').trim()) {
        add('Veuillez décrire vos objectifs patrimoniaux.', 'objectif', ['objectifs']);
      }

      return items;
    },
    validationErrors() {
      return this.validationErrorItems.map((e) => e.message);
    },

    isFormValid() {
      return this.validationErrors.length === 0;
    },
    hasProfessionalActivities() {
      return this.possede_entreprise === 'Oui' || this.possede_commerce === 'Oui';
    },
    totalProfessionalRevenue() {
      return (this.activites_professionnelles || []).reduce((sum, activite) => {
        return sum + (Number(activite?.revenu_mensuel) || 0);
      }, 0);
    },
    totalProfessionalValue() {
      return (this.activites_professionnelles || []).reduce((sum, activite) => {
        return sum + (Number(activite?.valorisation) || 0);
      }, 0);
    },
    // Revenu mensuel réel = salaire + revenus entreprise (dividende) + revenus locatifs (tous mensuels)
    revenuMensuelReelCalcule() {
      const s = Number(this.salaire) || 0;
      const e = Number(this.totalProfessionalRevenue) || 0;
      const l = Number(this.revenus_locatifs) || 0;
      const me = Number(this.montant_compte_epargne) || 0;
      const md = Number(this.montant_compte_disponible) || 0;
      const mdat = Number(this.montant_compte_dat) || 0;
      const mv = Number(this.montant_compte_valeur) || 0;
      const ma = Number(this.montant_assurance_check) || 0;
      return s + e + l + me + md + mdat + mv + ma;
    }
    ,
    draftWatchTrigger() {
      const keys = (typeof this.getDraftKeysForServer === 'function')
        ? this.getDraftKeysForServer()
        : [];
      const out = {};
      keys.forEach((k) => { out[k] = this[k]; });
      // Empreintes des pièces (évite de mettre les data URLs dans le watch ; détecte ajout / remplacement / vidage).
      const fp = (v) => {
        if (v == null || v === '') return 0;
        if (typeof v !== 'string') return String(v).length;
        const n = v.length;
        if (n === 0) return 0;
        return `${n}:${v.charCodeAt(0)}:${v.charCodeAt(n - 1)}:${v.charCodeAt((n >> 1) || 0)}`;
      };
      const blobKeys = typeof this.getDraftBlobLikeFieldKeys === 'function' ? this.getDraftBlobLikeFieldKeys() : [];
      blobKeys.forEach((k) => {
        out[`__df_${k}`] = fp(this[k]);
      });
      return out;
    },
  },

  methods: {
    getDetailedErrorMessage(error) {
      const validationMessages = formatValidationErrors(error?.response?.data);
      return getErrorMessage(error, { validationMessages });
    },
    isWizardStep(stepId) {
      return this.wizardStepId === stepId;
    },
    ensureWizardStepValid() {
      const steps = this.availableWizardSteps;
      if (!steps.length) return;
      if (!steps.some((step) => step.id === this.wizardStepId)) {
        this.wizardStepId = steps[0].id;
      }
      if (!this.isPersonalInfoLocked && this.wizardStepId !== 'page1_profile') {
        this.wizardStepId = 'page1_profile';
      }
    },
    goToWizardStep(stepId) {
      const steps = this.availableWizardSteps;
      if (!steps.some((step) => step.id === stepId)) return;
      this.errorMessage = '';
      this.wizardStepId = stepId;
      if (stepId === 'page6_submit') this.hasVisitedSubmitStep = true;
      try { this.persistDraft(); } catch (_) { }
      this.scrollWizardIntoView();
    },
    scrollWizardIntoView() {
      this.$nextTick(() => {
        // .form-progress est en position:sticky (top:0) : une fois l'utilisateur scrollé
        // au-delà (cas courant, la barre Suivant est en bas de chaque page), l'élément
        // reste visuellement épinglé en haut et scrollIntoView() le considère déjà "en
        // vue" — il ne fait rien. Le scroll de la page ne bouge donc pas, et la nouvelle
        // page (affichée via v-show, pas une vraie navigation) hérite de l'ancienne
        // position, parfois bien au-delà de sa propre hauteur ("elle commence en fin").
        // window.scrollTo(0) donne le même résultat visuel (le sticky se pose en haut)
        // sans dépendre du positionnement de l'élément ciblé.
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    },
    /** Affiche le message ET scroll/surligne le champ concerné (au lieu de laisser le
     * message seul en haut de page, invisible si le champ fautif est plus bas). */
    failWizardStepValidation(message, anchorId, mobileKeys = []) {
      this.errorMessage = message;
      this.scrollToErrorItems([{ anchorId, mobileKeys }]);
      return false;
    },
    validateWizardStepBeforeLeave() {
      if (this.isWizardStep('page2b_situation')) {
        if (!this.situation || !this.regim) {
          return this.failWizardStepValidation('Veuillez compléter votre situation familiale.', 'situation', ['situation']);
        }
      }
      if (this.isWizardStep('page3b_passifs_revenus')) {
        if (!this.pret_en_cours) {
          return this.failWizardStepValidation('Veuillez indiquer si vous avez des prêts en cours.', 'pret_en_cours', ['passifs']);
        }
        if (this.pret_en_cours === 'Oui') {
          if (this.montant_pret == null || this.montant_pret === '' || Number(this.montant_pret) < 0) {
            return this.failWizardStepValidation('Veuillez renseigner le montant total des encours de prêts.', 'montant_pret', ['passifs']);
          }
          if (this.duree_pret == null || this.duree_pret === '' || Number(this.duree_pret) < 0) {
            return this.failWizardStepValidation('Veuillez renseigner la durée restante des prêts (en mois).', 'duree_pret', ['passifs']);
          }
          if (this.montant_mensualite == null || this.montant_mensualite === '' || Number(this.montant_mensualite) < 0) {
            return this.failWizardStepValidation('Veuillez renseigner le montant mensuel remboursé.', 'montant_mensualite', ['passifs']);
          }
        }
      }
      if (this.isWizardStep('page4_objectifs')) {
        if (!String(this.objectif || '').trim()) {
          return this.failWizardStepValidation('Veuillez décrire vos objectifs patrimoniaux.', 'objectif', ['objectifs']);
        }
      }
      this.errorMessage = '';
      return true;
    },
    async wizardNext() {
      if (this.isReportGenerated) {
        const steps = this.availableWizardSteps;
        const idx = steps.findIndex((step) => step.id === this.wizardStepId);
        if (idx < 0 || idx >= steps.length - 1) return;
        this.goToWizardStep(steps[idx + 1].id);
        return;
      }
      if (this.isWizardStep('page1_profile') && !this.isPersonalInfoLocked) {
        if (!this.isPersonalInfoComplete) {
          this.errorMessage = this.salaireBelowEligibility
            ? `Vous ne remplissez pas les conditions. Le revenu mensuel net minimum requis est de ${this.minRevenuMensuelLabel}.`
            : 'Veuillez compléter toutes les informations personnelles avant de continuer.';
          return;
        }
        this.lockPersonalInfo();
        return;
      }
      if (!this.isPersonalInfoLocked && this.wizardStepId !== 'page1_profile') {
        this.goToWizardStep('page1_profile');
        this.errorMessage = 'Veuillez d\'abord valider vos informations personnelles.';
        return;
      }
      if (this.isWizardStep('page5_documents_payment')) {
        if (!this.isFinancialDocumentsComplete) {
          const result = await Swal.fire({
            title: 'Justificatifs manquants',
            text: "Si vous n'envoyez pas ces documents maintenant, vous ne recevrez pas le code de vos résultats. Vous pourrez les ajouter plus tard depuis votre dossier.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: "Confirmer l'envoi",
            cancelButtonText: 'Revenir en arrière',
            confirmButtonColor: '#28a745',
          });
          if (!result.isConfirmed) return;
        }
        this.saveFinancialDocuments();
        return;
      }
      if (!this.validateWizardStepBeforeLeave()) return;
      const steps = this.availableWizardSteps;
      const idx = steps.findIndex((step) => step.id === this.wizardStepId);
      if (idx < 0 || idx >= steps.length - 1) return;
      this.goToWizardStep(steps[idx + 1].id);
    },
    wizardPrev() {
      const steps = this.availableWizardSteps;
      const idx = steps.findIndex((step) => step.id === this.wizardStepId);
      if (idx <= 0) return;
      this.goToWizardStep(steps[idx - 1].id);
    },
    advanceWizardAfterPersonalInfoLock() {
      this.ensureWizardStepValid();
      this.wizardStepId = 'page2_coordonnees';
      try { this.persistDraft(); } catch (_) { }
      this.scrollWizardIntoView();
    },
    migrateWizardStepId() {
      const wasPage2Situation = this.wizardStepId === 'page2_situation';
      const mapped = LEGACY_WIZARD_STEP_MAP[this.wizardStepId];
      if (mapped) this.wizardStepId = mapped;
      // 'page2_situation' couvrait Coordonnées + Situation + Patrimoine + Activités
      // avant leur scission en 4 étapes : même logique que page3_finances ci-dessous,
      // on retrouve la sous-section la plus avancée via mobileSections plutôt que de
      // ramener silencieusement le client à Coordonnées (mapping par défaut ci-dessus).
      if (wasPage2Situation) {
        const sections = this.mobileSections || {};
        if (sections.activites) {
          this.wizardStepId = 'page2d_activites';
        } else if (sections.immobilier) {
          this.wizardStepId = 'page2c_patrimoine';
        } else if (sections.situation) {
          this.wizardStepId = 'page2b_situation';
        }
      }
      // 'page3_finances' couvrait Avoirs + Passifs + Revenus + Charges avant leur
      // scission en 3 étapes (page3_finances / page3b_passifs_revenus /
      // page3c_charges) : pour un brouillon déjà entamé sous l'ancien découpage,
      // on retrouve la sous-section la plus avancée via mobileSections (déjà
      // restauré à ce stade, cf. normalizeStateAfterDraftMerge appelé juste avant)
      // — sinon la scission le ramènerait silencieusement à Avoirs même s'il avait
      // déjà rempli Charges.
      if (this.wizardStepId === 'page3_finances') {
        const sections = this.mobileSections || {};
        if (sections.charges) {
          this.wizardStepId = 'page3c_charges_1';
        } else if (sections.passifs || sections.revenus) {
          this.wizardStepId = 'page3b_passifs_revenus';
        }
      }
    },
    async applyRouteWizardStep() {
      const queryStep = this.$route?.query?.step;
      const storedStep = sessionStorage.getItem('payment_return_step');
      const paymentReturnStep = storedStep;
      const target = queryStep || storedStep;
      if (!target) return;
      if (storedStep) sessionStorage.removeItem('payment_return_step');
      let migrated = LEGACY_WIZARD_STEP_MAP[target] || target;
      const isPostPaymentReturn =
        POST_PAYMENT_WIZARD_STEPS.has(String(queryStep || '')) ||
        POST_PAYMENT_WIZARD_STEPS.has(String(paymentReturnStep || ''));
      if (isPostPaymentReturn && (migrated === 'page5_documents_payment' || migrated === 'page5_contract')) {
        migrated = 'page3_finances';
      }
      if (!WIZARD_STEPS.some((step) => step.id === migrated)) return;
      await this.loadClientProfileForOffre();
      const isPostPaymentFlow =
        isPostPaymentReturn &&
        (migrated === 'page3_finances' || migrated === 'page5_documents_payment');
      if (!this.isPersonalInfoLocked && !isPostPaymentFlow) return;
      this.wizardStepId = migrated;
      if (isPostPaymentFlow) {
        try { sessionStorage.removeItem('payment_just_confirmed'); } catch (_) { }
      }
      if (this.wizardStepId === 'page3_finances' && !this.clientApte && isPostPaymentFlow) {
        this.startClientAptePolling();
      }
      if (this.$route?.query?.step) {
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => { });
      }
    },
    createProfessionalActivityId() {
      return `activite_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    },
    createEmptyProfessionalActivity(type = 'entreprise_formelle') {
      return {
        id: this.createProfessionalActivityId(),
        type_activite: type,
        description: '',
        revenu_mensuel: null,
        valorisation: null,
      };
    },
    normalizeProfessionalActivity(activity = {}, fallbackType = 'entreprise_formelle') {
      return {
        id: activity.id || this.createProfessionalActivityId(),
        type_activite: activity.type_activite === 'commerce' ? 'commerce' : fallbackType,
        description: activity.description || '',
        revenu_mensuel: activity.revenu_mensuel ?? null,
        valorisation: activity.valorisation ?? null,
      };
    },
    enabledProfessionalActivityTypes() {
      const types = [];
      if (this.possede_entreprise === 'Oui') types.push('entreprise_formelle');
      if (this.possede_commerce === 'Oui') types.push('commerce');
      return types;
    },
    syncProfessionalActivityTotals() {
      this.revenus_entreprise = this.totalProfessionalRevenue;
      this.valeur_entreprise = this.totalProfessionalValue;
      this.persistDraft();
    },
    syncProfessionalActivitiesFromFlags() {
      const enabledTypes = this.enabledProfessionalActivityTypes();
      if (enabledTypes.length === 0) {
        this.activites_professionnelles = [];
        this.revenus_entreprise = 0;
        this.valeur_entreprise = 0;
        this.persistDraft();
        return;
      }

      this.activites_professionnelles = (this.activites_professionnelles || [])
        .filter((activite) => enabledTypes.includes(activite?.type_activite))
        .map((activite) => this.normalizeProfessionalActivity(activite, enabledTypes[0]));

      if (this.activites_professionnelles.length === 0) {
        this.activites_professionnelles = [this.createEmptyProfessionalActivity(enabledTypes[0])];
      }

      this.syncProfessionalActivityTotals();
    },
    addProfessionalActivity(type = null) {
      const enabledTypes = this.enabledProfessionalActivityTypes();
      if (enabledTypes.length === 0) return;
      const selectedType = type && enabledTypes.includes(type) ? type : enabledTypes[0];
      this.activites_professionnelles.push(this.createEmptyProfessionalActivity(selectedType));
      this.persistDraft();
    },
    removeProfessionalActivity(index) {
      this.activites_professionnelles.splice(index, 1);
      this.syncProfessionalActivityTotals();
    },
    restoreProfessionalActivities(rawActivities = null, legacySituation = null) {
      const activities = Array.isArray(rawActivities) ? rawActivities : [];
      if (activities.length > 0) {
        this.activites_professionnelles = activities.map((activite) =>
          this.normalizeProfessionalActivity(activite, activite?.type_activite === 'commerce' ? 'commerce' : 'entreprise_formelle')
        );
      } else {
        const hasLegacyEnterprise =
          legacySituation?.possede_entreprise ||
          Number(legacySituation?.revenus_entreprise || 0) > 0 ||
          Number(legacySituation?.valeur_entreprise || 0) > 0;
        this.activites_professionnelles = hasLegacyEnterprise
          ? [this.normalizeProfessionalActivity({
            type_activite: 'entreprise_formelle',
            description: 'Activité existante',
            revenu_mensuel: legacySituation?.revenus_entreprise || 0,
            valorisation: legacySituation?.valeur_entreprise || 0,
          })]
          : [];
      }
      this.syncProfessionalActivityTotals();
    },
    /**
     * Formulaire (hors section Informations personnelles) : ouvre les sections mobiles
     * nécessaires, fait défiler jusqu’au premier champ en erreur, surligne tous les
     * champs concernés et place le focus sur le premier.
     */
    /**
     * Guidage visuel vers une liste d'erreurs (items = { message, anchorId, mobileKeys }).
     * Utilisé à la fois pour les validations front (validationErrorItems) et les erreurs 400 backend.
     */
    scrollToErrorItems(items) {
      if (!items.length) return;
      const keysOpened = new Set();
      items.forEach((item) => {
        (item.mobileKeys || []).forEach((k) => {
          if (this.mobileSections[k] !== undefined) keysOpened.add(k);
        });
      });
      keysOpened.forEach((k) => {
        this.mobileSections[k] = true;
      });
      this.$nextTick(() => {
        const elements = [];
        items.forEach((item) => {
          const id = item.anchorId;
          if (!id) return;
          let el = document.getElementById(id);
          if (!el && String(id).startsWith('input-')) {
            el = document.querySelector(`[id="${id}"]`);
          }
          if (el && !elements.includes(el)) elements.push(el);
        });
        const first = elements[0];
        if (!first) return;
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const highlightMs = 4500;
        elements.forEach((el) => {
          el.classList.add('field-error-highlight');
          setTimeout(() => {
            el.classList.remove('field-error-highlight');
          }, highlightMs);
        });
        const tag = (first.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'select' || tag === 'textarea') {
          try {
            first.focus({ preventScroll: true });
          } catch (_) {
            try {
              first.focus();
            } catch (_) { }
          }
        } else {
          const focusable = first.querySelector('input:not([type="hidden"]), select, textarea');
          if (focusable) {
            try {
              focusable.focus({ preventScroll: true });
            } catch (_) {
              focusable.focus();
            }
          }
        }
      });
    },
    /**
     * Formulaire (hors section Informations personnelles) : ouvre les sections mobiles
     * nécessaires, fait défiler jusqu’au premier champ en erreur, surligne tous les
     * champs concernés et place le focus sur le premier.
     */
    scrollToValidationErrors() {
      const first = this.validationErrorItems[0];
      if (first) {
        const stepForError = this.wizardStepForValidationItem(first);
        if (stepForError && stepForError !== this.wizardStepId) {
          this.goToWizardStep(stepForError);
        }
      }
      this.$nextTick(() => {
        this.scrollToErrorItems(this.validationErrorItems);
      });
    },
    wizardStepForValidationItem(item) {
      const id = String(item?.anchorId || '');
      const keys = item?.mobileKeys || [];
      if (id === 'page3-payment-title' || id === 'pret_en_cours' || id === 'montant_pret' || id === 'duree_pret' || id === 'montant_mensualite') {
        return 'page3b_passifs_revenus';
      }
      if (keys.includes('activites') || id === 'section-activites') {
        return 'page2d_activites';
      }
      if (keys.includes('immobilier')) {
        return 'page2c_patrimoine';
      }
      if (keys.includes('situation') || id === 'situation') {
        return 'page2b_situation';
      }
      if (keys.includes('coordonnees') || id === 'telMobile') {
        return 'page2_coordonnees';
      }
      if (keys.includes('avoirs') || id.startsWith('input-compte') || id.startsWith('input-assurance') || id.startsWith('input-montant')) {
        return 'page3_finances';
      }
      if (keys.includes('charges')) {
        return 'page3c_charges_1';
      }
      if (keys.includes('passifs') || keys.includes('revenus')) {
        return 'page3b_passifs_revenus';
      }
      if (keys.includes('objectifs') || id === 'objectif') {
        return 'page4_objectifs';
      }
      if (id === 'section-documents-payment' || keys.includes('documents')) {
        return 'page5_documents_payment';
      }
      return null;
    },
    // S'appuie sur validationErrorItems (mêmes règles que la validation de soumission)
    // pour afficher un badge "Complété" / "À compléter" sur les en-têtes de sous-section.
    isSectionComplete(key) {
      const hasErrors = this.validationErrorItems.some((item) => (item.mobileKeys || []).includes(key));
      if (hasErrors) return false;
      // Avoirs : tous les comptes/assurances sont facultatifs, donc aucune erreur de
      // validation ne se déclenche tant qu'aucune case n'est cochée — sans ce garde-fou,
      // le badge affichait "Complété" dès l'arrivée sur la page, avant toute saisie.
      if (key === 'avoirs') {
        return Boolean(
          this.has_compte_valeur || this.has_compte_cheque || this.has_compte_epargne
          || this.has_compte_disponible || this.has_compte_dat
          || this.has_assurance_vie || this.has_assurance_produit || this.has_assurance_deces
        );
      }
      // Activités : les deux menus "Possédez-vous une entreprise / un commerce ?"
      // n'ont pas de règle de validation dédiée (ils ne bloquent l'envoi que
      // lorsqu'on répond "Oui" sans ajouter d'activité) — sans ce garde-fou, le
      // badge affichait "Complété" avant même d'avoir répondu aux deux questions.
      if (key === 'activites') {
        return Boolean(this.possede_entreprise) && Boolean(this.possede_commerce);
      }
      return true;
    },
    getDraftKey() {
      const uid = this.user_id || 'unknown';
      const offre = this.typeOffre === 'duo' ? 'duo' : 'persona';
      const ordre = this.typeOffre === 'duo' ? `_${this.ordreDuo || 1}` : '';
      return `form_apat_draft_${uid}_${offre}${ordre}`;
    },
    /** Champs potentiellement très volumineux (data URL, texte long) : quota + empreintes watcher. */
    getDraftBlobLikeFieldKeys() {
      return ['photo', 'justificatif_identite', 'justificatif_residence', 'justificatif_revenu', 'releve', 'documents_propriete'];
    },
    getDraftDocumentSessionBackupKey() {
      return `${this.getDraftKey()}__docBackup`;
    },
    assignProfileStrIfPresent(key, val) {
      if (val == null || String(val).trim() === '') return;
      this[key] = val;
    },
    assignClientBirthFromApi(data) {
      if (!data || typeof data !== 'object') return;
      this.assignProfileStrIfPresent('born', normalizeDateToIso(data.date_born));
      this.assignProfileStrIfPresent('comborn', data.commune_born);
      this.assignProfileStrIfPresent('depborn', data.departement_born);
    },
    assignPrefillNumberIfPresent(targetKey, source, sourceKey = targetKey) {
      if (!source || !Object.prototype.hasOwnProperty.call(source, sourceKey)) return;
      const raw = source[sourceKey];
      if (raw == null || raw === '') return;
      const n = Number(raw);
      if (!Number.isFinite(n)) return;
      if (isFormApatFieldEmpty(this[targetKey]) || n !== 0) {
        this[targetKey] = n;
      }
    },
    assignPrefillNumbersFrom(source, keys) {
      if (!source || typeof source !== 'object') return;
      keys.forEach((k) => this.assignPrefillNumberIfPresent(k, source, k));
    },
    async applyDiagnosticPrefillIfNeeded(consume = false) {
      // Duo conjoint 2 : le préremplissage diagnostic est lié au compte (email), pas au conjoint.
      // Sans ce garde-fou, le conjoint 2 hériterait des objectifs du conjoint 1 (ou de qui a rempli
      // le diagnostic public) dès que son propre champ objectif est encore vide.
      if (this.typeOffre === 'duo' && this.ordreDuo === 2) return;
      const prefill = resolveFormApatPrefillForUser(this.email);
      if (prefill) {
        if (applyFormApatPrefill(this, prefill)) {
          try { this.persistDraft(); } catch (_) { }
        }
        if (consume) consumeFormApatPrefill();
        return;
      }
      // Diagnostic rempli sur un autre appareil : localStorage n'a rien, on va
      // chercher côté serveur (préremplissage sauvegardé par email au moment du diagnostic).
      // Deux garde-fous pour ne pas spammer l'API à chaque montage / rappel :
      // - en mémoire, le composant appelle cette méthode 2x au chargement ;
      // - persistant (localStorage), pour ne pas réinterroger à chaque nouvelle visite
      //   de cet appareil une fois que le compte a déjà été vérifié une fois.
      if (!isAuthenticatedSession() || this._serverDiagnosticPrefillFetching) return;
      if (wasServerDiagnosticPrefillChecked(this.email)) return;
      this._serverDiagnosticPrefillFetching = true;
      try {
        const { data } = await axios.get('/api/diagnostic-prefill/', this.getAuthConfig());
        markServerDiagnosticPrefillChecked(this.email);
        if (data?.data && typeof data.data === 'object') {
          if (applyFormApatPrefill(this, data.data)) {
            try { this.persistDraft(); } catch (_) { }
          }
        }
      } catch (_) {
        /* pas de préremplissage disponible, formulaire vierge */
      } finally {
        this._serverDiagnosticPrefillFetching = false;
      }
    },
    readDraftDocumentSessionBackupForOrdre(uid, ordre) {
      const base = `form_apat_draft_${uid}_duo_${ordre}`;
      const raw = sessionStorage.getItem(`${base}__docBackup`);
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (_) {
        return null;
      }
    },
    mergeDraftDocumentsFromSessionBackup() {
      try {
        const raw = sessionStorage.getItem(this.getDraftDocumentSessionBackupKey());
        if (!raw) return;
        const docs = JSON.parse(raw);
        this.getDraftBlobLikeFieldKeys().forEach((k) => {
          const b = docs[k];
          if (typeof b !== 'string' || b.trim() === '') return;
          const cur = this[k];
          const curEmpty = cur == null || (typeof cur === 'string' && cur.trim() === '');
          if (curEmpty) this[k] = b;
        });
        this.sanitizeOrphanDocumentFilenamesInPlace(this);
        this.syncPersonalPhotoUiAfterDraftChange();
        this.$nextTick(() => {
          try { this.resetJustificatifFileInputElements(); } catch (_) { }
        });
      } catch (_) { }
    },
    /** Aligner aperçu / drapeaux webcam sur `this.photo` (évite double bloc UI au switch Duo). */
    syncPersonalPhotoUiAfterDraftChange() {
      try {
        this.stopWebcam();
      } catch (_) { }
      if (this.photo && String(this.photo).trim() !== '') {
        this.photoPreview = this.photo;
        this.photoCaptured = true;
      } else {
        this.photoPreview = '';
        this.photoCaptured = false;
      }
    },
    /**
     * Si le contenu du fichier (data URL / base64) est absent, ne pas garder seulement le nom :
     * brouillon « léger » + localStorage, ou stash Duo, peuvent laisser un nom sans pièce — le bouton
     * « Valider » reste alors désactivé alors que l’écran semble rempli. On réaligne aussi les
     * inputs file natifs (le navigateur peut encore afficher un ancien nom alors que le state Vue est vide).
     */
    sanitizeOrphanDocumentFilenamesInPlace(target) {
      if (!target || typeof target !== 'object') return;
      const pairs = [
        ['photo', 'photo_filename'],
        ['justificatif_identite', 'justificatif_identite_filename'],
        ['justificatif_residence', 'justificatif_residence_filename'],
        ['justificatif_revenu', 'justificatif_revenu_filename'],
        ['releve', 'releve_filename'],
      ];
      pairs.forEach(([blobKey, nameKey]) => {
        if (this.isEmptyDuoPersonalVal(target[blobKey])) {
          target[nameKey] = '';
        }
      });
    },
    resetJustificatifFileInputElements() {
      if (typeof document === 'undefined') return;
      [
        ['justificatif_identite', 'justificatif_identite'],
        ['justificatif_residence', 'justificatif_residence'],
        ['justificatif_revenu', 'justificatif_revenu'],
      ].forEach(([id, field]) => {
        if (!this.isEmptyDuoPersonalVal(this[field])) return;
        const el = document.getElementById(id);
        if (el && el.type === 'file') el.value = '';
      });
      const refEl = this.$refs.photoFallbackFileInput;
      if (refEl && this.isEmptyDuoPersonalVal(this.photo)) {
        try {
          refEl.value = '';
        } catch (_) { }
      }
    },
    /** Champs alignés sur `isPersonalInfoComplete` (switch Duo + secours mémoire session). */
    getDuoPersonalSwitchKeys() {
      return [
        'last_name', 'first_name', 'born', 'comborn', 'depborn', 'fonction',
        'salaire', 'nb_annees_restantes',
        'photo', 'photo_filename',
        'type_justificatif_identite',
        'justificatif_identite', 'justificatif_identite_filename',
        'justificatif_residence', 'justificatif_residence_filename',
        'justificatif_revenu', 'justificatif_revenu_filename',
        'releve', 'releve_filename',
      ];
    },
    isEmptyDuoPersonalVal(v) {
      if (v == null) return true;
      if (typeof v === 'number') return false;
      if (typeof v === 'string') return v.trim() === '';
      return false;
    },
    stashDuoPersonalForOrdre(ordre) {
      if (String(this.typeOffre || '').toLowerCase() !== 'duo') return;
      const n = ordre === 2 ? 2 : 1;
      try { this.sanitizeOrphanDocumentFilenamesInPlace(this); } catch (_) { }
      if (!this.duoPersonalStashByOrdre || typeof this.duoPersonalStashByOrdre !== 'object') {
        this.duoPersonalStashByOrdre = { 1: null, 2: null };
      }
      const keys = this.getDuoPersonalSwitchKeys();
      const snap = {};
      keys.forEach((k) => { snap[k] = this[k]; });
      this.duoPersonalStashByOrdre[n] = snap;
    },
    applyDuoPersonalStashFromMemory(ordre) {
      if (String(this.typeOffre || '').toLowerCase() !== 'duo') return;
      const n = ordre === 2 ? 2 : 1;
      const stash = this.duoPersonalStashByOrdre?.[n];
      if (!stash || typeof stash !== 'object') return;
      const keys = this.getDuoPersonalSwitchKeys();
      const isEmpty = (v) => this.isEmptyDuoPersonalVal(v);
      keys.forEach((k) => {
        if (!isEmpty(stash[k]) && isEmpty(this[k])) {
          this[k] = stash[k];
        }
      });
      this.sanitizeOrphanDocumentFilenamesInPlace(this);
      this.syncPersonalPhotoUiAfterDraftChange();
      this.$nextTick(() => {
        try { this.resetJustificatifFileInputElements(); } catch (_) { }
      });
    },
    /** Au chargement Duo : lit les deux clés localStorage pour alimenter le filet mémoire. */
    hydrateDuoPersonalStashFromLocalStorage() {
      if (String(this.typeOffre || '').toLowerCase() !== 'duo') return;
      const uid = this.user_id || 'unknown';
      const keys = this.getDuoPersonalSwitchKeys();
      if (!this.duoPersonalStashByOrdre || typeof this.duoPersonalStashByOrdre !== 'object') {
        this.duoPersonalStashByOrdre = { 1: null, 2: null };
      }
      [1, 2].forEach((ordre) => {
        const raw = localStorage.getItem(`form_apat_draft_${uid}_duo_${ordre}`);
        if (!raw) return;
        try {
          const parsed = JSON.parse(raw);
          const docExtra = this.readDraftDocumentSessionBackupForOrdre(uid, ordre);
          const snap = {};
          keys.forEach((k) => {
            let v = parsed[k];
            if ((v == null || (typeof v === 'string' && v.trim() === '')) && docExtra && typeof docExtra[k] === 'string' && docExtra[k].trim() !== '') {
              v = docExtra[k];
            }
            snap[k] = v;
          });
          this.sanitizeOrphanDocumentFilenamesInPlace(snap);
          this.duoPersonalStashByOrdre[ordre] = snap;
        } catch (_) { }
      });
    },
    async lockPersonalInfo() {
      if (!this.isPersonalInfoComplete) {
        await Swal.fire({
          title: this.salaireBelowEligibility ? 'Conditions non remplies' : 'Informations incomplètes',
          text: this.salaireBelowEligibility
            ? `Vous ne remplissez pas les conditions. Le revenu mensuel net minimum requis est de ${this.minRevenuMensuelLabel}.`
            : 'Veuillez remplir tous les champs des informations personnelles avant de valider.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      const guarded = await personalInfoSubmitGuard.run(async () => {
        await this.executeLockPersonalInfo();
      });
      if (!guarded.ok && (guarded.reason === 'in_flight' || guarded.reason === 'cooldown')) {
        this.errorMessage = 'Validation en cours, veuillez patienter avant de réessayer.';
      }
    },
    // Verrouiller la section informations personnelles et mettre à jour le login sur le serveur
    async compressImageDataUrl(dataUrl, maxWidth = 1280, quality = 0.82) {
      if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
        return dataUrl;
      }
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          try {
            let width = img.width;
            let height = img.height;
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              resolve(dataUrl);
              return;
            }
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
          } catch {
            resolve(dataUrl);
          }
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
      });
    },
    async executeLockPersonalInfo() {
      Swal.close();
      this.personalInfoSaving = true;
      try {
        const config = { ...this.getAuthConfig(), timeout: 90000 }; // 90s pour upload photo/justificatif et backend potentiellement lent
        const generatedLogin = this.login;

        // La photo a déjà été validée à la capture ; la revalider ici ne faisait que
        // rejouer une inférence de plusieurs secondes avant l'envoi. Le serveur reste
        // l'autorité (netteté, cadrage, yeux visibles).
        let photoPayload = this.photo;
        if (photoPayload) {
          photoPayload = await this.compressImageDataUrl(photoPayload, 1600, 0.9);
        }

        const isDuo = String(this.typeOffre || 'persona').toLowerCase() === 'duo';
        await axios.post("/api/save-personal-info/?save_phase=identity", {
          save_phase: 'identity',
          ...(isDuo
            ? {
              ordre_duo: this.ordreDuo,
              first_name: this.first_name,
              last_name: this.last_name,
              username: generatedLogin,
              birthdate: this.born,
              birthplace: this.comborn,
              birthplace_department: this.depborn,
              fonction: this.fonction,
            }
            : {}),
          photo: photoPayload,
          type_justificatif_identite: this.type_justificatif_identite,
          justificatif_identite: this.justificatif_identite,
          salaire: this.salaire,
          nb_annees_restantes: this.nb_annees_restantes
        }, config);

        // 2-3. En offre Duo, on évite d'écraser le profil partagé (User/Profile + Client),
        // car il n'existe pas de modèle distinct par conjoint.
        // On met à jour ces champs uniquement en Persona (ou si l'offre n'est pas Duo).
        if (String(this.typeOffre || 'persona').toLowerCase() !== 'duo') {
          // 2. Seulement si les documents sont valides : mettre à jour le profil utilisateur (nom, prénom, login, etc.)
          await axios.put("/api/profile/", {
            username: generatedLogin,
            first_name: this.first_name,
            last_name: this.last_name,
            birthdate: this.born,
            birthplace: this.comborn,
            birthplace_department: this.depborn,
            fonction: this.fonction
          }, config);

          // 3. Mettre à jour le profil client avec date/commune/département de naissance
          await axios.put("/api/all-clients/", {
            date_born: this.born,
            commune_born: this.comborn,
            departement_born: this.depborn
          }, config);
        }

        this.login = generatedLogin;
        this.isPersonalInfoLocked = true;
        trackMonAnalysePersonalInfoSubmit({
          offer: this.typeOffre,
          ordreDuo: this.typeOffre === 'duo' ? this.ordreDuo : undefined,
        });

        // Sauvegarder le draft pour indiquer que la première partie est complète
        this.persistDraft();

        this.personalInfoSaving = false; // Masquer l'overlay avant d'afficher la modale (évite race condition)
        await Swal.fire({
          title: 'Informations validées',
          html: `✅ Vos informations personnelles ont été sauvegardées de manière sécurisée.<br><br>Votre identifiant de connexion est: <strong>${escapeHtml(generatedLogin)}</strong><br><br>Vous pouvez maintenant compléter votre questionnaire patrimonial.`,
          icon: 'success',
          confirmButtonText: 'Continuer'
        });
        this.advanceWizardAfterPersonalInfoLock();
      } catch (error) {
        console.error('Erreur lors de la validation:', error?.response?.data || error);
        const err = this.getDetailedErrorMessage(error);
        const detail = String(error?.response?.data?.detail || '').toLowerCase();
        const errText = String(error?.response?.data?.error || '').toLowerCase();
        if (detail.includes('justificatif_residence') || detail.includes('justificatif_revenu') || detail.includes('releve')) {
          err.title = 'Documents financiers non requis ici';
          err.message = 'Le justificatif de domicile et les pièces de revenus sont demandés à l\'étape 4 (après paiement), pas lors de la validation de l\'identité. Réessayez dans 1 à 2 minutes si le serveur vient d\'être mis à jour.';
        } else if (detail.includes('photo') || errText.includes('visage') || errText.includes('photo')) {
          // La photo est conservée : l'effacer renvoyait le client à l'écran de départ
          // sans qu'il puisse voir ce qui clochait. Il la remplace via « Reprendre la photo ».
          // Message fixe plutôt que err.message : ce dernier peut encore contenir
          // du texte serveur brut pour certains types d'erreur non catégorisés.
          this.selfieNotice = {
            type: 'error',
            text: 'Cette photo a été refusée. Reprenez-en une, nette et de face.',
          };
        }
        await Swal.fire({
          title: err.title,
          text: err.message,
          icon: err.icon || 'error',
          confirmButtonText: err.confirmLabel || 'OK'
        });
        this.persistDraft();
      } finally {
        this.personalInfoSaving = false;
      }
    },
    readFileAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    loadImageFromDataUrl(dataUrl) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = dataUrl;
      });
    },
    // ===== FACE DETECTION =====
    async initFaceDetection() {
      if (this.faceDetectionReady) return;

      // Pas de sortie anticipée quand un chargement est déjà en cours : `ensureFaceApiReady`
      // renvoie la promesse partagée. Sinon la capture repartait avec faceDetectionReady
      // à false et déclenchait à tort « validation non disponible ».
      this.faceDetectionLoading = true;
      try {
        await ensureFaceApiReady();
        this.faceDetectionReady = true;
      } catch (err) {
        console.warn('Impossible d\'initialiser face-api.js:', err?.message || err);
      } finally {
        this.faceDetectionLoading = false;
      }
    },
    async countFacesInPhoto(dataUrl) {
      const img = await this.loadImageFromDataUrl(dataUrl);

      if (this.faceDetectionReady && typeof faceapi !== 'undefined') {
        // scoreThreshold abaissé (défaut 0.5) : un selfie de téléphone (éclairage
        // intérieur, coiffe/foulard) fait souvent chuter la confiance sous 0.5 et
        // provoque un faux « aucun visage détecté ». Le serveur revalide de toute
        // façon la photo à la soumission, donc être plus permissif ici est sans risque.
        const detections = await faceapi.detectAllFaces(
          img,
          new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.3 })
        );
        return { count: detections.length, boxes: detections.map((d) => d.box), img };
      }

      if ('FaceDetector' in window) {
        const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 10 });
        const faces = await detector.detect(img);
        return { count: faces.length, boxes: faces.map((f) => f.boundingBox), img };
      }

      throw new Error('Détection faciale frontend non disponible');
    },
    /**
     * Netteté approximative (variance d'un filtre Laplacien 4-voisins, en niveaux de
     * gris, sur la zone du visage si connue) : même principe que le contrôle serveur
     * (backend/gestion/gestion/validation_utils.py, validate_single_face avec OpenCV),
     * pour détecter côté client une photo trop floue avant l'envoi plutôt que de
     * laisser le serveur la rejeter après coup sans avertissement préalable.
     */
    isPhotoTooBlurry(img, box) {
      try {
        const region = box && box.width > 0 && box.height > 0
          ? { x: Math.max(0, box.x), y: Math.max(0, box.y), width: box.width, height: box.height }
          : { x: 0, y: 0, width: img.naturalWidth || img.width, height: img.naturalHeight || img.height };
        const w = Math.max(2, Math.round(region.width));
        const h = Math.max(2, Math.round(region.height));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, region.x, region.y, w, h, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);

        const gray = new Float32Array(w * h);
        for (let i = 0, p = 0; i < data.length; i += 4, p++) {
          gray[p] = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        }

        let sum = 0;
        let sumSq = 0;
        let count = 0;
        const step = Math.max(1, Math.floor(Math.min(w, h) / 120));
        for (let y = 1; y < h - 1; y += step) {
          for (let x = 1; x < w - 1; x += step) {
            const idx = y * w + x;
            const lap = 4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - w] - gray[idx + w];
            sum += lap;
            sumSq += lap * lap;
            count++;
          }
        }
        if (!count) return false;
        const mean = sum / count;
        const variance = sumSq / count - mean * mean;
        return variance < 15; // seuil empirique, même ordre de grandeur que le contrôle serveur
      } catch (_) {
        return false; // échec d'analyse : ne pas bloquer, le serveur tranchera à la soumission
      }
    },
    /**
     * @returns {{ ok: true } | { ok: false, message: string } | { ok: null }}
     *   ok: null → validation frontend indisponible, le serveur tranchera à la soumission.
     */
    async validateSelfiePhoto(dataUrl) {
      try {
        await this.initFaceDetection();
        const { count, boxes, img } = await this.countFacesInPhoto(dataUrl);
        if (count === 0) {
          return { ok: false, message: SELFIE_FACE_MESSAGES.none };
        }
        if (count > 1) {
          return { ok: false, message: SELFIE_FACE_MESSAGES.multiple };
        }
        // Ratio de taille du visage et netteté : rapprochés du contrôle serveur
        // (validate_single_face) pour éviter qu'une photo acceptée ici soit rejetée
        // à la soumission sans avertissement préalable.
        const box = boxes && boxes[0];
        const imgW = img?.naturalWidth || img?.width || 0;
        const imgH = img?.naturalHeight || img?.height || 0;
        if (box && imgW && imgH) {
          const faceRatio = (box.width * box.height) / (imgW * imgH);
          if (faceRatio < 0.03) {
            return { ok: false, message: SELFIE_FACE_MESSAGES.tooSmall };
          }
          if (faceRatio > 0.70) {
            return { ok: false, message: SELFIE_FACE_MESSAGES.invalid };
          }
        }
        if (this.isPhotoTooBlurry(img, box)) {
          return { ok: false, message: SELFIE_FACE_MESSAGES.blurry };
        }
        return { ok: true };
      } catch (err) {
        console.warn('Validation selfie frontend:', err?.message || err);
        return { ok: null };
      }
    },
    async applySelfiePhotoFromValidation(dataUrl, file, event) {
      if (!this.uploadFileRefs || typeof this.uploadFileRefs !== 'object') {
        this.uploadFileRefs = {
          photo: null,
          justificatif_identite: null,
          justificatif_residence: null,
          justificatif_revenu: null,
          releve: null,
        };
      }
      this.uploadFileRefs.photo = file || null;
      this.photo = dataUrl;
      this.photoPreview = dataUrl;
      this.photoCaptured = true;
      this.photo_filename = file?.name || 'Photo_selfie.jpg';
      this.persistDraft();
    },
    /**
     * Détection indisponible (CDN lent, appareil sans WebGL) : on accepte la photo
     * avec un avertissement plutôt qu'une modale bloquante — le serveur contrôle
     * de toute façon le visage, le cadrage et la netteté à la validation.
     */
    noteFaceDetectionUnavailable() {
      this.selfieNotice = {
        type: 'info',
        text: 'Vérification automatique indisponible sur cet appareil. Votre photo sera contrôlée à la validation de l’étape : veillez à être seul, de face et bien éclairé.',
      };
    },
    async handleFileChange(event, field) {
      try {
        const file = event?.target?.files?.[0];
        if (!file) return;

        // Taille limite: ~6 Mo
        const MAX_SIZE = 6 * 1024 * 1024;
        if (file.size > MAX_SIZE) {
          await Swal.fire({ title: 'Fichier trop volumineux', text: 'Taille max 6 Mo', icon: 'warning' });
          event.target.value = '';
          return;
        }

        // Types autorisés (photo = image uniquement ; justificatifs = image ou PDF)
        const isPhoto = field === 'photo';
        const allowedTypes = isPhoto ? ['image/'] : ['image/', 'application/pdf'];
        if (!allowedTypes.some(t => file.type.startsWith(t))) {
          const typeMessage = isPhoto
            ? 'Formats acceptés: images uniquement'
            : 'Formats acceptés: images ou PDF';
          await Swal.fire({ title: 'Type non supporté', text: typeMessage, icon: 'error' });
          event.target.value = '';
          return;
        }

        const dataUrl = await this.readFileAsDataUrl(file);
        if (isPhoto) {
          this.selfieChecking = true;
          let validation;
          try {
            validation = await this.validateSelfiePhoto(dataUrl);
          } finally {
            this.selfieChecking = false;
          }
          if (validation.ok === false) {
            this.selfieNotice = { type: 'error', text: validation.message || SELFIE_FACE_MESSAGES.invalid };
            event.target.value = '';
            return;
          }
          if (validation.ok === null) {
            this.noteFaceDetectionUnavailable();
          } else {
            this.selfieNotice = null;
          }
          await this.applySelfiePhotoFromValidation(dataUrl, file, event);
          return;
        }

        if (!this.uploadFileRefs || typeof this.uploadFileRefs !== 'object') {
          this.uploadFileRefs = {
            photo: null,
            justificatif_identite: null,
            justificatif_residence: null,
            justificatif_revenu: null,
            releve: null,
          };
        }
        this.uploadFileRefs[field] = file;
        this[field] = dataUrl;
        const filenameByField = {
          justificatif_identite: 'justificatif_identite_filename',
          justificatif_residence: 'justificatif_residence_filename',
          justificatif_revenu: 'justificatif_revenu_filename',
          releve: 'releve_filename',
        };
        const fk = filenameByField[field];
        if (fk) this[fk] = file?.name || '';
        this.persistDraft();
      } catch (_) {
        await Swal.fire({ title: 'Erreur', text: "Impossible de lire le fichier", icon: 'error' });
      }
    },
    // ===== WEBCAM METHODS =====
    async startSelfieCapture() {
      this.webcamError = '';
      this.webcamActive = true;
      await this.$nextTick();
      await this.startWebcam();
    },
    async startWebcam() {
      try {
        this.webcamError = '';

        // 1. Obtenir le container
        const container = this.$refs.webcamContainer;
        if (!container) {
          throw new Error('Container not found');
        }

        // 2. Créer l'élément vidéo AVANT d'obtenir le stream
        container.innerHTML = '';
        const video = document.createElement('video');
        video.setAttribute('playsinline', 'true');
        video.setAttribute('muted', 'true');
        video.setAttribute('autoplay', 'true');
        video.className = 'webcam-video';
        video.style.width = '100%';
        video.style.maxWidth = '400px';
        video.style.border = '2px solid #ddd';
        video.style.borderRadius = '8px';
        video.style.display = 'block';

        // 3. Ajouter au DOM MAINTENANT
        container.appendChild(video);

        // 4. Contraintes adaptées desktop/mobile
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        const constraints = {
          audio: false,
          video: isMobile
            ? { facingMode: 'user' }  // Mobile: facingMode suffit
            : {
              width: { ideal: 1280, max: 1920 },
              height: { ideal: 720, max: 1080 }
            }  // Desktop: pas de facingMode
        };

        // 5. Obtenir le stream
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.webcamStream = stream;

        // 6. Assigner via Promise pour être sûr
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Timeout')), 10000);

          video.onloadedmetadata = () => {
            clearTimeout(timeout);
            video.play().then(resolve).catch(reject);
          };
          video.onerror = (e) => {
            clearTimeout(timeout);
            reject(e);
          };

          // L'assignation déclenche l'événement loadedmetadata
          video.srcObject = stream;
        });

        // 7. Succès
        this.webcamVideoElement = video;
        this.startAutoCaptureLoop();

      } catch (err) {
        this.webcamError = 'Erreur: Impossible d\'accéder à la caméra. Vérifiez les permissions.';
        console.error('Webcam error:', err);

        // Nettoyer
        if (this.webcamStream) {
          this.webcamStream.getTracks().forEach(track => track.stop());
          this.webcamStream = null;
        }
        this.webcamActive = false;

        // Le regroupement par nom d'erreur navigateur (NotFoundError, etc.) reste
        // utile pour orienter le message, mais jamais err.message brut en repli
        // (texte du navigateur, souvent en anglais, ex. "Permission denied").
        const noDevice = !err.message || /not found|not found here|NotFoundError|DevicesNotFoundError/i.test(err.message);
        const text = noDevice
          ? 'Aucune caméra détectée. Utilisez « Importer une photo » pour ajouter un selfie depuis votre galerie.'
          : 'Impossible d\'accéder à votre caméra. Vérifiez les permissions du navigateur.';
        await Swal.fire({
          title: 'Erreur caméra',
          text,
          icon: 'warning'
        });
      }
    },
    /**
     * Détection continue pendant que la webcam tourne : dès qu'un seul visage reste
     * détecté sans interruption pendant AUTO_CAPTURE_STABLE_MS, on déclenche la même
     * capture que le bouton "Capturer" manuel, qui reste disponible en secours.
     */
    startAutoCaptureLoop() {
      this.stopAutoCaptureLoop();
      this.autoCaptureRemainingMs = 1200;
      this.autoCaptureTimer = setInterval(() => {
        this.checkAutoCapture();
      }, 350);
    },
    stopAutoCaptureLoop() {
      if (this.autoCaptureTimer) {
        clearInterval(this.autoCaptureTimer);
        this.autoCaptureTimer = null;
      }
      this.autoCaptureStableSince = null;
      this.autoCaptureStable = false;
    },
    async checkAutoCapture() {
      const STABLE_MS = 1200;
      if (this.autoCaptureTriggering || this.selfieChecking) return;
      const video = this.webcamVideoElement;
      if (!video || video.readyState < 2) return;

      // Indépendant de face-api (pas de blocage si le CDN des modèles est lent/indisponible) :
      // un flux caméra persistant noir (paramètres de confidentialité Windows, caméra IR
      // sélectionnée par défaut, caméra déjà utilisée par une autre appli) laissait
      // l'utilisateur face à un écran noir sans la moindre explication.
      this._checkLiveFeedBlack(video);

      if (!this.faceDetectionReady || typeof faceapi === 'undefined') return;

      try {
        const detections = await faceapi.detectAllFaces(
          video,
          new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.3 })
        );
        if (detections.length !== 1) {
          this.autoCaptureStableSince = null;
          this.autoCaptureStable = false;
          this.autoCaptureRemainingMs = STABLE_MS;
          return;
        }

        if (this.autoCaptureStableSince == null) {
          this.autoCaptureStableSince = Date.now();
        }
        const elapsed = Date.now() - this.autoCaptureStableSince;
        this.autoCaptureStable = true;
        this.autoCaptureRemainingMs = Math.max(0, STABLE_MS - elapsed);

        if (elapsed >= STABLE_MS) {
          this.autoCaptureTriggering = true;
          try {
            await this.capturePhoto();
          } finally {
            this.autoCaptureTriggering = false;
            this.autoCaptureStableSince = null;
          }
        }
      } catch (err) {
        // Silencieux : un raté ponctuel de détection ne doit pas interrompre la boucle,
        // le prochain tick réessaiera.
        console.warn('Auto-capture détection:', err?.message || err);
      }
    },
    async capturePhoto() {
      try {
        const video = this.webcamVideoElement;
        const canvas = this.$refs.photoCanvas;

        if (!video || !canvas) {
          throw new Error('Éléments video/canvas non disponibles');
        }

        this.selfieChecking = true;

        // Configurer le canvas avec la même résolution que la vidéo
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Capturer la frame
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);

        // Certaines webcams (surtout sur ordinateur) envoient une ou plusieurs frames
        // noires le temps que l'exposition se calibre, alors même que readyState >= 2
        // indique déjà "des données disponibles" : sans ce contrôle, on capturait
        // parfois cette frame noire de calibration. On réessaie automatiquement
        // quelques fois avant d'abandonner, plutôt que de proposer une photo inutilisable.
        if (this._isFrameNearBlack(ctx, canvas.width, canvas.height)) {
          this._blackFrameRetries = (this._blackFrameRetries || 0) + 1;
          if (this._blackFrameRetries <= 5 && this.webcamActive) {
            setTimeout(() => {
              if (this.webcamActive) this.capturePhoto();
              else this.selfieChecking = false;
            }, 300);
            return;
          }
          this._blackFrameRetries = 0;
          this.selfieChecking = false;
          this.selfieNotice = { type: 'error', text: SELFIE_FACE_MESSAGES.tooDark };
          return;
        }
        this._blackFrameRetries = 0;

        const photoDataUrl = canvas.toDataURL('image/jpeg', 0.95);

        // Message en ligne plutôt que modale : la caméra reste ouverte et l'on peut
        // se recadrer puis recapturer sans fermer une boîte de dialogue à chaque essai.
        let validation;
        try {
          validation = await this.validateSelfiePhoto(photoDataUrl);
        } finally {
          this.selfieChecking = false;
        }
        if (validation.ok === false) {
          this.selfieNotice = { type: 'error', text: validation.message || SELFIE_FACE_MESSAGES.invalid };
          return;
        }
        if (validation.ok === null) {
          this.noteFaceDetectionUnavailable();
        } else {
          this.selfieNotice = null;
        }

        this.stopWebcam();
        await this.applySelfiePhotoFromValidation(photoDataUrl, null);
        this.photo_filename = 'Photo_webcam.jpg';
      } catch (err) {
        logger.error('Photo capture error:', err);
        console.error('Capture error:', err);
        this.selfieChecking = false;
        this.selfieNotice = { type: 'error', text: 'Impossible de capturer la photo. Réessayez.' };
      }
    },
    /**
     * Surveille le flux vidéo en direct (pas juste une capture ponctuelle) : si l'image
     * reste quasi-noire plusieurs secondes d'affilée (~9 vérifications à 350ms), la caméra
     * ne renvoie probablement aucune image exploitable (accès bloqué au niveau OS, caméra
     * IR par défaut, déjà utilisée ailleurs) — on l'indique clairement au lieu de laisser
     * l'utilisateur face à un écran noir muet.
     */
    _checkLiveFeedBlack(video) {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return;
        if (!this._blackPreviewCanvas) {
          this._blackPreviewCanvas = document.createElement('canvas');
        }
        const canvas = this._blackPreviewCanvas;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, w, h);

        if (this._isFrameNearBlack(ctx, w, h)) {
          this._blackPreviewStreak = (this._blackPreviewStreak || 0) + 1;
          if (this._blackPreviewStreak === 9) {
            this.webcamError = CAMERA_BLACK_FEED_MESSAGE;
          }
        } else {
          this._blackPreviewStreak = 0;
          if (this.webcamError === CAMERA_BLACK_FEED_MESSAGE) {
            this.webcamError = '';
          }
        }
      } catch (_) {
        // Échec d'analyse silencieux : ne pas interrompre la boucle d'auto-capture.
      }
    },
    /** Détecte une frame de calibration noire (webcam pas encore stabilisée) via un
     *  échantillonnage grossier de la luminosité, sans analyser tous les pixels. */
    _isFrameNearBlack(ctx, width, height) {
      try {
        const { data } = ctx.getImageData(0, 0, width, height);
        let total = 0;
        let count = 0;
        const stepX = Math.max(1, Math.floor(width / 40));
        const stepY = Math.max(1, Math.floor(height / 40));
        for (let y = 0; y < height; y += stepY) {
          for (let x = 0; x < width; x += stepX) {
            const idx = (y * width + x) * 4;
            total += (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
            count++;
          }
        }
        if (!count) return false;
        return total / count < 12;
      } catch (_) {
        return false; // échec d'analyse : ne pas bloquer la capture
      }
    },
    stopWebcam() {
      this.stopAutoCaptureLoop();
      if (this.webcamStream) {
        this.webcamStream.getTracks().forEach(track => track.stop());
        this.webcamStream = null;
      }
      // Nettoyer l'élément vidéo
      if (this.$refs.webcamContainer) {
        this.$refs.webcamContainer.innerHTML = '';
      }
      this.webcamVideoElement = null;
      this.webcamActive = false;
      this.webcamError = '';
      this._blackFrameRetries = 0;
      this._blackPreviewStreak = 0;
    },
    async retakePhoto() {
      this.photoCaptured = false;
      this.photoPreview = '';
      this.photo = '';
      this.photo_filename = '';
      this.selfieNotice = null;
      if (this.uploadFileRefs) this.uploadFileRefs.photo = null;
      const input = this.$refs.photoFallbackFileInput;
      if (input) input.value = '';
      try { this.persistDraft(); } catch (_) { }
      await this.startSelfieCapture();
    },
    async handlePhotoUpload(event) {
      await this.handleFileChange(event, 'photo');
    },
    persistDraft() {
      try {
        const keys = [
          'last_name', 'first_name', 'born', 'comborn', 'depborn', 'fonction', 'personalInfoLocked',
          'telMobile', 'ville', 'adresse', 'situation', 'regim', 'nb_enfants', 'nb_enfants_charges', 'nbenfants_non_charges', 'possede_entreprise', 'possede_commerce', 'revenus_entreprise', 'valeur_entreprise', 'activites_professionnelles', 'nb_annees_restantes',
          'bien_usage', 'valeurs_bien_usage', 'biens_generateurs', 'valeurs_biens_generateurs', 'revenus_locatifs', 'terrains', 'valeurterrains', 'documents_propriete', 'pret_en_cours', 'montant_pret', 'duree_pret', 'montant_mensualite',
          'has_compte_valeur', 'compte_valeur', 'has_compte_cheque', 'compte_cheque', 'has_compte_epargne', 'compte_epargne', 'has_compte_disponible', 'compte_disponible', 'has_assurance_vie', 'assurance_vie', 'has_assurance_produit', 'assurance_produit', 'has_assurance_deces', 'assurance_deces', 'placements_financiers', 'detention_conjointe',
          'loyer', 'telephone', 'electricite', 'gaz', 'eau', 'abonnement', 'ordures', 'reparation', 'fournitures', 'salaire_personnel_maison', 'popote', 'loyer_autre',
          'pret_voiture', 'transport_public', 'assurance_voiture', 'carburant', 'entretien_transport', 'autres_transport',
          'habitat', 'sante', 'vie', 'autresAssurance',
          'nourriture_animaux', 'frais_medicaux', 'toilettage', 'jouets', 'autres_animaux',
          'medical', 'coiffeur', 'habit', 'sport_soins', 'cotisations', 'nettoyage', 'autre_soins',
          'concert', 'cinema', 'theatre', 'sport', 'restaurant', 'autres_loisirs',
          'pret_personnel', 'pret_etudiant', 'epargne', 'foncier', 'locaux', 'autres_impots', 'soi_meme', 'enfant', 'autres_etudes', 'autres', 'caritatif', 'dimes',
          'analyseId', 'isReportGenerated', 'wizardStepId', 'financialDocumentsSaved',
          'objectif', 'montantObjectif', 'salaire', 'revenu_mensuel_reel', 'chiffres_affaires', 'resultats_nets', 'ebe_ebitda',
          'type_justificatif_identite', 'justificatif_identite', 'justificatif_identite_filename', 'justificatif_residence', 'justificatif_residence_filename', 'photo', 'photo_filename', 'justificatif_revenu', 'justificatif_revenu_filename', 'releve', 'releve_filename'
        ];
        const snapshot = {};
        keys.forEach(k => { snapshot[k] = this[k]; });
        const storageKey = this.getDraftKey();
        const backupKey = this.getDraftDocumentSessionBackupKey();
        const json = JSON.stringify(snapshot);
        try {
          localStorage.setItem(storageKey, json);
          try { sessionStorage.removeItem(backupKey); } catch (_) { }
        } catch (e) {
          const isQuota = e && (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014);
          if (isQuota) {
            const blobKeys = this.getDraftBlobLikeFieldKeys();
            const docs = {};
            const light = { ...snapshot };
            blobKeys.forEach((k) => {
              docs[k] = light[k];
              light[k] = typeof light[k] === 'string' ? '' : light[k];
            });
            try {
              localStorage.setItem(storageKey, JSON.stringify(light));
            } catch (_) { }
            try {
              sessionStorage.setItem(backupKey, JSON.stringify(docs));
            } catch (_) { }
          }
        }
        if (String(this.typeOffre || '').toLowerCase() === 'duo') {
          try { this.stashDuoPersonalForOrdre(this.ordreDuo); } catch (_) { }
        }
        // Important (Duo): on "fige" l'ordre_duo au moment où l'on planifie l'envoi.
        // Sinon, si l'utilisateur bascule d'onglet avant le timeout, on risque d'envoyer
        // le snapshot du conjoint A avec ordre_duo du conjoint B (effet miroir).
        this.scheduleSaveDraftToServer({ snapshot, ordreDuo: this.ordreDuo, typeOffre: this.typeOffre });
      } catch (_) { }
    },
    // Clés envoyées au serveur (sans les fichiers pour limiter la taille)
    getDraftKeysForServer() {
      return [
        'last_name', 'first_name', 'born', 'comborn', 'depborn', 'fonction', 'personalInfoLocked',
        'telMobile', 'ville', 'adresse', 'situation', 'regim', 'nb_enfants', 'nb_enfants_charges', 'nbenfants_non_charges', 'possede_entreprise', 'possede_commerce', 'revenus_entreprise', 'valeur_entreprise', 'activites_professionnelles', 'nb_annees_restantes',
        'bien_usage', 'valeurs_bien_usage', 'biens_generateurs', 'valeurs_biens_generateurs', 'revenus_locatifs', 'terrains', 'valeurterrains', 'documents_propriete', 'pret_en_cours', 'montant_pret', 'duree_pret', 'montant_mensualite',
        'has_compte_valeur', 'compte_valeur', 'has_compte_cheque', 'compte_cheque', 'has_compte_epargne', 'compte_epargne', 'has_compte_disponible', 'compte_disponible', 'has_assurance_vie', 'assurance_vie', 'has_assurance_produit', 'assurance_produit', 'has_assurance_deces', 'assurance_deces', 'placements_financiers', 'detention_conjointe',
        'loyer', 'telephone', 'electricite', 'gaz', 'eau', 'abonnement', 'ordures', 'reparation', 'fournitures', 'salaire_personnel_maison', 'popote', 'loyer_autre',
        'pret_voiture', 'transport_public', 'assurance_voiture', 'carburant', 'entretien_transport', 'autres_transport',
        'habitat', 'sante', 'vie', 'autresAssurance',
        'nourriture_animaux', 'frais_medicaux', 'toilettage', 'jouets', 'autres_animaux',
        'medical', 'coiffeur', 'habit', 'sport_soins', 'cotisations', 'nettoyage', 'autre_soins',
        'concert', 'cinema', 'theatre', 'sport', 'restaurant', 'autres_loisirs',
        'pret_personnel', 'pret_etudiant', 'epargne', 'foncier', 'locaux', 'autres_impots', 'soi_meme', 'enfant', 'autres_etudes', 'autres', 'caritatif', 'dimes',
        'objectif', 'montantObjectif', 'salaire', 'revenu_mensuel_reel', 'chiffres_affaires', 'resultats_nets', 'ebe_ebitda', 'type_justificatif_identite', 'financialDocumentsSaved',
        'justificatif_identite_filename', 'justificatif_residence_filename', 'photo_filename', 'justificatif_revenu_filename', 'releve_filename'
      ];
    },
    scheduleSaveDraftToServer(payload = null) {
      if (this._saveDraftToServerTimer) clearTimeout(this._saveDraftToServerTimer);
      this._saveDraftToServerTimer = setTimeout(() => {
        const frozenPayload = payload;
        this._saveDraftToServerTimer = null;
        this.saveDraftToServer(frozenPayload);
      }, 3000);
    },
    async saveDraftToServer(payload = null) {
      if (!isAuthenticatedSession()) return;
      try {
        const typeOffre = payload?.typeOffre || this.typeOffre;
        const ordreDuo = payload?.ordreDuo ?? this.ordreDuo;
        const snapshot = (() => {
          if (payload?.snapshot && typeof payload.snapshot === 'object') {
            // Ne jamais envoyer les fichiers: filtrer selon getDraftKeysForServer.
            const keys = this.getDraftKeysForServer();
            const filtered = {};
            keys.forEach((k) => { filtered[k] = payload.snapshot[k]; });
            filtered.wizard_step_id = payload.snapshot.wizardStepId ?? this.wizardStepId;
            return filtered;
          }
          const keys = this.getDraftKeysForServer();
          const s = {};
          keys.forEach(k => { s[k] = this[k]; });
          // Étape courante lue par le tableau de bord. Nom distinct de `wizardStepId`
          // pour que la relecture du brouillon ne déplace pas l'utilisateur dans le formulaire.
          s.wizard_step_id = this.wizardStepId;
          return s;
        })();
        const body = typeOffre === 'duo' ? { data: snapshot, ordre_duo: ordreDuo } : { data: snapshot };
        await axios.put('/api/form-draft/', body, this.getAuthConfig());
      } catch (e) {
        if (e.response?.status !== 401) console.warn('Auto-save brouillon serveur:', e?.message || e);
      }
    },
    async loadDraftFromServer() {
      if (!isAuthenticatedSession()) return;
      const params = this.typeOffre === 'duo' ? { ordre_duo: this.ordreDuo } : {};
      const { data } = await axios.get('/api/form-draft/', { ...this.getAuthConfig(), params });
      if (data?.data != null && typeof data.data === 'object' && !Array.isArray(data.data)) {
        const parsed = { ...data.data };
        // Compat anciens brouillons: isPersonalInfoLocked était global -> migrer vers personalInfoLocked.
        if (parsed.personalInfoLocked == null && parsed.isPersonalInfoLocked != null) {
          const legacy = parsed.isPersonalInfoLocked === true || parsed.isPersonalInfoLocked === 'true';
          const t = String(this.typeOffre || 'persona').toLowerCase();
          if (t === 'duo') {
            const key = this.ordreDuo === 2 ? 'duo_2' : 'duo_1';
            parsed.personalInfoLocked = { persona: false, duo_1: false, duo_2: false, [key]: legacy };
          } else {
            parsed.personalInfoLocked = { persona: legacy, duo_1: false, duo_2: false };
          }
        }
        Object.assign(this, parsed);
        this.restoreProfessionalActivities(parsed.activites_professionnelles, parsed);
        this.normalizeStateAfterDraftMerge();
      }
    },
    /**
     * Après fusion d'un brouillon (Object.assign), réparer les structures que le JSON
     * peut avoir corrompues — sinon v-show sur mobileSections.* peut casser tout le rendu.
     */
    normalizeStateAfterDraftMerge() {
      const keys = [
        'coordonnees',
        'situation',
        'immobilier',
        'activites',
        'avoirs',
        'passifs',
        'revenus',
        'charges',
        'objectifs',
        'documents',
      ];
      const defaults = {};
      keys.forEach((k) => {
        defaults[k] = true;
      });
      const m = this.mobileSections;
      if (!m || typeof m !== 'object' || Array.isArray(m)) {
        this.mobileSections = { ...defaults };
      } else {
        const merged = { ...defaults, ...m };
        keys.forEach((k) => {
          if (typeof merged[k] !== 'boolean') merged[k] = true;
        });
        this.mobileSections = merged;
      }
      if (!Array.isArray(this.activites_professionnelles)) {
        this.activites_professionnelles = [];
      }

      // Normaliser le verrou des infos perso (Duo: par conjoint)
      const p = this.personalInfoLocked;
      if (!p || typeof p !== 'object' || Array.isArray(p)) {
        this.personalInfoLocked = { persona: false, duo_1: false, duo_2: false };
      } else {
        const merged = { persona: false, duo_1: false, duo_2: false, ...p };
        ['persona', 'duo_1', 'duo_2'].forEach((k) => {
          merged[k] = merged[k] === true || merged[k] === 'true';
        });
        this.personalInfoLocked = merged;
      }
      // Le JSON brouillon (surtout serveur) peut contenir null explicite : Object.assign casse les tests
      // du computed isPersonalInfoComplete (this.photo && …, salaire != null, etc.) → bouton Valider grisé après switch Duo.
      const nullToEmpty = (key) => {
        if (this[key] === null || this[key] === undefined) this[key] = '';
      };
      [
        'last_name', 'first_name', 'born', 'comborn', 'depborn', 'fonction',
        'type_justificatif_identite', 'photo', 'photo_filename',
        'justificatif_identite', 'justificatif_identite_filename',
        'justificatif_residence', 'justificatif_residence_filename',
        'justificatif_revenu', 'justificatif_revenu_filename',
        'releve', 'releve_filename',
        'salaire', 'nb_annees_restantes',
      ].forEach(nullToEmpty);
      this.sanitizeOrphanDocumentFilenamesInPlace(this);
      this.$nextTick(() => {
        try { this.resetJustificatifFileInputElements(); } catch (_) { }
      });
    },
    startAutoSaveInterval() {
      this._autoSaveInterval = setInterval(() => {
        if (isAuthenticatedSession()) this.saveDraftToServer();
      }, 30000);
    },
    getAuthConfig() {
      if (!isAuthenticatedSession()) {
        this.errorMessage = getErrorMessage({ response: { status: 401 } }).message;
        this.$router.push('/connexion');
        throw new Error('Token manquant');
      }
      return {
        withCredentials: true
      };
    },
    // Chargement du profil utilisateur
    async loadUserProfile() {
      try {
        if (!isAuthenticatedSession()) {
          this.error = getErrorMessage({ response: { status: 401 } }).message;
          this.$router.push("/connexion");
          this.loading = false;
          return;
        }

        const response = await axios.get("/api/profile/", {
          withCredentials: true,
        });

        const data = response.data;
        storeUserProfileFromApi(data);

        // Champs en lecture seule
        this.last_name = data.last_name;
        this.first_name = data.first_name;
        this.email = data.email;
        this.login = data.username || '';
        this.assignProfileStrIfPresent('fonction', data.fonction);
        this.assignProfileStrIfPresent('adresse', data.adresse);
        this.assignProfileStrIfPresent('telMobile', data.tel);
        this.assignProfileStrIfPresent('ville', data.ville);
      } catch (error) {
        this.errorMessage = this.getDetailedErrorMessage(error).message;
      } finally {
        this.loading = false;
      }
    },
    // Chargement du profil client (et type_offre pour Duo)
    async loadClientProfileForOffre() {
      try {
        if (!isAuthenticatedSession()) return;
        const response = await axios.get("/api/all-clients/", {
          withCredentials: true
        });
        const data = response.data;
        this.typeOffre = normalizePayableOffer(data.type_offre || 'persona');
        this.assignClientBirthFromApi(data);
        this.clientApte = data.client_apte === true;
      } catch (error) {
        if (error.response?.status !== 401) console.error("Chargement profil client:", error);
      }
    },
    async loadClientProfile() {
      try {
        if (!isAuthenticatedSession()) return;
        const response = await axios.get("/api/all-clients/", this.getAuthConfig());
        const data = response.data;
        this.typeOffre = normalizePayableOffer(data.type_offre || this.typeOffre);
        this.assignClientBirthFromApi(data);
        this.clientApte = data.client_apte === true;
      } catch (error) {
        if (error.response?.status !== 401) console.error("Erreur lors du chargement des informations du client:", error);
      }
    },
    async switchOrdreDuo(ordre) {
      if (this.ordreDuo === ordre) return;
      if (this.typeOffre === 'duo') {
        try { this.stashDuoPersonalForOrdre(this.ordreDuo); } catch (_) { }
        try { this.stashDuoWorkflowForOrdre(this.ordreDuo); } catch (_) { }
      }
      // Sauvegarder le conjoint courant avant de basculer.
      try { this.persistDraft(); } catch (_) { }
      this.ordreDuo = ordre;
      // Important: si aucun brouillon n'existe pour le conjoint cible, Vue conserverait
      // les valeurs du conjoint précédent (même state). On réinitialise donc les champs
      // "draftables" aux valeurs par défaut avant de charger le brouillon.
      try { this.resetDraftableFieldsToDefaults(); } catch (_) { }
      if (this.typeOffre === 'duo') {
        try { this.applyDuoWorkflowFromStash(ordre); } catch (_) { }
      }
      try {
        await this.loadDraftFromServer();
        // Compléter par le brouillon local du conjoint ciblé (fichiers, etc.)
        try {
          const draft = localStorage.getItem(this.getDraftKey());
          if (draft) {
            const parsed = JSON.parse(draft);
            if (parsed.personalInfoLocked == null && parsed.isPersonalInfoLocked != null) {
              const legacy = parsed.isPersonalInfoLocked === true || parsed.isPersonalInfoLocked === 'true';
              const t = String(this.typeOffre || 'persona').toLowerCase();
              if (t === 'duo') {
                const key = this.ordreDuo === 2 ? 'duo_2' : 'duo_1';
                parsed.personalInfoLocked = { persona: false, duo_1: false, duo_2: false, [key]: legacy };
              } else {
                parsed.personalInfoLocked = { persona: legacy, duo_1: false, duo_2: false };
              }
            }
            Object.assign(this, parsed);
            this.restoreProfessionalActivities(parsed.activites_professionnelles, parsed);
          }
          try { this.mergeDraftDocumentsFromSessionBackup(); } catch (_) { }
        } catch (_) { }
        this.normalizeStateAfterDraftMerge();
        if (this.typeOffre === 'duo') {
          try { this.stashDuoWorkflowForOrdre(this.ordreDuo); } catch (_) { }
        }
        // Après fusion brouillon, mémoriser les infos perso : prefillFromLatestAnalysis peut encore
        // les vider (revenus/financier partiels, autres branches) — on restaure si le prefill les a vidées.
        const duoPersonalKeys = this.typeOffre === 'duo' ? this.getDuoPersonalSwitchKeys() : null;
        const duoPersonalSnapshot =
          this.typeOffre === 'duo' && duoPersonalKeys
            ? duoPersonalKeys.reduce((acc, k) => {
              acc[k] = this[k];
              return acc;
            }, {})
            : null;
        await this.prefillFromLatestAnalysis();
        if (duoPersonalSnapshot && duoPersonalKeys) {
          duoPersonalKeys.forEach((k) => {
            const prev = duoPersonalSnapshot[k];
            if (!this.isEmptyDuoPersonalVal(prev) && this.isEmptyDuoPersonalVal(this[k])) {
              this[k] = prev;
            }
          });
          try { this.applyDuoPersonalStashFromMemory(this.ordreDuo); } catch (_) { }
          try { this.persistDraft(); } catch (_) { }
        }
        if (this.typeOffre === 'duo') {
          try { this.stashDuoPersonalForOrdre(this.ordreDuo); } catch (_) { }
        }
        try { this.syncPersonalPhotoUiAfterDraftChange(); } catch (_) { }
      } catch (e) {
        console.warn('Switch conjoint:', e?.message || e);
      }
    },

    resetDraftableFieldsToDefaults() {
      const defaults = (this.$options?.data ? this.$options.data.call(this) : {}) || {};
      const keys = [
        'last_name', 'first_name', 'born', 'comborn', 'depborn', 'fonction',
        'telMobile', 'ville', 'adresse', 'situation', 'regim', 'nb_enfants', 'nb_enfants_charges', 'nbenfants_non_charges', 'possede_entreprise', 'possede_commerce', 'revenus_entreprise', 'valeur_entreprise', 'activites_professionnelles', 'nb_annees_restantes',
        'bien_usage', 'valeurs_bien_usage', 'biens_generateurs', 'valeurs_biens_generateurs', 'revenus_locatifs', 'terrains', 'valeurterrains', 'documents_propriete', 'pret_en_cours', 'montant_pret', 'duree_pret', 'montant_mensualite',
        'has_compte_valeur', 'compte_valeur', 'has_compte_cheque', 'compte_cheque', 'has_compte_epargne', 'compte_epargne', 'has_compte_disponible', 'compte_disponible', 'has_assurance_vie', 'assurance_vie', 'has_assurance_produit', 'assurance_produit', 'has_assurance_deces', 'assurance_deces', 'placements_financiers', 'detention_conjointe',
        'loyer', 'telephone', 'electricite', 'gaz', 'eau', 'abonnement', 'ordures', 'reparation', 'fournitures', 'salaire_personnel_maison', 'popote', 'loyer_autre',
        'pret_voiture', 'transport_public', 'assurance_voiture', 'carburant', 'entretien_transport', 'autres_transport',
        'habitat', 'sante', 'vie', 'autresAssurance',
        'nourriture_animaux', 'frais_medicaux', 'toilettage', 'jouets', 'autres_animaux',
        'medical', 'coiffeur', 'habit', 'sport_soins', 'cotisations', 'nettoyage', 'autre_soins',
        'concert', 'cinema', 'theatre', 'sport', 'restaurant', 'autres_loisirs',
        'pret_personnel', 'pret_etudiant', 'epargne', 'foncier', 'locaux', 'autres_impots', 'soi_meme', 'enfant', 'autres_etudes', 'autres', 'caritatif', 'dimes',
        'objectif', 'montantObjectif', 'salaire', 'revenu_mensuel_reel', 'chiffres_affaires', 'resultats_nets', 'ebe_ebitda',
        'type_justificatif_identite', 'justificatif_identite', 'justificatif_identite_filename', 'justificatif_residence', 'justificatif_residence_filename', 'photo', 'photo_filename', 'justificatif_revenu', 'justificatif_revenu_filename', 'releve', 'releve_filename',
        'analyseId', 'isReportGenerated', 'wizardStepId', 'financialDocumentsSaved'
      ];
      keys.forEach((k) => {
        // Évite d'écraser des clés non gérées par data() (sécurité)
        if (Object.prototype.hasOwnProperty.call(defaults, k)) {
          this[k] = defaults[k];
        } else {
          // Fallback minimal
          this[k] = Array.isArray(this[k]) ? [] : (typeof this[k] === 'string' ? '' : null);
        }
      });
      // Si on arrive sur un conjoint sans brouillon, son verrou doit être "déverrouillé" par défaut.
      // (Le verrou de l'autre conjoint reste intact dans personalInfoLocked.)
      try { this.isPersonalInfoLocked = false; } catch (_) { }
      this.normalizeStateAfterDraftMerge();
      try { this.syncPersonalPhotoUiAfterDraftChange(); } catch (_) { }
    },

    getPersonalInfoLockKeyForCurrentOffer() {
      const t = String(this.typeOffre || 'persona').toLowerCase();
      if (t === 'duo') return this.ordreDuo === 2 ? 'duo_2' : 'duo_1';
      return 'persona';
    },

    normalizeLatestAnalysisDetails(raw) {
      if (!raw) return null;
      if (Object.prototype.hasOwnProperty.call(raw, 'details') && raw.details == null) {
        return null;
      }
      if (raw.details && typeof raw.details === 'object') return raw.details;
      if (Array.isArray(raw) && raw.length > 0) {
        return raw[0]?.details || raw[0];
      }
      if (Array.isArray(raw.results) && raw.results.length > 0) {
        return raw.results[0]?.details || raw.results[0];
      }
      return raw;
    },
    markAnalysisAsSubmitted(analyseId) {
      if (analyseId != null) this.analyseId = analyseId;
      this.isReportGenerated = true;
      this.loading = false;
      this.ga4AbandonTracked = true;
      this.wizardStepId = 'page6_submit';
      if (this.typeOffre === 'duo') {
        try { this.stashDuoWorkflowForOrdre(this.ordreDuo); } catch (_) { }
      }
      try {
        this.persistDraft();
      } catch (_) { }
    },

    getDefaultDuoWorkflowState() {
      return {
        isReportGenerated: false,
        analyseId: null,
        wizardStepId: 'page1_profile',
        financialDocumentsSaved: false,
        hasVisitedSubmitStep: false,
      };
    },

    stashDuoWorkflowForOrdre(ordre) {
      if (this.typeOffre !== 'duo' || (ordre !== 1 && ordre !== 2)) return;
      this.duoWorkflowByOrdre = {
        ...this.duoWorkflowByOrdre,
        [ordre]: {
          isReportGenerated: !!this.isReportGenerated,
          analyseId: this.analyseId,
          wizardStepId: this.wizardStepId,
          financialDocumentsSaved: !!this.financialDocumentsSaved,
          hasVisitedSubmitStep: !!this.hasVisitedSubmitStep,
        },
      };
    },

    applyDuoWorkflowFromStash(ordre) {
      if (this.typeOffre !== 'duo' || (ordre !== 1 && ordre !== 2)) return;
      const stashed = this.duoWorkflowByOrdre?.[ordre];
      const defaults = this.getDefaultDuoWorkflowState();
      const w = stashed && typeof stashed === 'object' ? { ...defaults, ...stashed } : defaults;
      this.isReportGenerated = !!w.isReportGenerated;
      this.analyseId = w.analyseId ?? null;
      this.wizardStepId = w.wizardStepId || defaults.wizardStepId;
      this.financialDocumentsSaved = !!w.financialDocumentsSaved;
      this.hasVisitedSubmitStep = !!w.hasVisitedSubmitStep;
    },

    startClientAptePolling() {
      if (this.clientApte || this._clientAptePollTimer) return;
      this.clientAptePolling = true;
      this.clientAptePollAttempts = 0;
      const poll = async () => {
        if (this.clientApte) {
          this.stopClientAptePolling();
          return;
        }
        this.clientAptePollAttempts += 1;
        try {
          await this.loadClientProfileForOffre();
        } catch (_) { }
        if (this.clientApte || this.clientAptePollAttempts >= 24) {
          this.stopClientAptePolling();
          if (!this.clientApte && this.clientAptePollAttempts >= 24) {
            this.errorMessage =
              'La confirmation du paiement prend plus de temps que prévu. Actualisez la page dans quelques minutes ou contactez le support si le blocage persiste.';
          }
        }
      };
      poll();
      this._clientAptePollTimer = setInterval(poll, 5000);
    },

    stopClientAptePolling() {
      this.clientAptePolling = false;
      if (this._clientAptePollTimer) {
        clearInterval(this._clientAptePollTimer);
        this._clientAptePollTimer = null;
      }
    },
    trackFormAbandonIfNeeded() {
      if (this.ga4AbandonTracked || this.isReportGenerated) return;
      this.ga4AbandonTracked = true;
      trackMonAnalyseFormAbandon({
        offer: this.typeOffre,
        lastSection: resolveMonAnalyseLastSection({
          isReportGenerated: this.isReportGenerated,
          showRestOfForm: this.showRestOfForm,
          objectif: this.objectif,
          pret_en_cours: this.pret_en_cours,
          hasFinancialData: this.has_compte_valeur || this.has_compte_cheque || this.has_compte_epargne,
          situation: this.situation,
          isPersonalInfoLocked: this.isPersonalInfoLocked,
          clientApte: this.clientApte,
          wizardStepId: this.wizardStepId,
        }),
      });
    },
    async tryRecoverExistingAnalysisAfterSubmitError() {
      try {
        if (!isAuthenticatedSession()) return false;
        const params = { latest: 'true' };
        if (this.typeOffre === 'duo') params.ordre_duo = this.ordreDuo;
        const { data } = await axios.get('/api/analyse-patrimoniale/', {
          params,
          withCredentials: true,
          timeout: 25000,
        });
        const d = this.normalizeLatestAnalysisDetails(data);
        if (!d || d.id == null) return false;
        this.markAnalysisAsSubmitted(d.id);
        return true;
      } catch (_) {
        return false;
      }
    },
    async prefillFromLatestAnalysis() {
      try {
        if (!isAuthenticatedSession()) return;
        const params = { latest: 'true' };
        if (this.typeOffre === 'duo') params.ordre_duo = this.ordreDuo;
        const { data } = await axios.get('/api/analyse-patrimoniale/', {
          params,
          withCredentials: true
        });

        const d = this.normalizeLatestAnalysisDetails(data);
        if (!d) {
          this.analyseId = null;
          return;
        }
        if (d.id != null) {
          this.markAnalysisAsSubmitted(d.id);
        }
        // Préremplir les données par sections
        // Tolérer différentes clés renvoyées par l'API (compatibilité)
        this.prefillRevenus(d.revenus || d.Revenus);
        this.prefillSituation(d.situation);
        this.prefillImmobilier(d.immobilier || d.immobilier_passif);
        this.prefillFinancier(d.financier || d.patrimoine_financier);
        this.prefillCharges(d.charges);
        this.prefillObjectifs(d.objectifs);

      } catch (e) {
        console.error('Prefill latest analysis failed', e);
      }
    },

    prefillRevenus(revenus) {
      if (!revenus) return;
      // Ne pas forcer 0 si la clé est absente : après switch Duo le brouillon peut avoir un salaire
      // alors que l’objet revenus de l’API est partiel (sinon :disabled sur « Valider »).
      this.assignPrefillNumberIfPresent('salaire', revenus, 'salaire');
      this.assignPrefillNumberIfPresent('revenu_mensuel_reel', revenus, 'revenu_mensuel_reel');
      this.persistDraft();
    },

    prefillSituation(situation) {
      if (!situation) return;
      this.situation = situation.situation || '';
      this.regim = situation.regim || '';
      this.nb_enfants = situation.nb_enfants || 0;
      this.nb_enfants_charges = situation.nb_enfants_charges || 0;
      this.nbenfants_non_charges = situation.nbenfants_non_charges || 0;
      this.possede_entreprise = situation.possede_entreprise ? 'Oui' : 'Non';
      this.possede_commerce = situation.possede_commerce ? 'Oui' : 'Non';
      this.revenus_entreprise = situation.revenus_entreprise || 0;
      this.valeur_entreprise = situation.valeur_entreprise || 0;
      this.restoreProfessionalActivities(situation.activites_professionnelles, situation);
      this.persistDraft();
    },

    prefillImmobilier(immobilier) {
      if (!immobilier) return;
      this.bien_usage = immobilier.bien_usage || 0;
      this.valeurs_bien_usage = immobilier.valeurs_bien_usage || 0;
      this.choix_biens_usage = immobilier.choix_biens_usage ? 'Oui' : 'Non';
      this.biens_generateurs = immobilier.biens_generateurs || 0;
      this.valeurs_biens_generateurs = immobilier.valeurs_biens_generateurs || 0;
      this.revenus_locatifs = immobilier.revenus_locatifs || 0;
      this.terrains = immobilier.terrains ? 'Oui' : 'Non';
      this.valeurterrains = immobilier.valeurterrains || 0;
      this.documents_propriete = immobilier.documents_propriete || '';
      this.pret_en_cours = immobilier.pret_en_cours ? 'Oui' : 'Non';
      this.montant_pret = immobilier.montant_pret || 0;
      this.duree_pret = immobilier.duree_pret || 0;
      this.montant_mensualite = immobilier.montant_mensualite || 0;
      this.persistDraft();
    },

    prefillFinancier(financier) {
      if (!financier) return;
      this.has_compte_valeur = typeof financier.has_compte_valeur === 'boolean'
        ? financier.has_compte_valeur
        : !!financier.compte_valeur;
      this.compte_valeur = financier.compte_valeur || 0;
      this.has_compte_cheque = typeof financier.has_compte_cheque === 'boolean'
        ? financier.has_compte_cheque
        : !!financier.compte_cheque;
      this.compte_cheque = financier.compte_cheque || 0;
      this.has_compte_epargne = typeof financier.has_compte_epargne === 'boolean'
        ? financier.has_compte_epargne
        : !!financier.compte_epargne;
      this.compte_epargne = financier.compte_epargne || 0;
      this.has_compte_disponible = typeof financier.has_compte_disponible === 'boolean'
        ? financier.has_compte_disponible
        : !!financier.compte_disponible;
      this.compte_disponible = financier.compte_disponible || 0;
      this.montant_compte_disponible = financier.montant_compte_disponible ?? null;
      this.has_compte_dat = typeof financier.has_compte_dat === 'boolean'
        ? financier.has_compte_dat
        : !!financier.compte_dat;
      this.compte_dat = financier.compte_dat ?? null;
      this.montant_compte_dat = financier.montant_compte_dat ?? null;
      this.montant_compte_valeur = financier.montant_compte_valeur ?? null;
      this.montant_compte_epargne = financier.montant_compte_epargne ?? null;
      this.has_assurance_vie = typeof financier.has_assurance_vie === 'boolean'
        ? financier.has_assurance_vie
        : !!financier.assurance_vie;
      this.assurance_vie = financier.assurance_vie || 0;
      this.has_assurance_produit = typeof financier.has_assurance_produit === 'boolean'
        ? financier.has_assurance_produit
        : !!financier.assurance_produit;
      this.assurance_produit = financier.assurance_produit || 0;
      this.has_assurance_deces = typeof financier.has_assurance_deces === 'boolean'
        ? financier.has_assurance_deces
        : !!financier.assurance_deces;
      this.assurance_deces = financier.assurance_deces || 0;
      this.placements_financiers = financier.placements_financiers || '';
      this.detention_conjointe = financier.detention_conjointe || '';
      // Ne pas écraser photo / pièces avec des valeurs vides : après un switch Duo, le brouillon
      // local vient d’être restauré alors que l’API « latest » ne renvoie souvent pas ces champs
      // (ou pas au même format) — sinon le bouton « Valider » reste grisé alors que l’écran semble rempli.
      const tj = financier.type_justificatif_identite;
      if (tj != null && String(tj).trim() !== '') this.type_justificatif_identite = tj;
      const ji = financier.justificatif_identite;
      if (ji != null && String(ji).trim() !== '') this.justificatif_identite = ji;
      const jr = financier.justificatif_residence;
      if (jr != null && String(jr).trim() !== '') this.justificatif_residence = jr;
      const ph = financier.photo;
      if (ph != null && String(ph).trim() !== '') this.photo = ph;
      const jrev = financier.justificatif_revenu;
      if (jrev != null && String(jrev).trim() !== '') this.justificatif_revenu = jrev;
      const rel = financier.releve;
      if (rel != null && String(rel).trim() !== '') this.releve = rel;
      this.persistDraft();
    },

    prefillCharges(charges) {
      if (!charges) return;

      this.assignPrefillNumbersFrom(charges.logement, [
        'loyer', 'telephone', 'electricite', 'gaz', 'eau', 'abonnement',
        'ordures', 'reparation', 'fournitures', 'salaire_personnel_maison',
        'popote', 'loyer_autre',
      ]);
      this.assignPrefillNumbersFrom(charges.transport, [
        'pret_voiture', 'transport_public', 'assurance_voiture', 'carburant',
        'entretien_transport', 'autres_transport',
      ]);
      const assurance = charges.assurance || {};
      this.assignPrefillNumbersFrom(assurance, ['habitat', 'sante', 'vie']);
      this.assignPrefillNumberIfPresent(
        'autresAssurance',
        { autresAssurance: assurance.autres ?? assurance.autresAssurance },
        'autresAssurance',
      );
      this.assignPrefillNumbersFrom(charges.animaux, [
        'nourriture_animaux', 'frais_medicaux', 'toilettage', 'jouets', 'autres_animaux',
      ]);
      this.assignPrefillNumbersFrom(charges.soins, [
        'medical', 'coiffeur', 'habit', 'sport_soins', 'cotisations', 'nettoyage', 'autre_soins',
      ]);
      this.assignPrefillNumbersFrom(charges.loisirs, [
        'concert', 'cinema', 'theatre', 'sport', 'restaurant', 'autres_loisirs',
      ]);
      this.assignPrefillNumbersFrom(charges.emprunts, [
        'pret_personnel', 'pret_etudiant', 'epargne',
      ]);
      this.assignPrefillNumbersFrom(charges.impots, [
        'foncier', 'locaux', 'autres_impots',
      ]);
      this.assignPrefillNumbersFrom(charges.etudes, [
        'soi_meme', 'enfant', 'autres_etudes',
      ]);
      this.assignPrefillNumbersFrom(charges.autres, [
        'autres', 'caritatif', 'dimes',
      ]);
      this.persistDraft();
    },

    prefillObjectifs(objectifs) {
      if (!objectifs) return;
      if (objectifs.objectif != null && String(objectifs.objectif).trim() !== '') {
        this.objectif = objectifs.objectif;
      }
      if (objectifs.montant != null && objectifs.montant !== '') {
        const n = Number(objectifs.montant);
        if (Number.isFinite(n) && (isFormApatFieldEmpty(this.montantObjectif) || n !== 0)) {
          this.montantObjectif = n;
        }
      }
      this.persistDraft();
    },

    async handleSubmit() {
      if (this.isReportGenerated) {
        const result = await Swal.fire({
          title: 'Analyse déjà envoyée',
          text: 'Ce formulaire ne peut plus être modifié. Pour toute correction, contactez un conseiller.',
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Contacter un conseiller',
          cancelButtonText: 'Fermer',
        });
        if (result.isConfirmed) {
          this.$router.push('/analyse-patrimoniale/contact');
        }
        return;
      }
      // Bloquer l'envoi tant que toutes les données requises ne sont pas remplies
      // (hors section Informations personnelles : pas de liste modale, guidage visuel sur le formulaire)
      try { await this.loadClientProfileForOffre(); } catch (_) { }
      if (this.validationErrorItems.length > 0) {
        const first = this.validationErrorItems[0];
        this.errorMessage = first?.message || 'Veuillez compléter les champs obligatoires avant d’envoyer.';
        this.scrollToValidationErrors();
        return;
      }
      if (this.isReportGenerated) return;

      const guarded = await formSubmitGuard.run(async () => {
        await this.executeHandleSubmit();
      });
      if (!guarded.ok) {
        if (guarded.reason === 'in_flight' || guarded.reason === 'cooldown') {
          this.errorMessage = 'Envoi en cours, veuillez patienter avant de réessayer.';
        }
      }
    },
    async executeHandleSubmit() {
      this.loading = true;
      this.errorMessage = '';
      trackMonAnalyseFormSubmit({
        offer: this.typeOffre,
        ordreDuo: this.typeOffre === 'duo' ? this.ordreDuo : undefined,
      });
      let config;

      try {
        // 1: Générer la config d'authentification
        config = { ...this.getAuthConfig(), timeout: 90000 };

        // 2. Mise à jour profil utilisateur
        await axios.put("/api/profile/", {
          tel: this.telMobile,
          ville: this.ville
        }, config);

        // 3. Mise à jour profil client (Duo : naissance uniquement pour conjoint 1)
        if (this.typeOffre !== 'duo' || this.ordreDuo === 1) {
          await axios.put("/api/all-clients/", {
            date_born: this.born,
            commune_born: this.comborn,
            departement_born: this.depborn
          }, config);
        }

        // 4. Préparer les données complètes pour l'analyse
        const payload = {

          // Situation
          situation: {
            utilisateur: Number(this.user_id),
            situation: this.situation,
            regim: this.regim,
            nb_enfants: this.nb_enfants || 0,
            nb_enfants_charges: this.nb_enfants_charges || 0,
            nbenfants_non_charges: this.nbenfants_non_charges || 0,
            possede_entreprise: this.possede_entreprise === 'Oui',
            possede_commerce: this.possede_commerce === 'Oui',
            revenus_entreprise: this.totalProfessionalRevenue || 0,
            valeur_entreprise: this.totalProfessionalValue || 0,
            activites_professionnelles: (this.activites_professionnelles || []).map((activite) => ({
              type_activite: activite.type_activite === 'commerce' ? 'commerce' : 'entreprise_formelle',
              description: activite.description || '',
              revenu_mensuel: Number(activite.revenu_mensuel) || 0,
              valorisation: Number(activite.valorisation) || 0,
            })),
            nb_annees_restantes_avant_retraite: this.nb_annees_restantes || 0,
          },

          // Patrimoine Immobilier (valeurs numériques en number, pas null)
          immobilier_passif: {
            utilisateur: Number(this.user_id),
            bien_usage: Number(this.bien_usage) || 0,
            valeurs_bien_usage: Number(this.valeurs_bien_usage) || 0,
            choix_biens_usage: this.choix_biens_usage === 'Oui',
            biens_generateurs: Number(this.biens_generateurs) || 0,
            valeurs_biens_generateurs: Number(this.valeurs_biens_generateurs) || 0,
            revenus_locatifs: Number(this.revenus_locatifs) || 0,
            terrains: this.terrains === 'Oui',
            valeurterrains: Number(this.valeurterrains) || 0,
            documents_propriete: this.documents_propriete != null ? String(this.documents_propriete) : '',
            pret_en_cours: this.pret_en_cours === 'Oui',
            montant_pret: Number(this.montant_pret) || 0,
            duree_pret: Number(this.duree_pret) || 0,
            montant_mensualite: Number(this.montant_mensualite) || 0
          },

          // Avoirs Financiers
          patrimoine_financier: {
            utilisateur: Number(this.user_id),
            has_compte_valeur: Boolean(this.has_compte_valeur),
            compte_valeur: this.has_compte_valeur ? (this.compte_valeur || 0) : 0,
            montant_compte_valeur: this.has_compte_valeur ? (this.montant_compte_valeur || 0) : 0,
            has_compte_cheque: Boolean(this.has_compte_cheque),
            compte_cheque: this.has_compte_cheque ? (this.compte_cheque || 0) : 0,
            has_compte_epargne: Boolean(this.has_compte_epargne),
            compte_epargne: this.has_compte_epargne ? (this.compte_epargne || 0) : 0,
            montant_compte_epargne: this.has_compte_epargne ? (this.montant_compte_epargne || 0) : 0,
            has_compte_dat: Boolean(this.has_compte_dat),
            compte_dat: this.has_compte_dat ? (this.compte_dat || 0) : 0,
            montant_compte_dat: this.has_compte_dat ? (this.montant_compte_dat || 0) : 0,
            has_compte_disponible: Boolean(this.has_compte_disponible),
            compte_disponible: this.has_compte_disponible ? (this.compte_disponible || 0) : 0,
            montant_compte_disponible: this.has_compte_disponible ? (this.montant_compte_disponible || 0) : 0,
            has_assurance_vie: Boolean(this.has_assurance_vie),
            assurance_vie: this.has_assurance_vie ? (this.assurance_vie || 0) : 0,
            has_assurance_produit: Boolean(this.has_assurance_produit),
            assurance_produit: this.has_assurance_produit ? (this.assurance_produit || 0) : 0,
            has_assurance_deces: Boolean(this.has_assurance_deces),
            assurance_deces: this.has_assurance_deces ? (this.assurance_deces || 0) : 0,
            detention_conjointe: this.detention_conjointe,
            placements_financiers: this.placements_financiers,
            type_justificatif_identite: this.type_justificatif_identite,
            justificatif_identite: this.justificatif_identite,
            justificatif_residence: this.justificatif_residence,
            photo: this.photo,
            justificatif_revenu: this.justificatif_revenu,
            releve: this.releve,
          },

          // Revenus
          revenus: {
            utilisateur: Number(this.user_id),
            salaire: this.salaire || 0,
            revenu_mensuel_reel: this.revenuMensuelReelCalcule || 0,
            ...(this.isProOffer ? {
              chiffres_affaires: (this.chiffres_affaires || []).map((v) => Number(v) || 0),
              resultats_nets: (this.resultats_nets || []).map((v) => Number(v) || 0),
              ebe_ebitda: Number(this.ebe_ebitda) || 0,
            } : {})
          },

          // Charges
          charges: {
            utilisateur: this.user_id,
            logement: {
              loyer: this.loyer || 0,
              telephone: this.telephone || 0,
              electricite: this.electricite || 0,
              gaz: this.gaz || 0,
              eau: this.eau || 0,
              abonnement: this.abonnement || 0,
              ordures: this.ordures || 0,
              reparation: this.reparation || 0,
              fournitures: this.fournitures || 0,
              salaire_personnel_maison: this.salaire_personnel_maison || 0,
              popote: this.popote || 0,
              loyer_autre: this.loyer_autre || 0
            },
            transport: {
              pret_voiture: this.pret_voiture || 0,
              transport_public: this.transport_public || 0,
              assurance_voiture: this.assurance_voiture || 0,
              carburant: this.carburant || 0,
              entretien_transport: this.entretien_transport || 0,
              autres_transport: this.autres_transport || 0
            },
            assurance: {
              habitat: this.habitat || 0,
              sante: this.sante || 0,
              vie: this.vie || 0,
              autres: this.autresAssurance || 0
            },
            animaux: {
              nourriture_animaux: this.nourriture_animaux || 0,
              frais_medicaux: this.frais_medicaux || 0,
              toilettage: this.toilettage || 0,
              jouets: this.jouets || 0,
              autres_animaux: this.autres_animaux || 0
            },
            soins: {
              medical: this.medical || 0,
              coiffeur: this.coiffeur || 0,
              habit: this.habit || 0,
              sport_soins: this.sport_soins || 0,
              cotisations: this.cotisations || 0,
              nettoyage: this.nettoyage || 0,
              autre_soins: this.autre_soins || 0
            },
            loisirs: {
              concert: this.concert || 0,
              cinema: this.cinema || 0,
              theatre: this.theatre || 0,
              sport: this.sport || 0,
              restaurant: this.restaurant || 0,
              autres_loisirs: this.autres_loisirs || 0
            },
            emprunts: {
              pret_personnel: this.pret_personnel || 0,
              pret_etudiant: this.pret_etudiant || 0,
              epargne: this.epargne || 0
            },
            impots: {
              foncier: this.foncier || 0,
              locaux: this.locaux || 0,
              autres_impots: this.autres_impots || 0
            },
            etudes: {
              soi_meme: this.soi_meme || 0,
              enfant: this.enfant || 0,
              autres_etudes: this.autres_etudes || 0
            },

            autres: {
              autres: this.autres || 0,
              caritatif: this.caritatif || 0,
              dimes: this.dimes || 0
            },
          },

          // Objectifs
          objectifs: {
            utilisateur: Number(this.user_id),
            objectif: this.objectif,
            montant: this.montantObjectif || 0
          }
        };

        // 5. Soumission analyse (ordre_duo pour offre Duo)
        if (this.typeOffre === 'duo') payload.ordre_duo = this.ordreDuo;
        const response = await axios.post('/api/analyse-patrimoniale/', payload, config);

        this.markAnalysisAsSubmitted(
          response.data.details?.id ?? response.data?.id ?? this.analyseId
        );
        trackMonAnalyseComplete({
          offer: this.typeOffre,
          analysisId: this.analyseId,
          ordreDuo: this.typeOffre === 'duo' ? this.ordreDuo : undefined,
        });
        this.loading = false; // Masquer l'overlay avant d'afficher la modale (évite race condition)
        const result = await Swal.fire({
          title: 'Succès',
          text: "Votre analyse a été enregistrée avec succès. Dans un maximum de 72h vous serez notifié par email et votre résultat d'analyse sera dans l'onglet Résultats d'analyse.",
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'Retour au tableau de bord'
        });
        if (result.isConfirmed) {
          this.$router.push('/analyse-patrimoniale/dashboards');
          return;
        }

        // Repeupler le formulaire avec les données renvoyées par l'API pour garantir la persistance
        const rd = this.normalizeLatestAnalysisDetails(response.data);
        if (rd) {
          this.prefillRevenus(rd.revenus || rd.Revenus);
          this.prefillSituation(rd.situation);
          this.prefillImmobilier(rd.immobilier || rd.immobilier_passif);
          this.prefillFinancier(rd.financier || rd.patrimoine_financier);
          this.prefillCharges(rd.charges);
          this.prefillObjectifs(rd.objectifs);
        }
      } catch (error) {
        console.error('Erreur détaillée:', error.response?.data);

        if (error.response?.status === 401) {
          await Swal.fire({
            title: 'Erreur!',
            text: 'Session expirée, veuillez vous reconnecter',
            icon: 'error',
            confirmButtonText: 'OK'
          });
          clearAuthenticatedSession();
          this.$router.push('/connexion');
        } else if (error.response?.status === 402) {
          this.loading = false;
          await Swal.fire({
            title: 'Règlement requis',
            text: 'Un règlement est requis avant de soumettre votre analyse patrimoniale.',
            icon: 'info',
            confirmButtonText: 'Procéder au paiement'
          });
          this.$router.push('/analyse-patrimoniale/page-de-paiement?context=submit-analysis');
          return;
        } else if (error.response?.status === 400) {
          const duplicateDetail = String(error.response?.data?.detail || '');
          if (duplicateDetail.toLowerCase().includes('existe déjà')) {
            await this.tryRecoverExistingAnalysisAfterSubmitError();
            if (!this.isReportGenerated) {
              this.markAnalysisAsSubmitted(this.analyseId);
            }
            return;
          }
          // Erreurs de validation backend : guider l'utilisateur vers les champs concernés
          const data = error.response?.data || {};
          const items = [];

          // Immobilier passif > documents_propriete
          if (data.immobilier_passif && data.immobilier_passif.documents_propriete) {
            items.push({
              message: 'Immobilier passif > Documents propriété : ' + (Array.isArray(data.immobilier_passif.documents_propriete)
                ? data.immobilier_passif.documents_propriete.join(' ')
                : String(data.immobilier_passif.documents_propriete)),
              anchorId: 'documents_propriete',
              mobileKeys: ['immobilier'],
            });
          }

          // Objectifs > objectif
          if (data.objectifs && data.objectifs.objectif) {
            items.push({
              message: 'Objectifs > Objectif : ' + (Array.isArray(data.objectifs.objectif)
                ? data.objectifs.objectif.join(' ')
                : String(data.objectifs.objectif)),
              anchorId: 'objectif',
              mobileKeys: ['objectifs'],
            });
          }

          if (items.length) {
            this.scrollToErrorItems(items);
          } else {
            // Fallback : afficher le message générique si on ne sait pas encore mapper les champs
            const err = this.getDetailedErrorMessage(error);
            await Swal.fire({
              title: err.title,
              text: err.message,
              icon: err.icon || 'info',
              confirmButtonText: err.confirmLabel || 'OK'
            });
          }
        } else {
          const recovered = await this.tryRecoverExistingAnalysisAfterSubmitError();
          if (recovered) {
            return;
          }
          const err = this.getDetailedErrorMessage(error);
          await Swal.fire({
            title: err.title,
            text: err.message,
            icon: err.icon || 'error',
            confirmButtonText: err.confirmLabel || 'OK'
          });
        }
        this.persistDraft();
      } finally {
        this.loading = false;
      }
    },

    async downloadPDF() {
      try {
        this.downloadingPDF = true;
        const response = await axios.get(
          `/api/analyse-patrimoniale/${this.analyseId}/generate_pdf/`,
          { ...this.getAuthConfig(), responseType: 'blob' }
        );

        // Créer un lien pour télécharger le PDF
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `analyse_patrimoniale_${this.analyseId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.errorMessage = this.getDetailedErrorMessage(error).message;
        console.error(error);
      } finally {
        this.downloadingPDF = false;
      }
    },
    async saveFinancialDocuments() {
      this.financialDocumentsSaving = true;
      Swal.close();
      this.errorMessage = '';
      try {
        const config = this.getAuthConfig();
        await axios.post('/api/save-personal-info/', {
          save_phase: 'financial_documents',
          ...(String(this.typeOffre || 'persona').toLowerCase() === 'duo'
            ? { ordre_duo: this.ordreDuo }
            : {}),
          justificatif_residence: this.justificatif_residence,
          justificatif_revenu: this.justificatif_revenu,
          releve: this.releve,
        }, config);
        this.financialDocumentsSaved = true;
        this.persistDraft();
        // Dossier déjà envoyé (ex. ajout d'un justificatif manquant depuis le rappel du
        // tableau de bord) : on reste sur cette page plutôt que d'avancer dans le wizard,
        // qui n'a de sens que pour une première soumission.
        if (!this.isReportGenerated) {
          const steps = this.availableWizardSteps;
          const idx = steps.findIndex((step) => step.id === this.wizardStepId);
          if (idx >= 0 && idx < steps.length - 1) {
            this.goToWizardStep(steps[idx + 1].id);
          }
        }
      } catch (error) {
        const err = this.getDetailedErrorMessage(error);
        this.errorMessage = err.message;
        await Swal.fire({
          title: err.title,
          text: err.message,
          icon: err.icon || 'error',
          confirmButtonText: err.confirmLabel || 'OK',
        });
      } finally {
        this.financialDocumentsSaving = false;
      }
    },
  }
};
</script>

<style scoped>
/* Champs de saisie plus confortables partout dans le formulaire */
.form-group input:not([type="checkbox"]):not([type="radio"]),
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1rem;
  min-height: 48px;
  box-sizing: border-box;
}

.page3-finances-step,
.page3b-finances-step,
.page3c-finances-step {
  position: relative;
  min-height: 320px;
}

.page4-documents {
  position: relative;
  min-height: 320px;
}

.page4-documents__intro {
  margin: 0 0 1rem;
  color: var(--muted);
}

.photo-preview {
  max-width: 120px;
  height: auto;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.photo-preview--large {
  max-width: 400px;
  width: 100%;
  border-radius: 8px;
  margin-bottom: 12px;
}

.photo-canvas-hidden {
  display: none;
}

.form-group--selfie {
  grid-column: 1 / -1;
}

.photo-selfie-hint {
  margin: 0 0 12px;
  font-size: 0.9rem;
  color: var(--muted);
  line-height: 1.45;
}

.photo-selfie-locked {
  padding: 12px;
  background: #e8f5e9;
  border-radius: 8px;
  color: #2e7d32;
}

.photo-selfie-loading {
  padding: 12px;
  background: #e3f2fd;
  border-radius: 8px;
  margin-bottom: 12px;
}

.photo-selfie-loading p {
  margin: 0;
  color: var(--brand-navy);
  font-size: 0.9em;
}

.photo-selfie-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.photo-selfie-options__btn {
  width: 100%;
  justify-content: center;
  text-align: center;
}

.photo-selfie-options__upload {
  cursor: pointer;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.photo-selfie-input-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.photo-selfie-accepted {
  font-size: 0.85em;
  color: #15803d;
  margin-top: 8px;
}

.photo-selfie-notice {
  margin: 0 0 12px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.5;
  border: 1px solid transparent;
}

.photo-selfie-notice--error {
  background: var(--brand-red-soft);
  border-color: rgba(152, 34, 28, 0.28);
  color: var(--brand-red);
}

.photo-selfie-notice--info {
  background: rgba(1, 38, 62, 0.05);
  border-color: rgba(1, 38, 62, 0.14);
  color: var(--brand-navy);
}

.salaire-eligibility-notice {
  margin-top: 10px;
}

.input-eligibility-error {
  border-color: var(--brand-red, #98221c) !important;
  background-color: var(--brand-red-soft, #fef2f2);
}

.webcam-stage {
  position: relative;
  max-width: 400px;
  margin: 12px auto;
}

.webcam-container {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #111;
  box-shadow: 0 8px 24px rgba(1, 38, 62, 0.18);
}

.webcam-video {
  display: block;
  width: 100%;
  max-width: 400px;
  border-radius: 12px;
}

.webcam-frame-guide {
  pointer-events: none;
  position: absolute;
  inset: 11% 19%;
  border: 2px dashed var(--apat-gold, #c9a227);
  border-radius: 50% / 42%;
  box-shadow: 0 0 0 9999px rgba(1, 38, 62, 0.45), inset 0 0 26px rgba(201, 162, 39, 0.28);
  transition: border-color 0.2s ease;
}

.webcam-frame-guide--aligned {
  border-style: solid;
  border-color: #2e7d32;
  box-shadow: 0 0 0 9999px rgba(1, 38, 62, 0.45), 0 0 0 4px rgba(46, 125, 50, 0.35), inset 0 0 26px rgba(46, 125, 50, 0.35);
  animation: webcam-frame-pulse 0.9s ease-in-out infinite;
}

@keyframes webcam-frame-pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.65;
  }
}

.webcam-frame-hint {
  margin: 10px 0 0;
  font-size: 0.85rem;
  color: var(--muted);
  text-align: center;
}

.webcam-section--controls {
  margin-top: 12px;
  border: none;
  background: transparent;
  padding: 0;
}

.webcam-controls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.webcam-error-msg {
  color: #d32f2f;
  margin-top: 8px;
}

.webcam-section--preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.webcam-section--preview .photo-preview--large {
  box-shadow: 0 8px 24px rgba(1, 38, 62, 0.18);
}

.webcam-section {
  border: 1px solid rgba(1, 38, 62, 0.12);
  border-radius: 12px;
  padding: 16px;
  margin: 12px 0;
  background-color: #fafafa;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

.btn-primary {
  background-color: var(--brand-gold);
  color: var(--brand-ink);
}

.btn-primary:hover {
  background-color: var(--brand-gold-light);
  color: var(--brand-ink);
}

.btn-success {
  background-color: #4caf50;
  color: white;
}

.btn-success:hover {
  background-color: #45a049;
}

.btn-secondary {
  background-color: #757575;
  color: white;
}

.btn-secondary:hover {
  background-color: #616161;
}

.btn-outline {
  background: #fff;
  color: var(--bank-navy, #1f3b73);
  border: 1px solid var(--bank-navy, #1f3b73);
}

.btn-outline:hover {
  background: #f1f5f9;
}

@media (max-width: 576px) {
  .photo-selfie-options {
    grid-template-columns: 1fr;
  }
}

.btn-warning {
  background-color: #ff9800;
  color: white;
}

.btn-warning:hover {
  background-color: #e68900;
}

.buttons-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  margin-top: 1.5rem;
}

.submit-btn--done {
  cursor: default;
  opacity: 0.95;
}

.submit-done-notice {
  width: 100%;
  max-width: 640px;
  flex-direction: row;
  align-items: flex-start;
}

.submit-done-notice__text {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  line-height: 1.45;
}

.submit-done-notice__text strong {
  font-weight: 600;
}

.submit-done-notice__link {
  margin-top: 0.35rem;
  color: var(--brand-navy);
  font-weight: 600;
  text-decoration: none;
  align-self: flex-start;
}

.submit-done-notice__link:hover {
  text-decoration: underline;
}

.submit-done-notice__link--btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
}

.submit-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  align-items: safe center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  pointer-events: all;
  /* Écran court (clavier encore ouvert, etc.) : sans défilement, le centrage
     vertical pousse le haut du panneau (spinner compris) hors du viewport,
     invisible et inaccessible. */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.submit-overlay__panel {
  max-width: 420px;
  width: 100%;
  padding: 1.75rem 1.5rem;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  text-align: center;
}

.submit-overlay__spinner {
  width: 2.5rem;
  height: 2.5rem;
  margin-bottom: 1rem;
}

.submit-overlay__title {
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
}

.submit-overlay__hint {
  margin: 0;
  font-size: 0.95rem;
  color: var(--muted);
  line-height: 1.5;
}

.notification {
  padding: 15px;
  margin: 10px 0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
}

.notification svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.notification.success {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.notification.error {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
}

.notification.info {
  background-color: rgba(201, 162, 39, 0.1);
  color: var(--brand-navy, #01263e);
  border: 1px solid rgba(201, 162, 39, 0.3);
  justify-content: space-between;
  flex-wrap: wrap;
}

.notification__action {
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
}

.notification__action:hover {
  opacity: 0.8;
}

.notification svg path {
  fill: currentColor;
}

.submit-done-notice {
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
}

.accounts-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.account-item-horizontal {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 16px;
  background: var(--surface);
  border-radius: 8px;
  border: 1px solid var(--line);
}

.account-item-horizontal .checkbox-wrapper {
  width: auto;
  flex: 0 0 auto;
}

.account-item-header {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.account-item-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 420px;
  margin-left: 0;
  padding-left: 0;
}

.account-item-fields .field-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.account-item-fields .field-row label {
  font-size: 0.9rem;
  color: var(--muted);
  font-weight: 500;
}

.account-item-fields .field-row input {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  font-size: 1rem;
}

.account-item-fields .field-row input.error {
  border-color: #ef4444;
  background-color: #fef2f2;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  /* .checkbox-wrapper est lui-même l'enfant flex de .account-item-header : sans
     min-width:0 ICI (en plus de celui sur le <label> ci-dessous), sa taille minimale
     automatique reste calculée sur le libellé non replié, et "width:100%" ne suffit
     pas à le faire rétrécir — le texte déborde alors de la carte sur mobile. */
  min-width: 0;
}

.checkbox-wrapper input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-wrapper label {
  font-weight: 500;
  cursor: pointer;
  /* Un enfant flex a "min-width: auto" par défaut : sans ça, il refuse de
     passer à la ligne et déborde du conteneur pour les libellés longs
     ("Compte valeur mobilière (compte titre)", etc.). */
  min-width: 0;
  flex: 1;
  overflow-wrap: break-word;
}

.input[type="number"] {
  flex: 1;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 220px;
  max-width: 380px;
  font-size: 16px;
}

.pay-btn {
  background-color: #24c672;
  /* Bleu distinct */
  color: white;
  border: 1px solid #24c672;
}

.pay-btn:hover {
  background-color: #67ffdcea;
  border-color: #67ffdcea;
}

/* Guidage : surbrillance temporaire après validation échouée */
.field-error-highlight {
  outline: 3px solid #e11d48 !important;
  box-shadow: 0 0 0 4px rgba(225, 29, 72, 0.25) !important;
  border-radius: 8px;
  transition: outline 0.2s ease, box-shadow 0.2s ease;
}

.missing-residence-banner {
  max-width: 920px;
  margin: 0 auto 1rem;
  padding: 1rem 1.25rem;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 12px;
  color: #9a3412;
}

.missing-residence-banner--in-section {
  max-width: none;
  margin: 1rem 0 0;
}

.missing-residence-banner__title {
  margin: 0 0 0.75rem 0;
  font-size: 0.95rem;
  line-height: 1.45;
}

.missing-residence-banner .form-group label {
  font-weight: 600;
  color: #7c2d12;
}

.missing-residence-banner .form-group input[type="file"],
.form-section .form-group input[type="file"] {
  display: block;
  width: 100%;
}

/* Styles pour les erreurs de validation */
.error {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.form-group .error {
  border: 2px solid #dc3545;
  border-radius: 4px;
}

/* Amélioration de l'UX pour les champs conditionnels */
.input[type="number"]:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.mtn-sai {
  color: #ac302a;
  ;
}

.titre-marrow {
  color: rgb(1, 38, 62);
}

/* Intro & progression diagnostic */
.form-progress {
  position: sticky;
  top: 0;
  z-index: 20;
  margin-bottom: 1.25rem;
  padding: 1rem 1.15rem 1.1rem;
  border-radius: 16px;
  background: var(--apat-paper, rgba(255, 255, 255, 0.96));
  border: 1px solid var(--apat-line, rgba(1, 38, 62, 0.12));
  box-shadow: 0 12px 28px rgba(1, 38, 62, 0.08);
  backdrop-filter: blur(10px);
}

.form-progress__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.55rem;
}

.form-progress__phase {
  font-size: 0.92rem;
  font-weight: 650;
  color: var(--apat-navy, rgb(1, 38, 62));
}

.form-progress__pct {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--brand-gold);
}

.form-progress__track {
  height: 9px;
  border-radius: 999px;
  background: rgba(1, 38, 62, 0.08);
  overflow: hidden;
}

.form-progress__bar {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--brand-navy) 0%, #1a2d44 45%, var(--brand-gold) 100%);
  transition: width 0.35s ease;
}

.form-apat--readonly .patrimonial-form :is(input, select, textarea) {
  pointer-events: none;
  background-color: rgba(1, 38, 62, 0.04);
  color: var(--client-text-muted, #64748b);
  cursor: default;
}

.form-apat--readonly .patrimonial-form button:not(.ux-toggle):not(.submit-done-notice__link--btn) {
  pointer-events: none;
  opacity: 0.65;
}

.form-progress__steps {
  display: flex;
  gap: 0.5rem;
  list-style: none;
  margin: 0.65rem 0 0;
  padding: 0;
}

.form-progress__steps li {
  flex: 1;
  text-align: center;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(1, 38, 62, 0.45);
  padding: 0.4rem 0.3rem;
  border-radius: 999px;
  background: rgba(1, 38, 62, 0.04);
  border: 1px solid transparent;
}

.form-progress__steps li.is-active {
  color: var(--brand-navy);
  background: rgba(201, 162, 39, 0.18);
  border-color: rgba(201, 162, 39, 0.35);
}

.form-progress__steps li.is-done {
  color: #0f766e;
  background: rgba(15, 118, 110, 0.1);
}

.form-intro {
  margin-bottom: 1.25rem;
  padding: 1.15rem 1.2rem;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(1, 38, 62, 0.04), rgba(212, 175, 55, 0.08));
  border: 1px solid rgba(1, 38, 62, 0.1);
}

.form-intro__title {
  margin: 0 0 0.85rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: rgb(1, 38, 62);
}

.form-intro__steps {
  margin: 0 0 0.85rem;
  padding-left: 1.2rem;
  color: rgba(1, 38, 62, 0.88);
}

.form-intro__steps li {
  margin-bottom: 0.45rem;
  line-height: 1.45;
}

.form-intro__hint {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.86rem;
  color: rgba(1, 38, 62, 0.62);
  font-weight: 400;
}

.form-intro__trust {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.75rem;
  font-size: 0.88rem;
  color: rgba(1, 38, 62, 0.78);
}

.form-intro__benefits summary {
  cursor: pointer;
  font-weight: 600;
  color: rgb(1, 38, 62);
  margin-bottom: 0.35rem;
}

.form-intro__benefits ul {
  margin: 0.35rem 0 0;
  padding-left: 1.2rem;
  color: rgba(1, 38, 62, 0.82);
  font-size: 0.9rem;
}

.form-intro__benefits li {
  margin-bottom: 0.25rem;
}

.form-header--hint,
.form-header--welcome {
  font-size: 0.94rem;
  color: rgba(1, 38, 62, 0.82);
  line-height: 1.5;
  padding: 0.9rem 1.1rem;
  border-radius: 12px;
  background: rgba(201, 162, 39, 0.08);
  border: 1px solid rgba(201, 162, 39, 0.22);
  margin-bottom: 1rem;
}

.wizard-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin: 1.25rem 0 0.5rem;
  padding: 1rem 1.15rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(1, 38, 62, 0.12);
  box-shadow: 0 12px 28px rgba(1, 38, 62, 0.07);
  position: sticky;
  bottom: 0.5rem;
  z-index: 15;
}

.wizard-nav__status {
  flex: 1;
  text-align: center;
  font-size: 0.88rem;
  font-weight: 600;
  color: rgba(1, 38, 62, 0.72);
  min-width: 120px;
}

.wizard-nav__btn {
  appearance: none;
  border-radius: 12px;
  padding: 0.72rem 1.2rem;
  font-weight: 650;
  font-size: 0.92rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, background 0.15s ease;
}

.wizard-nav__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.wizard-nav__btn--ghost {
  color: var(--brand-navy);
  background: rgba(1, 38, 62, 0.04);
  border-color: rgba(1, 38, 62, 0.14);
}

.wizard-nav__btn--ghost:hover:not(:disabled) {
  background: rgba(1, 38, 62, 0.08);
}

.wizard-nav__btn--primary {
  color: #fff;
  background: linear-gradient(135deg, var(--brand-navy) 0%, #1a2d44 70%, #243b55 100%);
  border-color: rgba(201, 162, 39, 0.45);
  box-shadow: 0 12px 24px rgba(1, 38, 62, 0.2);
}

.wizard-nav__btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(1, 38, 62, 0.26);
}

.wizard-submit-intro {
  margin: 0;
  padding: 0.75rem 0;
  color: rgba(1, 38, 62, 0.82);
  line-height: 1.55;
}

.form-progress__steps--mini {
  flex-wrap: wrap;
  gap: 0.35rem;
}

.form-progress__steps--mini li {
  flex: 1 1 auto;
  min-width: 72px;
  font-size: 0.62rem;
  padding: 0.28rem 0.35rem;
  line-height: 1.2;
}

@media (max-width: 576px) {
  .wizard-nav {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "status status" "prev next";
    gap: 0.6rem;
  }

  .wizard-nav__status {
    grid-area: status;
    text-align: center;
  }

  .wizard-nav__btn--ghost {
    grid-area: prev;
  }

  .wizard-nav__btn--primary {
    grid-area: next;
  }

  .form-progress__steps--mini li {
    min-width: 58px;
    font-size: 0.58rem;
  }
}

/* Animation pour les champs qui apparaissent */
.input[type="number"] {
  transition: all 0.3s ease;
}

/* Indicateur visuel pour les champs requis */
/* .checkbox-wrapper label::after {
  content: " *";
  color: #dc3545;
  font-weight: bold;
} */

.p {
  color: #dc3545;
  font-weight: bold;
}

/* Section verrouillée : lecture seule assumée, pas de grisé qui casse la lisibilité */
.section-locked input:disabled,
.section-locked input[readonly],
.section-locked select:disabled,
.section-locked textarea:disabled {
  background-color: rgba(1, 38, 62, 0.04);
  color: var(--client-text-muted, #64748b);
  cursor: default;
  border-color: rgba(1, 38, 62, 0.12);
}

.form-section.section-locked {
  background-color: #fff;
  border-radius: 16px;
  padding: 1.15rem 1.25rem;
  border: 1px solid rgba(1, 38, 62, 0.1);
}

#section-personal.form-section {
  background-color: #fff;
}

/* Badge de verrouillage */
.lock-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background-color: rgba(21, 128, 61, 0.12);
  color: #15803d;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 10px;
}

.lock-badge--inline {
  margin-left: 8px;
  vertical-align: middle;
}

/* Rappel de l'offre une fois le dossier engagé */
.offre-locked__value {
  margin: 0 0 0.35rem;
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--brand-navy);
}

.offre-locked__hint {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--client-text-muted, #6b6155);
}

/* Offre Duo : onglets Conjoint 1 / Conjoint 2 */
.duo-selector {
  margin-bottom: 0;
}

.duo-selector--personal {
  margin-bottom: 1rem;
  padding: 0 2px;
}

.duo-tabs {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.duo-tab {
  padding: 10px 22px;
  border: 1.5px solid rgba(1, 38, 62, 0.14);
  border-radius: 999px;
  background: #fff;
  font-weight: 600;
  color: var(--brand-navy);
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s, box-shadow 0.2s;
}

.duo-tab:hover {
  border-color: rgba(201, 162, 39, 0.55);
  color: var(--brand-navy);
}

.duo-tab.active {
  background: linear-gradient(135deg, var(--brand-navy), #1a2d44);
  border-color: rgba(201, 162, 39, 0.5);
  color: #fff;
  box-shadow: 0 8px 18px rgba(1, 38, 62, 0.16);
}

.duo-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.35rem;
  font-size: 0.75rem;
  color: #0f766e;
}

.duo-tab.active .duo-tab-badge {
  color: var(--brand-gold);
}

.duo-hint {
  font-size: 0.9rem;
  margin: 0;
}

/* Champ Login */
.login-field {
  background-color: #e8f5e9 !important;
  font-weight: 600;
  color: #2e7d32 !important;
  border-color: #c8e6c9 !important;
}

.login-hint {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #6c757d;
  font-style: italic;
}

/* Action de section */
.section-action {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px dashed #ddd;
  display: flex;
  justify-content: flex-end;
}

/* Bouton de validation */
.validate-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.validate-btn:hover:not(:disabled) {
  background-color: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.validate-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.validate-btn svg {
  flex-shrink: 0;
}

.professional-activities {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.professional-activities__header,
.professional-activities__summary,
.professional-activities__empty {
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--surface);
  border: 1px solid var(--line);
}

.professional-activities__title {
  margin: 0 0 4px 0;
  font-weight: 700;
  color: #1e293b;
}

.professional-activities__hint {
  margin: 0;
  color: var(--muted);
  font-size: 0.92rem;
}

.professional-activity-card {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

.professional-activity-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.professional-activity-card__remove {
  border: 1px solid #fecaca;
  background: #fff5f5;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.professional-activities__actions {
  display: flex;
  justify-content: flex-start;
}

/* Conteneur formulaire : largeur maîtrisée et centrée sur tous les écrans */
.patrimonial-form-container.form-apat-shell {
  --apat-navy: var(--brand-navy);
  --apat-navy-deep: var(--brand-navy-deep);
  --apat-gold: var(--brand-gold);
  --apat-gold-soft: var(--brand-gold-soft);
  --apat-ink: var(--brand-ink);
  --apat-muted: var(--muted);
  --apat-line: rgba(1, 38, 62, 0.12);
  --apat-paper: rgba(255, 255, 255, 0.94);
  position: relative;
  width: 100%;
  max-width: 1120px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
  /* Bas généreux : la barre de navigation est collante et recouvrirait sinon
     les derniers champs de chaque étape. */
  padding: 1.25rem 1rem 6rem;
  isolation: isolate;
}

.form-apat-hero {
  margin: 0 0 var(--space-6);
}

.form-apat-hero__title {
  margin: 0 0 var(--space-3);
  font-family: var(--font-display);
  font-size: clamp(1.5rem, 3.2vw, 2rem);
  font-weight: 600;
  line-height: 1.25;
  color: var(--brand-navy);
}

.form-apat-hero__lead {
  margin: 0;
  max-width: 60ch;
  font-family: var(--font-body);
  font-size: 1rem;
  line-height: 1.6;
  color: var(--client-text-muted, #6b6155);
}

.patrimonial-form {
  width: 100%;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.form-grid--2cols {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

/* Paire de champs côte à côte à l’intérieur d’une grille déjà mono-colonne. */
.form-grid--inline-pair {
  grid-column: 1 / -1;
  gap: 1rem;
}

.charges-fields h6 {
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
}

.charges-fields h6:first-of-type {
  margin-top: 0.5rem;
}

/* En-tête statique d'une catégorie de charges (plus d'accordéon : tout est visible,
   on navigue entre les 4 pages avec la barre Précédent/Suivant du wizard). */
.charge-category-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 650;
  color: #01263e;
}

/* Badge de progression sur les en-têtes de sous-section */
.section-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 650;
  font-family: var(--font-body);
  letter-spacing: 0;
}

.section-status-badge--done {
  background-color: rgba(21, 128, 61, 0.12);
  color: #15803d;
}

.section-status-badge--pending {
  background-color: rgba(201, 162, 39, 0.16);
  color: #8a6d1a;
}

/* Responsive — par type d'appareil (tablette 768, puis chaque largeur mobile) */
@media (max-width: 768px) {

  /* Spécificité alignée sur la règle de base .patrimonial-form-container.form-apat-shell
     (ligne ~5687, hors media query) : sinon, à spécificité égale, une règle non
     conditionnelle avec deux classes gagne toujours sur une règle à une seule
     classe même dans un @media qui matche — le padding/marge ci-dessous était
     donc silencieusement ignoré avant ce correctif. */
  .patrimonial-form-container.form-apat-shell {
    padding-left: max(10px, env(safe-area-inset-left));
    padding-right: max(10px, env(safe-area-inset-right));
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .patrimonial-form-container>* {
    width: 100%;
    max-width: min(520px, 100%);
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-grid--2cols {
    grid-template-columns: 1fr;
  }

  .form-grid .form-group,
  .form-group input:not([type="checkbox"]):not([type="radio"]),
  .form-group select,
  .form-group textarea {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .duo-tabs {
    flex-wrap: wrap;
  }

  .duo-tab {
    flex: 1;
    min-width: 120px;
  }

  .form-section {
    padding: 12px;
    width: 100%;
    box-sizing: border-box;
  }

  .btn,
  .validate-btn,
  input,
  select,
  textarea {
    min-height: 44px;
  }
}

@media (max-width: 576px) {

  /* Spécificité alignée sur .patrimonial-form-container.form-apat-shell (voir
     commentaire identique dans le bloc @media 768px ci-dessus) : sans les deux
     classes ici, ce bloc perd contre la règle de base et n'a aucun effet. */
  .patrimonial-form-container.form-apat-shell {
    /* Le layout parent (.dashboard-content) ajoute déjà ~12px de padding de
       chaque côté ; on "déborde" par-dessus pour occuper toute la largeur de
       l'écran au lieu de cumuler les marges du parent et du formulaire.
       width doit repasser à auto : sinon width:100% (hérité) + ces deux
       marges négatives sur-contraint la boîte, et le navigateur ignore
       silencieusement margin-right (spec CSS, direction LTR) pour rééquilibrer
       l'équation — résultat : ~24px d'écart à droite au lieu d'un bord à bord
       symétrique. */
    width: auto;
    margin-left: -12px;
    margin-right: -12px;
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
    /* La barre Précédent/Suivant est en flux normal (cf. règle .wizard-nav
       ci-dessous) : pas besoin de dégagement artificiel, juste un peu de
       respiration en bas de page. */
    padding-bottom: calc(2rem + env(safe-area-inset-bottom));
  }

  /* Cartes bord à bord : plus de marge latérale, juste un léger padding
     interne pour que le texte ne colle pas aux bords de l'écran. */
  .patrimonial-form-container>* {
    max-width: 100%;
    border-radius: 0;
  }

  /* .form-progress est en position: sticky et hérite d'un "width: 100%; max-width:
     min(520px, 100%)" fixé par le bloc @media 768px ci-dessus : sur les écrans
     entre 520 et 576px, ce plafond de 520px le laissait plus étroit que les cartes
     (edge-to-edge) juste en dessous, d'où le décalage. On force explicitement. */
  .form-progress {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
    border-radius: 0;
    box-sizing: border-box;
  }

  /* La carte de succès (icône + texte en ligne) occupe toute la largeur sur
     mobile : un layout en ligne calé à gauche paraît décentré. On empile
     icône/texte et on centre, comme un écran de confirmation classique. */
  .submit-done-notice {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .submit-done-notice__text {
    align-items: center;
  }

  .submit-done-notice__link {
    align-self: center;
  }

  /* .form-section a son propre padding interne (12px) qui donne son inset visuel
     aux cartes ; ces éléments "nus" (pas de carte autour) n'en ont pas et se
     retrouvaient collés au bord de l'écran une fois le conteneur passé à 0 —
     décalés par rapport au texte des cartes juste en dessous. On aligne. */
  .form-apat-hero,
  .duo-selector--personal {
    padding-left: 12px;
    padding-right: 12px;
  }

  .form-header,
  .titre-marrow {
    font-size: 0.95rem;
  }

  .section-title {
    font-size: 1.08rem;
    color: rgb(0, 31, 67);
  }

  .form-section {
    padding: 12px;
    border-left: none;
    border-right: none;
    border-radius: 0;
  }

  .form-section.section-locked,
  #section-personal.form-section {
    border-radius: 0;
  }

  /* Barre Précédent/Suivant en flux normal (comme .diagnostic-actions dans
     public/Diagnostic.vue) : plus de position sticky/fixed, elle s'affiche
     simplement en bas du contenu de l'étape, une fois qu'on y arrive en
     scrollant — ne peut donc jamais recouvrir un champ. */
  .wizard-nav {
    position: static;
    /* Largeur forcée explicitement (comme .form-progress plus haut) : la règle
       générique ".patrimonial-form-container > *" ne suffit pas toujours à
       elle seule, ce qui laissait la barre plus large que le reste du formulaire. */
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    margin: 1.5rem 0 0;
    /* Padding horizontal aligné sur .form-section (12px) : sinon les boutons
       Précédent/Suivant se retrouvent collés au bord, décalés par rapport au
       contenu des cartes juste au-dessus. */
    padding: 1rem 12px 0;
    border: none;
    border-top: 1px dashed rgba(1, 38, 62, 0.14);
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  /* Carte plus soignée sur mobile : accent doré en tête, champs moins "plats" */
  .form-section {
    border-top: 3px solid var(--brand-gold, #c9a227);
    box-shadow: 0 8px 20px rgba(1, 38, 62, 0.08);
  }

  .form-group input:not([type="checkbox"]):not([type="radio"]),
  .form-group select,
  .form-group textarea {
    background-color: #f9fafb;
    border-radius: 10px;
  }

  .form-group input:not([type="checkbox"]):not([type="radio"]):focus,
  .form-group select:focus,
  .form-group textarea:focus {
    background-color: #fff;
    border-color: var(--brand-navy, #01263e);
    box-shadow: 0 0 0 3px rgba(1, 38, 62, 0.12);
  }
}

/* 430px — iPhone 15 Plus, Pixel 9 */
@media (max-width: 430px) {
  .account-item-fields {
    max-width: 100%;
  }
}

/* 414px / 390px — iPhone Plus, iPhone 14/15/16 */
@media (max-width: 414px) {
  .account-item-fields {
    max-width: 100%;
  }
}

/* 375px — iPhone 6/7/8, X/11/12/13 */
@media (max-width: 375px) {
  .form-section {
    padding: 10px;
  }

  .validate-btn {
    width: 100%;
    justify-content: center;
    min-height: 44px;
  }

  .section-action {
    flex-direction: column;
  }
}

/* 360px — Samsung Galaxy, Tecno, Android répandu */
@media (max-width: 360px) {
  .form-section {
    padding: 8px;
  }

  .validate-btn {
    font-size: 0.9rem;
  }
}

/* 320px — Très petits écrans */
@media (max-width: 320px) {
  .form-header .titre-marrow {
    font-size: 0.85rem;
  }
}

/* UX mobile : sections repliables */
.mobile-only {
  display: none;
}

@media (max-width: 576px) {
  .mobile-only {
    display: inline-flex;
  }
}

.ux-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 10px 0 12px;
  width: 100%;
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  font-weight: 700;
  cursor: pointer;
}
</style>
